import {
  type ApprovalRequestId,
  DEFAULT_MODEL_BY_PROVIDER,
  type ManagedAgent,
  type EnvironmentId,
  type ManagedAgentId,
  type MessageId,
  type AssistantDeliveryMode,
  type ComposerQueuedMessage,
  type ModelSelection,
  type ProjectScript,
  type ModelSlug,
  type ProviderKind,
  type ProjectEntry,
  type ProjectId,
  type ProviderApprovalDecision,
  type ProviderMentionReference,
  type ProviderNativeCommandDescriptor,
  type ProviderSkillDescriptor,
  type ProviderSkillReference,
  PROVIDER_DISPLAY_NAMES,
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS,
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS_TOTAL_BYTES,
  type ResolvedKeybindingsConfig,
  type ServerProviderStatus,
  ThreadId,
  type TurnId,
  type EditorId,
  type KeybindingCommand,
  OrchestrationThreadActivity,
  ProviderInteractionMode,
  RuntimeMode,
} from "@taskin-code/contracts";
import {
  getModelCapabilities,
  modelSelectionDiffers,
  normalizeModelSlug,
} from "@taskin-code/shared/model";
import { buildTemporaryWorktreeBranchName } from "@taskin-code/shared/git";
import { buildAgentModelSelection } from "@taskin-code/shared/managedAgentModelOptions";
import {
  attachmentMaxBytes,
  formatAttachmentSize,
  resolveAttachmentType,
} from "@taskin-code/shared/attachmentKinds";
import {
  buildPromptThreadTitleFallback,
  GENERIC_CHAT_THREAD_TITLE,
} from "@taskin-code/shared/chatThreads";
import {
  resolveThreadWorkspaceState,
  resolveThreadBranchSourceCwd,
  resolveThreadWorkspaceCwd as resolveSharedThreadWorkspaceCwd,
} from "@taskin-code/shared/threadEnvironment";
import { deriveTerminalCommandIdentity } from "@taskin-code/shared/terminalThreads";
import { deriveAssociatedWorktreeMetadata } from "@taskin-code/shared/threadWorkspace";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { GoTasklist } from "react-icons/go";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { gitCreateWorktreeMutationOptions, gitBranchesQueryOptions } from "~/lib/gitReactQuery";
import { resolveProviderDiscoveryCwd } from "~/lib/providerDiscovery";
import {
  providerAgentsQueryOptions,
  providerComposerCapabilitiesQueryOptions,
  providerCommandsQueryOptions,
  providerModelsQueryOptions,
  providerPluginsQueryOptions,
  providerSkillsQueryOptions,
  supportsNativeSlashCommandDiscovery,
  supportsPluginDiscovery,
  supportsSkillDiscovery,
} from "~/lib/providerDiscoveryReactQuery";
import { projectSearchEntriesQueryOptions } from "~/lib/projectReactQuery";
import { serverConfigQueryOptions, serverQueryKeys } from "~/lib/serverReactQuery";
import { isElectron } from "../env";
import { describeWorktreeBaseFallback } from "../worktreeBase";
import { parseDiffRouteSearch, stripDiffSearchParams } from "../diffRouteSearch";
import { resolveSubagentPresentationForThread } from "../lib/subagentPresentation";
import {
  buildThreadBreadcrumbs,
  deriveThreadPlaneIssue,
  enrichSubagentWorkEntries,
} from "./ChatView.logic";
import {
  createRelevantWorkLogThreadsSelector,
  createThreadLineageSelector,
} from "./ChatView.selectors";
import {
  clampCollapsedComposerCursor,
  type ComposerTrigger,
  collapseExpandedComposerCursor,
  detectComposerTrigger,
  expandCollapsedComposerCursor,
  replaceTextRange,
  stripComposerTriggerText,
} from "../composer-logic";
import { useAuthStore, useHasGlobalPermission } from "../auth/authStore";
import {
  createDelegatedSubagentTasksSelector,
  createDelegationsAwaitingParentSelector,
  createProjectSelector,
  createThreadDetailHydratedSelector,
  createThreadSelector,
} from "../storeSelectors";
import {
  canOfferForkSlashCommand,
  canOfferReviewSlashCommand,
  hasProviderNativeSlashCommand,
  resolveComposerSlashRootBranch,
} from "../composerSlashCommands";
import {
  derivePendingApprovals,
  derivePendingUserInputs,
  derivePhase,
  deriveTimelineEntries,
  deriveActiveWorkStartedAt,
  deriveActivePlanState,
  deriveActiveBackgroundTasksState,
  deriveActiveSubagentTasksState,
  combineActiveSubagentTasks,
  findSidebarProposedPlan,
  findLatestProposedPlan,
  deriveWorkLogEntries,
  hasActionableProposedPlan,
  hasLiveTurnTailWork,
  isLatestTurnSettled,
} from "../session-logic";
import {
  buildPendingUserInputAnswers,
  derivePendingUserInputProgress,
  setPendingUserInputCustomAnswer,
  togglePendingUserInputOptionSelection,
  type PendingUserInputDraftAnswer,
} from "../pendingUserInput";
import { useStore } from "../store";
import { getThreadFromState } from "../threadDerivation";
import {
  buildPlanImplementationThreadTitle,
  buildPlanImplementationPrompt,
  proposedPlanTitle,
  resolvePlanFollowUpSubmission,
} from "../proposedPlan";
import { truncateTitle } from "../truncateTitle";
import {
  DEFAULT_INTERACTION_MODE,
  DEFAULT_RUNTIME_MODE,
  DEFAULT_THREAD_TERMINAL_ID,
  MAX_TERMINALS_PER_GROUP,
  type ChatMessage,
  type TurnDiffSummary,
} from "../types";
import { useTheme } from "../hooks/useTheme";
import { useThreadWorkspaceHandoff } from "../hooks/useThreadWorkspaceHandoff";
import { useComposerCommandMenuItems } from "../hooks/useComposerCommandMenuItems";
import { useThreadHandoff } from "../hooks/useThreadHandoff";
import { useTurnDiffSummaries } from "../hooks/useTurnDiffSummaries";
import { useProviderModelCatalog } from "../hooks/useProviderModelCatalog";
import { useReconcileComposerModelSelection } from "../hooks/useReconcileComposerModelSelection";
import { isSelectionAllowedByPolicy } from "../allowedModelSelection";
import BranchToolbar from "./BranchToolbar";
import { resolveBranchToolbarVisibility } from "./BranchToolbar.logic";
import { ThreadWorktreeHandoffDialog } from "./ThreadWorktreeHandoffDialog";
import {
  formatShortcutLabel,
  resolveShortcutCommand,
  shortcutLabelForCommand,
} from "../keybindings";
import PlanSidebar from "./PlanSidebar";
import TerminalWorkspaceTabs from "./TerminalWorkspaceTabs";
import { ThreadTerminalDrawerLazy } from "./ThreadTerminalDrawerLazy";
import {
  AlarmClockIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  XIcon,
} from "~/lib/icons";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "./ui/menu";
import { cn, isMacPlatform, randomUUID } from "~/lib/utils";
import { toastManager } from "./ui/toast";
import { decodeProjectScriptKeybindingRule } from "~/lib/projectScriptKeybindings";
import { type NewProjectScriptInput } from "./ProjectScriptsControl";
import {
  commandForProjectScript,
  nextProjectScriptId,
  projectScriptRuntimeEnv,
  projectScriptIdFromCommand,
} from "~/projectScripts";
import { setupProjectScript } from "@taskin-code/shared/projectScripts";
import { usePendingSetupScriptStore } from "~/pendingSetupScriptStore";
import { promoteThreadFromTemporary } from "~/temporaryThreadStore";
import { SidebarHeaderTrigger } from "./ui/sidebar";
import { newCommandId, newMessageId, newThreadId } from "~/lib/utils";
import { readNativeApi } from "~/nativeApi";
import { recordThreadVisit } from "../lib/threadInboxActions";
import {
  formatSnoozeReturn,
  resolveTurnVisitAcknowledgement,
  threadReturnedFromSnooze,
} from "../lib/threadInboxState";
import { useSideChatAnchorStore } from "~/sideChatAnchorStore";
import { requestClearConversation } from "~/clearConversation";
import {
  confirmTerminalTabClose,
  resolveTerminalCloseTitle,
} from "~/lib/terminalCloseConfirmation";
import {
  getCustomModelOptionsByProvider,
  getCustomModelsByProvider,
  getProviderStartOptions,
  resolveAppModelSelection,
  useAppSettings,
} from "../appSettings";
import { resolveTerminalNewAction } from "../lib/terminalNewAction";
import { resolveAttachmentPreviewUrl } from "../lib/attachmentUrls";
import { selectComposerQueueEntry, useComposerQueueStore } from "../composerQueueStore";
import { QueuedComposerTurnsControl } from "./chat/QueuedComposerTurnsControl";
import { isTerminalFocused } from "../lib/terminalFocus";
import {
  type ComposerAttachment,
  type DraftThreadEnvMode,
  type PersistedComposerAttachment,
  useComposerDraftStore,
  useComposerThreadDraft,
  useEffectiveComposerModelState,
} from "../composerDraftStore";
import { attachmentOnlyBootstrapPrompt } from "../lib/displayedUserMessage";
import {
  appendQuotedContextsToPrompt,
  formatQuotedContextLabel,
  insertInlineQuotedContextPlaceholder,
  removeInlineQuotedContextPlaceholder,
  type QuotedContextDraft,
  type QuotedContextSelection,
} from "../lib/quotedContext";
import {
  deriveLatestContextWindowSnapshot,
  deriveCumulativeCostUsd,
  formatContextWindowTokens,
} from "../lib/contextWindow";
import {
  CLAUDE_RESUME_COMPACTION_MINUTES,
  DismissedResumeCompactionThreads,
  hasDismissedResumeCompaction,
  isResumeCompactionPreviewEnabled,
  NO_DISMISSED_RESUME_COMPACTION_THREADS,
  previewResumeCompactionWindow,
  rememberDismissedResumeCompactionThread,
  RESUME_COMPACTION_DISMISSED_STORAGE_KEY,
  shouldOfferResumeCompaction,
} from "../lib/resumeCompaction";
import { formatVoiceRecordingDuration, useVoiceRecorder } from "../lib/voiceRecorder";
import { shouldUseCompactComposerFooter } from "./composerFooterLayout";
import { selectThreadTerminalState, useTerminalStateStore } from "../terminalStateStore";
import { collectTerminalIdsFromLayout } from "../terminalPaneLayout";
import {
  resolveSplitViewFocusedThreadId,
  selectSplitView,
  type SplitViewPanePanelState,
  useSplitViewStore,
} from "../splitViewStore";
import { ComposerPromptEditor, type ComposerPromptEditorHandle } from "./ComposerPromptEditor";
import { ChatHeader } from "./chat/ChatHeader";
import { ChatTranscriptPane } from "./chat/ChatTranscriptPane";
import { useOlderThreadHistory } from "./chat/useOlderThreadHistory";
import { MessageSelectionToolbar } from "./chat/MessageSelectionToolbar";
import { resolveSideChatAgents } from "./chat/SideChatPanel.logic";
import { useTranscriptSelection } from "./chat/useTranscriptSelection";
import { ComposerSlashStatusDialog } from "./chat/ComposerSlashStatusDialog";
import { ExpandedImagePreview } from "./chat/ExpandedImagePreview";
import { AVAILABLE_PROVIDER_OPTIONS, ProviderModelPicker } from "./chat/ProviderModelPicker";
import { ComposerCommandItem, ComposerCommandMenu } from "./chat/ComposerCommandMenu";
import { ComposerPendingApprovalActions } from "./chat/ComposerPendingApprovalActions";
import { ComposerExtrasMenu } from "./chat/ComposerExtrasMenu";
import { ComposerPendingApprovalPanel } from "./chat/ComposerPendingApprovalPanel";
import { ComposerPendingUserInputPanel } from "./chat/ComposerPendingUserInputPanel";
import { ComposerPlanFollowUpBanner } from "./chat/ComposerPlanFollowUpBanner";
import { ComposerResumeCompactionBanner } from "./chat/ComposerResumeCompactionBanner";
import { COMPOSER_TOP_PANEL_CLASSNAME } from "./chat/composerTopPanel";
import { ComposerVoiceButton } from "./chat/ComposerVoiceButton";
import { ComposerVoiceRecorderBar } from "./chat/ComposerVoiceRecorderBar";
import { ComposerAttachmentChip } from "./chat/ComposerAttachmentChip";
import { ActivePlanCard } from "./chat/ActivePlanCard";
import { ActiveSubagentsIndicator } from "./chat/ActiveSubagentsIndicator";
import { useChatAutoScrollController } from "./chat/useChatAutoScrollController";
import {
  getComposerProviderState,
  renderProviderTraitsPicker,
} from "./chat/composerProviderRegistry";
import { getComposerTraitSelection } from "./chat/composerTraits";
import { ProviderHealthBanner } from "./chat/ProviderHealthBanner";
import { ThreadErrorBanner } from "./chat/ThreadErrorBanner";
import { RateLimitBanner, deriveLatestRateLimitStatus } from "./chat/RateLimitBanner";
import {
  ACTIVE_TURN_LAYOUT_SETTLE_DELAY_MS,
  appendVoiceTranscriptToPrompt,
  describeVoiceRecordingStartError,
  isVoiceAuthExpiredMessage,
  sanitizeVoiceErrorMessage,
  shouldStartActiveTurnLayoutGrace,
  shouldAutoDeleteTerminalThreadOnLastClose,
  buildExpiredTerminalContextToastCopy,
  buildLocalDraftThread,
  buildThreadCreateModelSelection,
  shouldRenderTerminalWorkspace,
  buildComposerQueueEnqueueInput,
  cloneComposerImageForRetry,
  collectUserMessageBlobPreviewUrls,
  type ComposerPluginSuggestion,
  createLocalDispatchSnapshot,
  deriveComposerSendState,
  formatOutgoingPrompt,
  isSaveComposerToQueueShortcut,
  promptIncludesSkillMention,
  resolvePromptPluginMentions,
  skillMentionPrefix,
  summarizeQueuedComposerTurns,
  hasServerAcknowledgedLocalDispatch,
  LAST_INVOKED_SCRIPT_BY_PROJECT_KEY,
  LastInvokedScriptByProjectSchema,
  type LocalDispatchSnapshot,
  readFileAsDataUrl,
  revokeBlobPreviewUrl,
  revokeUserMessagePreviewUrls,
} from "./ChatView.logic";
import { openNewThreadDialog } from "../newThreadDialogStore";
import { useLocalStorage } from "~/hooks/useLocalStorage";
import { useNowAfter } from "~/hooks/useNowAfter";
import { useComposerSlashCommands } from "../hooks/useComposerSlashCommands";
import { useHandleNewThread } from "../hooks/useHandleNewThread";
import {
  canCreateThreadHandoff,
  resolveHandoffTargetProvider,
  resolveThreadHandoffBadgeLabel,
} from "../lib/threadHandoff";
import {
  resolveDiffEnvironmentState,
  resolveThreadEnvironmentMode,
} from "../lib/threadEnvironment";
import { buildNextProviderOptions } from "../providerModelOptions";
import { useThreadEnvironmentBadges } from "../hooks/useThreadEnvironment";
import { useProjectUiCapabilities } from "~/auth/projectUiCapabilities";

const ATTACHMENT_PREVIEW_HANDOFF_TTL_MS = 5000;
const EMPTY_ACTIVITIES: OrchestrationThreadActivity[] = [];
const EMPTY_MESSAGES: ChatMessage[] = [];
const EMPTY_KEYBINDINGS: ResolvedKeybindingsConfig = [];
const EMPTY_PROJECT_ENTRIES: ProjectEntry[] = [];
const EMPTY_PROVIDER_NATIVE_COMMANDS: ProviderNativeCommandDescriptor[] = [];
const EMPTY_PROVIDER_SKILLS: ProviderSkillDescriptor[] = [];
function eventTargetsComposer(
  event: globalThis.KeyboardEvent,
  composerForm: HTMLFormElement | null,
): boolean {
  if (!composerForm) return false;
  const target = event.target;
  return target instanceof Node ? composerForm.contains(target) : false;
}

function canHandleComposerPickerShortcut(
  event: globalThis.KeyboardEvent,
  composerForm: HTMLFormElement | null,
): boolean {
  if (!composerForm) return false;
  if (eventTargetsComposer(event, composerForm)) return true;
  const target = event.target;
  return (
    target === document.body ||
    target === document.documentElement ||
    document.activeElement === document.body ||
    document.activeElement === document.documentElement
  );
}
const EMPTY_AVAILABLE_EDITORS: EditorId[] = [];
const EMPTY_PROVIDER_STATUSES: ServerProviderStatus[] = [];
const EMPTY_PENDING_USER_INPUT_ANSWERS: Record<string, PendingUserInputDraftAnswer> = {};

const EMPTY_COMPOSER_PLUGIN_SUGGESTIONS: ComposerPluginSuggestion[] = [];

/** What the composer hands to the queue. The rest of the snapshot is read off live state. */
type QueueComposerMessageRequest =
  | {
      kind: "chat";
      prompt: string;
      trimmedPrompt: string;
      attachments: ReadonlyArray<ComposerAttachment>;
      quotedContexts: ReadonlyArray<QuotedContextDraft>;
    }
  | { kind: "plan-follow-up"; text: string; interactionMode: "default" | "plan" };

const COMPOSER_PATH_QUERY_DEBOUNCE_MS = 120;
const SCRIPT_TERMINAL_COLS = 120;
const SCRIPT_TERMINAL_ROWS = 30;
const VOICE_RECORDER_ACTION_ARM_DELAY_MS = 250;
// How long to keep re-checking provider health after starting a CLI login, and
// how often. An OAuth round trip through the browser takes a while, so the
// window is generous; the interval stays coarse because each poll shells out
// to the provider CLI on the server.
const REAUTH_POLL_WINDOW_MS = 180_000;
const REAUTH_POLL_INTERVAL_MS = 5_000;

function warnVoiceGuard(event: string, details?: Record<string, unknown>) {
  if (!import.meta.env.DEV) {
    return;
  }
  if (details) {
    console.warn(`[voice] ${event}`, details);
    return;
  }
  console.warn(`[voice] ${event}`);
}

const providerMentionReferencesEqual = (
  left: ReadonlyArray<ProviderMentionReference>,
  right: ReadonlyArray<ProviderMentionReference>,
): boolean =>
  left.length === right.length &&
  left.every(
    (mention, index) => mention.path === right[index]?.path && mention.name === right[index]?.name,
  );

const extendReplacementRangeForTrailingSpace = (
  text: string,
  rangeEnd: number,
  replacement: string,
): number => {
  if (!replacement.endsWith(" ")) {
    return rangeEnd;
  }
  return text[rangeEnd] === " " ? rangeEnd + 1 : rangeEnd;
};

const syncTerminalContextsByIds = (
  contexts: ReadonlyArray<QuotedContextDraft>,
  ids: ReadonlyArray<string>,
): QuotedContextDraft[] => {
  const contextsById = new Map(contexts.map((context) => [context.id, context]));
  return ids.flatMap((id) => {
    const context = contextsById.get(id);
    return context ? [context] : [];
  });
};

const terminalContextIdListsEqual = (
  contexts: ReadonlyArray<QuotedContextDraft>,
  ids: ReadonlyArray<string>,
): boolean =>
  contexts.length === ids.length && contexts.every((context, index) => context.id === ids[index]);

interface ChatViewProps {
  threadId: ThreadId;
  paneScopeId?: string;
  surfaceMode?: "single" | "split";
  isFocusedPane?: boolean;
  panelState?: SplitViewPanePanelState;
  onToggleDiffPanel?: () => void;
  onToggleBrowserPanel?: () => void;
  onToggleFilesPanel?: () => void;
  onOpenSideChatPanel?: () => void;
  onOpenTurnDiffPanel?: (turnId: TurnId, filePath?: string) => void;
  onSplitSurface?: () => void;
  onMaximizeSurface?: () => void;
}

export default function ChatView({
  threadId,
  paneScopeId = "single",
  surfaceMode = "single",
  isFocusedPane = true,
  panelState,
  onToggleDiffPanel,
  onToggleBrowserPanel,
  onToggleFilesPanel,
  onOpenSideChatPanel,
  onOpenTurnDiffPanel,
  onSplitSurface,
  onMaximizeSurface,
}: ChatViewProps) {
  // Whether this person was granted a recap. Gated here rather than on the
  // server because it is a preference, not a boundary: the sentence summarises a
  // thread they can already open in full, so there is nothing to withhold. What
  // the grant really buys is on the server — see `RecapReactor`, where a member
  // without it stops counting as somebody who missed a turn, and a thread whose
  // whole audience lacks it never reaches the model.
  const receivesRecap = useHasGlobalPermission("threads.recap");
  const markThreadVisited = useStore((store) => store.markThreadVisited);
  const syncServerReadModel = useStore((store) => store.syncServerReadModel);
  const setStoreThreadError = useStore((store) => store.setError);
  const setStoreThreadWorkspace = useStore((store) => store.setThreadWorkspace);
  const { settings } = useAppSettings();
  const setStickyComposerModelSelection = useComposerDraftStore(
    (store) => store.setStickyModelSelection,
  );
  const timestampFormat = settings.timestampFormat;
  const navigate = useNavigate();
  const { handleNewThread } = useHandleNewThread();
  const { createThreadHandoff } = useThreadHandoff();
  const rawSearch = useSearch({
    strict: false,
    select: (params) => parseDiffRouteSearch(params),
  });
  const activeSplitView = useSplitViewStore(selectSplitView(rawSearch.splitViewId ?? null));
  const removeThreadFromSplitViews = useSplitViewStore((store) => store.removeThreadFromSplitViews);
  const { resolvedTheme } = useTheme();
  const queryClient = useQueryClient();
  const createWorktreeMutation = useMutation(gitCreateWorktreeMutationOptions({ queryClient }));
  const composerDraft = useComposerThreadDraft(threadId);
  const prompt = composerDraft.prompt;
  const composerAttachments = composerDraft.attachments;
  const composerQuotedContexts = composerDraft.quotedContexts;
  const composerQueueEntry = useComposerQueueStore((store) =>
    selectComposerQueueEntry(store, threadId.length > 0 ? threadId : null),
  );
  const queuedComposerMessages = composerQueueEntry.items;
  const loadComposerQueue = useComposerQueueStore((store) => store.load);
  const enqueueComposerQueueMessage = useComposerQueueStore((store) => store.enqueue);
  const setComposerQueueAutoSend = useComposerQueueStore((store) => store.setAutoSend);
  const removeComposerQueueMessage = useComposerQueueStore((store) => store.remove);
  const sendComposerQueueMessage = useComposerQueueStore((store) => store.send);
  const [queuedMessagesOpen, setQueuedMessagesOpen] = useState(false);
  const {
    isRecording: isVoiceRecording,
    durationMs: voiceRecordingDurationMs,
    waveformLevels: voiceWaveformLevels,
    startRecording: startVoiceRecording,
    stopRecording: stopVoiceRecording,
    cancelRecording: cancelVoiceRecording,
  } = useVoiceRecorder();
  const [isVoiceTranscribing, setIsVoiceTranscribing] = useState(false);
  const queuedComposerSummary = useMemo(
    () => summarizeQueuedComposerTurns(queuedComposerMessages),
    [queuedComposerMessages],
  );
  const composerSendState = useMemo(
    () =>
      deriveComposerSendState({
        prompt,
        imageCount: composerAttachments.length,
        quotedContexts: composerQuotedContexts,
      }),
    [composerAttachments.length, composerQuotedContexts, prompt],
  );
  const nonPersistedComposerAttachmentIds = composerDraft.nonPersistedAttachmentIds;
  const setComposerDraftPrompt = useComposerDraftStore((store) => store.setPrompt);
  const setComposerDraftModelSelection = useComposerDraftStore((store) => store.setModelSelection);
  const setComposerDraftProviderModelOptions = useComposerDraftStore(
    (store) => store.setProviderModelOptions,
  );
  const setComposerDraftRuntimeMode = useComposerDraftStore((store) => store.setRuntimeMode);
  const setComposerDraftInteractionMode = useComposerDraftStore(
    (store) => store.setInteractionMode,
  );
  const addComposerDraftAttachment = useComposerDraftStore((store) => store.addAttachment);
  const addComposerDraftAttachments = useComposerDraftStore((store) => store.addAttachments);
  const removeComposerDraftAttachment = useComposerDraftStore((store) => store.removeAttachment);
  const insertComposerDraftTerminalContext = useComposerDraftStore(
    (store) => store.insertQuotedContext,
  );
  const addComposerDraftTerminalContexts = useComposerDraftStore(
    (store) => store.addQuotedContexts,
  );
  const removeComposerDraftTerminalContext = useComposerDraftStore(
    (store) => store.removeQuotedContext,
  );
  const setComposerDraftTerminalContexts = useComposerDraftStore(
    (store) => store.setQuotedContexts,
  );
  const clearComposerDraftPersistedAttachments = useComposerDraftStore(
    (store) => store.clearPersistedAttachments,
  );
  const syncComposerDraftPersistedAttachments = useComposerDraftStore(
    (store) => store.syncPersistedAttachments,
  );
  const clearComposerDraftContent = useComposerDraftStore((store) => store.clearComposerContent);
  const setDraftThreadContext = useComposerDraftStore((store) => store.setDraftThreadContext);
  const draftThread = useComposerDraftStore(
    (store) => store.draftThreadsByThreadId[threadId] ?? null,
  );
  const serverThread = useStore(useMemo(() => createThreadSelector(threadId), [threadId]));
  const fallbackDraftProjectId = draftThread?.projectId ?? null;
  const fallbackDraftProject = useStore(
    useMemo(() => createProjectSelector(fallbackDraftProjectId), [fallbackDraftProjectId]),
  );
  const promptRef = useRef(prompt);
  const [isDragOverComposer, setIsDragOverComposer] = useState(false);
  const [expandedImage, setExpandedImage] = useState<ExpandedImagePreview | null>(null);
  const [optimisticUserMessages, setOptimisticUserMessages] = useState<ChatMessage[]>([]);
  const optimisticUserMessagesRef = useRef(optimisticUserMessages);
  optimisticUserMessagesRef.current = optimisticUserMessages;
  const composerTerminalContextsRef = useRef<QuotedContextDraft[]>(composerQuotedContexts);
  const [localDraftErrorsByThreadId, setLocalDraftErrorsByThreadId] = useState<
    Record<ThreadId, string | null>
  >({});
  const [localDispatch, setLocalDispatch] = useState<LocalDispatchSnapshot | null>(null);
  const [isConnecting, _setIsConnecting] = useState(false);
  const [isRevertingCheckpoint, setIsRevertingCheckpoint] = useState(false);
  const [respondingRequestIds, setRespondingRequestIds] = useState<ApprovalRequestId[]>([]);
  const [respondingUserInputRequestIds, setRespondingUserInputRequestIds] = useState<
    ApprovalRequestId[]
  >([]);
  const [pendingUserInputAnswersByRequestId, setPendingUserInputAnswersByRequestId] = useState<
    Record<string, Record<string, PendingUserInputDraftAnswer>>
  >({});
  const [pendingUserInputQuestionIndexByRequestId, setPendingUserInputQuestionIndexByRequestId] =
    useState<Record<string, number>>({});
  // Per question: whether the user folded it away to read the conversation
  // behind it. Keyed by request so a new question always arrives open.
  const [collapsedPendingUserInputByRequestId, setCollapsedPendingUserInputByRequestId] = useState<
    Record<string, boolean>
  >({});
  const [expandedWorkGroups, setExpandedWorkGroups] = useState<Record<string, boolean>>({});
  // Per tool call: whether its detail panel (command + output) is open.
  const [expandedWorkEntries, setExpandedWorkEntries] = useState<Record<string, boolean>>({});
  const [planSidebarOpen, setPlanSidebarOpen] = useState(false);
  const [isComposerFooterCompact, setIsComposerFooterCompact] = useState(false);
  const [composerCommandPicker, setComposerCommandPicker] = useState<
    null | "fork-target" | "review-target"
  >(null);
  // Tracks whether the user explicitly dismissed the sidebar for the active turn.
  const planSidebarDismissedForTurnRef = useRef<string | null>(null);
  // When set, the thread-change reset effect will open the sidebar instead of closing it.
  // Used by "Implement in a new thread" to carry the sidebar-open intent across navigation.
  const planSidebarOpenOnNextThreadRef = useRef(false);
  const [terminalFocusRequestId, setTerminalFocusRequestId] = useState(0);
  const [isReauthenticatingProvider, setIsReauthenticatingProvider] = useState(false);
  // Epoch ms until which we poll provider health after launching a CLI login.
  // The login is interactive and finishes in the terminal, so there is no
  // completion signal to await — polling is what clears the banner on its own.
  const [reauthPollDeadline, setReauthPollDeadline] = useState<number | null>(null);
  const [reauthPollTick, setReauthPollTick] = useState(0);
  const [composerHighlightedItemId, setComposerHighlightedItemId] = useState<string | null>(null);
  const [attachmentPreviewHandoffByMessageId, setAttachmentPreviewHandoffByMessageId] = useState<
    Record<string, Record<string, string>>
  >({});
  const [composerCursor, setComposerCursor] = useState(() =>
    collapseExpandedComposerCursor(prompt, prompt.length),
  );
  const [composerTrigger, setComposerTrigger] = useState<ComposerTrigger | null>(() =>
    detectComposerTrigger(prompt, prompt.length),
  );
  const [selectedComposerSkills, setSelectedComposerSkills] = useState<ProviderSkillReference[]>(
    [],
  );
  const [selectedComposerMentions, setSelectedComposerMentions] = useState<
    ProviderMentionReference[]
  >([]);
  const [lastInvokedScriptByProjectId, setLastInvokedScriptByProjectId] = useLocalStorage(
    LAST_INVOKED_SCRIPT_BY_PROJECT_KEY,
    {},
    LastInvokedScriptByProjectSchema,
  );
  const [isModelPickerOpen, setIsModelPickerOpen] = useState(false);
  const [isTraitsPickerOpen, setIsTraitsPickerOpen] = useState(false);

  useEffect(() => {
    setComposerCommandPicker(null);
    setIsModelPickerOpen(false);
    setIsTraitsPickerOpen(false);
  }, [threadId]);
  useEffect(() => {
    // Thread-bound handoff dialog state is reset by the dedicated hook.
  }, [threadId]);
  const composerEditorRef = useRef<ComposerPromptEditorHandle>(null);
  const composerFormRef = useRef<HTMLFormElement>(null);
  const composerFormHeightRef = useRef(0);
  const composerAttachmentsRef = useRef<ComposerAttachment[]>([]);
  const composerSelectLockRef = useRef(false);
  const composerMenuOpenRef = useRef(false);
  const composerMenuItemsRef = useRef<ComposerCommandItem[]>([]);
  const activeComposerMenuItemRef = useRef<ComposerCommandItem | null>(null);
  const attachmentPreviewHandoffByMessageIdRef = useRef<Record<string, Record<string, string>>>({});
  const attachmentPreviewHandoffTimeoutByMessageIdRef = useRef<Record<string, number>>({});
  const sendInFlightRef = useRef(false);
  const dragDepthRef = useRef(0);
  const terminalOpenByThreadRef = useRef<Record<string, boolean>>({});
  const activatedThreadIdRef = useRef<ThreadId | null>(null);

  const terminalState = useTerminalStateStore((state) =>
    selectThreadTerminalState(state.terminalStateByThreadId, threadId),
  );
  const storeSetTerminalOpen = useTerminalStateStore((s) => s.setTerminalOpen);
  const storeSetTerminalPresentationMode = useTerminalStateStore(
    (s) => s.setTerminalPresentationMode,
  );
  const storeSetTerminalWorkspaceLayout = useTerminalStateStore(
    (s) => s.setTerminalWorkspaceLayout,
  );
  const storeOpenTerminalThreadPage = useTerminalStateStore((s) => s.openTerminalThreadPage);
  const storeSetTerminalWorkspaceTab = useTerminalStateStore((s) => s.setTerminalWorkspaceTab);
  const storeSetTerminalHeight = useTerminalStateStore((s) => s.setTerminalHeight);
  const storeSetTerminalMetadata = useTerminalStateStore((s) => s.setTerminalMetadata);
  const storeSetTerminalActivity = useTerminalStateStore((s) => s.setTerminalActivity);
  const storeSplitTerminalLeft = useTerminalStateStore((s) => s.splitTerminalLeft);
  const storeSplitTerminalRight = useTerminalStateStore((s) => s.splitTerminalRight);
  const storeSplitTerminalDown = useTerminalStateStore((s) => s.splitTerminalDown);
  const storeSplitTerminalUp = useTerminalStateStore((s) => s.splitTerminalUp);
  const storeNewTerminal = useTerminalStateStore((s) => s.newTerminal);
  const storeNewTerminalTab = useTerminalStateStore((s) => s.newTerminalTab);
  const storeOpenNewFullWidthTerminal = useTerminalStateStore((s) => s.openNewFullWidthTerminal);
  const storeCloseWorkspaceChat = useTerminalStateStore((s) => s.closeWorkspaceChat);
  const storeSetActiveTerminal = useTerminalStateStore((s) => s.setActiveTerminal);
  const storeCloseTerminal = useTerminalStateStore((s) => s.closeTerminal);
  const storeCloseTerminalGroup = useTerminalStateStore((s) => s.closeTerminalGroup);
  const storeResizeTerminalSplit = useTerminalStateStore((s) => s.resizeTerminalSplit);
  const storeClearTerminalState = useTerminalStateStore((s) => s.clearTerminalState);

  const setPrompt = useCallback(
    (nextPrompt: string) => {
      setComposerDraftPrompt(threadId, nextPrompt);
    },
    [setComposerDraftPrompt, threadId],
  );
  const addComposerAttachment = useCallback(
    (attachment: ComposerAttachment) => {
      addComposerDraftAttachment(threadId, attachment);
    },
    [addComposerDraftAttachment, threadId],
  );
  const addComposerAttachmentsToDraft = useCallback(
    (attachments: ComposerAttachment[]) => {
      addComposerDraftAttachments(threadId, attachments);
    },
    [addComposerDraftAttachments, threadId],
  );
  const addComposerTerminalContextsToDraft = useCallback(
    (contexts: QuotedContextDraft[]) => {
      addComposerDraftTerminalContexts(threadId, contexts);
    },
    [addComposerDraftTerminalContexts, threadId],
  );
  const removeComposerAttachmentFromDraft = useCallback(
    (attachmentId: string) => {
      removeComposerDraftAttachment(threadId, attachmentId);
    },
    [removeComposerDraftAttachment, threadId],
  );
  const removeComposerTerminalContextFromDraft = useCallback(
    (contextId: string) => {
      const contextIndex = composerQuotedContexts.findIndex((context) => context.id === contextId);
      if (contextIndex < 0) {
        return;
      }
      const nextPrompt = removeInlineQuotedContextPlaceholder(promptRef.current, contextIndex);
      promptRef.current = nextPrompt.prompt;
      setPrompt(nextPrompt.prompt);
      removeComposerDraftTerminalContext(threadId, contextId);
      setComposerCursor(nextPrompt.cursor);
      setComposerTrigger(
        detectComposerTrigger(
          nextPrompt.prompt,
          expandCollapsedComposerCursor(nextPrompt.prompt, nextPrompt.cursor),
        ),
      );
    },
    [composerQuotedContexts, removeComposerDraftTerminalContext, setPrompt, threadId],
  );

  const localDraftError = serverThread ? null : (localDraftErrorsByThreadId[threadId] ?? null);
  const localDraftThread = useMemo(
    () =>
      draftThread
        ? buildLocalDraftThread(
            threadId,
            draftThread,
            fallbackDraftProject?.defaultModelSelection ?? {
              provider: "codex",
              model: DEFAULT_MODEL_BY_PROVIDER.codex,
            },
            localDraftError,
          )
        : undefined,
    [draftThread, fallbackDraftProject?.defaultModelSelection, localDraftError, threadId],
  );
  const activeThread = serverThread ?? localDraftThread;
  const runtimeMode =
    composerDraft.runtimeMode ?? activeThread?.runtimeMode ?? DEFAULT_RUNTIME_MODE;
  const interactionMode =
    composerDraft.interactionMode ?? activeThread?.interactionMode ?? DEFAULT_INTERACTION_MODE;
  const isServerThread = serverThread !== undefined;
  // A server thread renders from its shell row the moment it is clicked, while
  // its transcript snapshot is still in flight. Without this the empty pane
  // claims the thread has no messages.
  const isThreadDetailHydrated = useStore(
    useMemo(() => createThreadDetailHydratedSelector(threadId), [threadId]),
  );
  const isTranscriptLoading = isServerThread && !isThreadDetailHydrated;
  const { hasMoreHistory, isLoadingOlderHistory, loadOlderHistory } =
    useOlderThreadHistory(threadId);
  const isLocalDraftThread = !isServerThread && localDraftThread !== undefined;
  const canCheckoutPullRequestIntoThread = isLocalDraftThread;
  const diffOpen = rawSearch.panel === "diff";
  const browserOpen = rawSearch.panel === "browser";
  const filesOpen = rawSearch.panel === "files";
  const resolvedDiffOpen = panelState ? panelState.panel === "diff" : diffOpen;
  const resolvedBrowserOpen = panelState ? panelState.panel === "browser" : browserOpen;
  const resolvedFilesOpen = panelState ? panelState.panel === "files" : filesOpen;
  const activeThreadId = activeThread?.id ?? null;
  const activeLatestTurn = activeThread?.latestTurn ?? null;
  const threadActivities = activeThread?.activities ?? EMPTY_ACTIVITIES;
  const hasLiveTurnTail = hasLiveTurnTailWork({
    latestTurn: activeLatestTurn,
    messages: activeThread?.messages ?? EMPTY_MESSAGES,
    activities: threadActivities,
    session: activeThread?.session ?? null,
  });
  const activeContextWindow = useMemo(
    () => deriveLatestContextWindowSnapshot(threadActivities),
    [threadActivities],
  );
  const activeCumulativeCostUsd = useMemo(
    () => deriveCumulativeCostUsd(threadActivities),
    [threadActivities],
  );
  const activeRateLimitStatus = useMemo(
    () => deriveLatestRateLimitStatus(threadActivities),
    [threadActivities],
  );
  const latestTurnSettledByProvider = isLatestTurnSettled(
    activeLatestTurn,
    activeThread?.session ?? null,
  );
  const latestTurnSettled = latestTurnSettledByProvider && !hasLiveTurnTail;
  const activeProjectId = activeThread?.projectId ?? draftThread?.projectId ?? null;
  // What this account's grants let the screen show. A PM working in a
  // persistent environment gets the conversation and the preview; the diff, the
  // plan, the model picker and the branch line are the four surfaces an admin
  // can take away without taking away the chat.
  const uiCapabilities = useProjectUiCapabilities(activeProjectId);
  const activeProject = useStore(
    useMemo(() => createProjectSelector(activeProjectId), [activeProjectId]),
  );
  const threadLineageThreads = useStore(
    useMemo(() => createThreadLineageSelector(activeThread?.id ?? null), [activeThread?.id]),
  );
  const threadBreadcrumbs = useMemo(
    () => buildThreadBreadcrumbs(threadLineageThreads, activeThread),
    [activeThread, threadLineageThreads],
  );
  // Own link first, then the nearest ancestor's; a draft not yet promoted has
  // no lineage at all, so it falls back to the issue it was seeded with.
  const activeThreadPlaneIssue = useMemo(() => {
    const fromLineage = deriveThreadPlaneIssue(threadLineageThreads);
    if (fromLineage) return fromLineage;
    const draftRef = draftThread?.planeIssue?.ref ?? null;
    return draftRef ? { ref: draftRef, inherited: false } : null;
  }, [threadLineageThreads, draftThread?.planeIssue?.ref]);
  // Which environment this thread runs in, by name: the badge has to tell a
  // personal workspace from the team's shared one.
  const { environmentLabel: activeThreadEnvironmentLabel, trialLabel: activeThreadTrialLabel } =
    useThreadEnvironmentBadges({
      projectId: (activeThread?.projectId ?? null) as ProjectId | null,
      environmentId: (activeThread?.environmentId ?? null) as EnvironmentId | null,
    });
  const resolvedThreadEnvMode = isServerThread
    ? (activeThread?.envMode ?? null)
    : (draftThread?.envMode ?? null);
  const resolvedThreadWorktreePath = isServerThread
    ? (activeThread?.worktreePath ?? null)
    : (draftThread?.worktreePath ?? null);
  const diffEnvironmentState = resolveDiffEnvironmentState({
    projectCwd: activeProject?.cwd ?? null,
    envMode: resolvedThreadEnvMode,
    worktreePath: resolvedThreadWorktreePath,
  });
  const diffEnvironmentPending = diffEnvironmentState.pending;
  const diffDisabledReason = diffEnvironmentState.disabledReason;
  const activeThreadAssociatedWorktree = useMemo(
    () =>
      deriveAssociatedWorktreeMetadata({
        branch: activeThread?.branch ?? null,
        worktreePath: activeThread?.worktreePath ?? null,
        associatedWorktreePath: activeThread?.associatedWorktreePath ?? null,
        associatedWorktreeBranch: activeThread?.associatedWorktreeBranch ?? null,
        associatedWorktreeRef: activeThread?.associatedWorktreeRef ?? null,
      }),
    [
      activeThread?.associatedWorktreeBranch,
      activeThread?.associatedWorktreePath,
      activeThread?.associatedWorktreeRef,
      activeThread?.branch,
      activeThread?.worktreePath,
    ],
  );

  /**
   * The branch selector's "check out this pull request" entry, now that the dialog it used to open
   * is gone. It hands off to the New thread dialog, which is where a thread is opened on a pull
   * request from anywhere else.
   *
   * It preselects the project but not the pull request: this call site has a reference string —
   * whatever the current branch's pull request happens to be — and the dialog's picker wants the
   * head branch and title too. Resolving them here would mean a second read of something the
   * picker is about to list anyway, so the pull request is one click away rather than preselected.
   */
  const openPullRequestDialog = useCallback(() => {
    if (!canCheckoutPullRequestIntoThread || !activeProject) {
      return;
    }
    openNewThreadDialog({ preferredProjectId: activeProject.id });
    setComposerHighlightedItemId(null);
  }, [activeProject, canCheckoutPullRequestIntoThread]);

  // The turn this pane has already acknowledged, so a completion is written at
  // most once no matter how the store answers. Belt to the resolver's braces:
  // the write feeds one of this effect's own dependencies, so anything that
  // stops it from landing — a clock, a stale row, a future merge rule — turns
  // into a render loop, and React kills the whole screen when it does.
  const acknowledgedTurnCompletionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeThread?.id) return;
    if (!latestTurnSettled) return;
    const visitedAt = resolveTurnVisitAcknowledgement({
      turnCompletedAt: activeLatestTurn?.completedAt,
      lastVisitedAt: activeThread.lastVisitedAt,
    });
    if (visitedAt === null) return;
    const turnCompletion = `${activeThread.id}:${activeLatestTurn?.completedAt}`;
    if (acknowledgedTurnCompletionRef.current === turnCompletion) return;
    acknowledgedTurnCompletionRef.current = turnCompletion;

    markThreadVisited(activeThread.id, visitedAt);
    // And to the server, so the same thread is not still bold on the phone.
    // Fire-and-forget: the local store has already moved on, and this only ever
    // moves forward, so a lost write is corrected by the next visit.
    const api = readNativeApi();
    if (api) {
      recordThreadVisit({ api: api.orchestration, threadId: activeThread.id, visitedAt });
    }
  }, [
    activeThread?.id,
    activeThread?.lastVisitedAt,
    activeLatestTurn?.completedAt,
    latestTurnSettled,
    markThreadVisited,
  ]);

  // Where a snooze of this reader's ended, shown at the tail of the transcript.
  //
  // Held in state rather than read from the thread on every render because
  // opening the thread is exactly what clears the mark: the live value is gone
  // by the time this would paint. Frozen at open, dropped when the thread is
  // left — it is a note about arriving here, not a fact about the thread.
  const [snoozeReturnAt, setSnoozeReturnAt] = useState<string | null>(null);
  const snoozeReturnHandledThreadIdRef = useRef<ThreadId | null>(null);

  useEffect(() => {
    setSnoozeReturnAt(null);
    snoozeReturnHandledThreadIdRef.current = null;
  }, [activeThread?.id]);

  useEffect(() => {
    if (!activeThread?.id) return;
    if (snoozeReturnHandledThreadIdRef.current === activeThread.id) return;
    if (!threadReturnedFromSnooze(activeThread)) return;
    snoozeReturnHandledThreadIdRef.current = activeThread.id;
    setSnoozeReturnAt(activeThread.wokenAt ?? null);

    // Opening the thread is the acknowledgement, so the read instant moves now
    // rather than waiting for the unread-completion rule above — which would
    // never fire on a thread that came back with nothing new on it, and would
    // leave the mark on the row forever.
    const visitedAt = new Date().toISOString();
    markThreadVisited(activeThread.id, visitedAt);
    const api = readNativeApi();
    if (api) {
      recordThreadVisit({ api: api.orchestration, threadId: activeThread.id, visitedAt });
    }
  }, [activeThread, markThreadVisited]);

  const sessionProvider = activeThread?.session?.provider ?? null;
  const selectedProviderByThreadId = composerDraft.activeProvider ?? null;
  const threadProvider =
    activeThread?.modelSelection.provider ?? activeProject?.defaultModelSelection?.provider ?? null;
  const hasThreadStarted = Boolean(
    activeThread &&
    (activeThread.latestTurn !== null ||
      activeThread.messages.length > 0 ||
      activeThread.session !== null),
  );
  const lockedProvider: ProviderKind | null = hasThreadStarted
    ? (sessionProvider ?? threadProvider ?? selectedProviderByThreadId ?? null)
    : null;
  const selectedProvider: ProviderKind =
    lockedProvider ?? selectedProviderByThreadId ?? threadProvider ?? settings.defaultProvider;
  const [dismissedResumeCompactionThreads, setDismissedResumeCompactionThreads] = useLocalStorage(
    RESUME_COMPACTION_DISMISSED_STORAGE_KEY,
    NO_DISMISSED_RESUME_COMPACTION_THREADS,
    DismissedResumeCompactionThreads,
  );
  const [dismissedResumeCompactionKeys, setDismissedResumeCompactionKeys] = useState<
    ReadonlySet<string>
  >(new Set());
  const nativeResumeCompactionDismissed = useMemo(
    () => hasDismissedResumeCompaction(threadActivities),
    [threadActivities],
  );
  // The "never ask again" answer is a fact about this thread, not about the
  // person reading it: remember it per thread so opening someone else's thread
  // cannot silence the banner everywhere for the reader.
  const resumeCompactionThreadDismissed =
    activeThread != null && dismissedResumeCompactionThreads.includes(activeThread.id);
  useEffect(() => {
    if (!activeThreadId || !nativeResumeCompactionDismissed) return;
    setDismissedResumeCompactionThreads((current) =>
      rememberDismissedResumeCompactionThread(current, activeThreadId),
    );
  }, [activeThreadId, nativeResumeCompactionDismissed, setDismissedResumeCompactionThreads]);
  const voiceTranscriptionRequestIdRef = useRef(0);
  const voiceThreadIdRef = useRef(threadId);
  const voiceProviderRef = useRef<ProviderKind>(selectedProvider);
  const voiceRecordingStartedAtRef = useRef<number | null>(null);
  voiceThreadIdRef.current = threadId;
  voiceProviderRef.current = selectedProvider;
  const customModelsByProvider = useMemo(() => getCustomModelsByProvider(settings), [settings]);
  // Before deriving what to show: a user whose policy grants an agent and no
  // models inherits a selection the server refuses, so correct the draft first.
  useReconcileComposerModelSelection({
    threadId,
    hasThreadStarted,
    threadModelSelection: activeThread?.modelSelection,
    projectModelSelection: activeProject?.defaultModelSelection,
    defaultProvider: settings.defaultProvider,
  });
  const {
    modelOptions: composerModelOptions,
    selectedModel,
    selectedAgentId,
  } = useEffectiveComposerModelState({
    threadId,
    selectedProvider,
    threadModelSelection: activeThread?.modelSelection,
    projectModelSelection: activeProject?.defaultModelSelection,
    customModelsByProvider,
  });
  const composerProviderState = useMemo(
    () =>
      getComposerProviderState({
        provider: selectedProvider,
        model: selectedModel,
        prompt,
        modelOptions: composerModelOptions,
      }),
    [composerModelOptions, prompt, selectedModel, selectedProvider],
  );
  const selectedPromptEffort = composerProviderState.promptEffort;
  const selectedModelOptionsForDispatch = composerProviderState.modelOptionsForDispatch;
  const selectedModelSelection = useMemo<ModelSelection>(
    () => ({
      provider: selectedProvider,
      model: selectedModel,
      ...(selectedAgentId ? { agentId: selectedAgentId } : {}),
      ...(selectedModelOptionsForDispatch ? { options: selectedModelOptionsForDispatch } : {}),
    }),
    [selectedModel, selectedAgentId, selectedModelOptionsForDispatch, selectedProvider],
  );
  const providerOptionsForDispatch = useMemo(() => getProviderStartOptions(settings), [settings]);
  const selectedModelForPicker = selectedModel;
  const claudeDynamicAgentsQuery = useQuery(
    providerAgentsQueryOptions({ provider: "claudeAgent" }),
  );
  const codexDynamicAgentsQuery = useQuery(providerAgentsQueryOptions({ provider: "codex" }));
  // Model options, managed agents (Settings → Agents — not the provider-native
  // subagents queried just above) and provider statuses, resolved the same way
  // the "New thread" dialog resolves them.
  const { modelOptionsByProvider, visibleManagedAgents, providerStatuses } =
    useProviderModelCatalog();
  // The picker is already filtered by this allowlist; the header needs it too,
  // to flag a thread whose persisted model the user may no longer use.
  const allowedModelKeys = useAuthStore((state) => state.allowedModelKeys);
  // Same idea for agents, with one difference: an empty set means the account
  // has no personal agent policy, so it must not flag anything.
  const allowedAgentIds = useAuthStore((state) => state.allowedAgentIds);
  const currentUser = useAuthStore((state) => state.user);
  // Optimistic user messages are painted before the server confirms the
  // event, so authorship has to come from the local session rather than the
  // read model. Installs without auth have no session user — those messages
  // stay unlabeled until the server round-trip resolves (or never, if auth
  // is off), same as any other message with no known author.
  const optimisticAuthorFields = currentUser
    ? { authorUserId: currentUser.id, authorDisplayName: currentUser.name }
    : {};
  const selectedModelForPickerWithCustomFallback = useMemo(() => {
    const currentOptions = modelOptionsByProvider[selectedProvider];
    return currentOptions.some((option) => option.slug === selectedModelForPicker)
      ? selectedModelForPicker
      : (normalizeModelSlug(selectedModelForPicker, selectedProvider) ?? selectedModelForPicker);
  }, [modelOptionsByProvider, selectedModelForPicker, selectedProvider]);
  const searchableModelOptions = useMemo(
    () =>
      AVAILABLE_PROVIDER_OPTIONS.filter(
        (option) => lockedProvider === null || option.value === lockedProvider,
      ).flatMap((option) =>
        modelOptionsByProvider[option.value].map(({ slug, name }) => ({
          provider: option.value,
          providerLabel: option.label,
          slug,
          name,
          searchSlug: slug.toLowerCase(),
          searchName: name.toLowerCase(),
          searchProvider: option.label.toLowerCase(),
        })),
      ),
    [lockedProvider, modelOptionsByProvider],
  );
  const phase = derivePhase(activeThread?.session ?? null);
  const rawWorkLogEntries = useMemo(
    () => deriveWorkLogEntries(threadActivities),
    [threadActivities],
  );
  const hasWorkLogSubagents = useMemo(
    () =>
      rawWorkLogEntries.some(
        (entry) =>
          (entry.subagentTask?.receiverProviderThreadIds?.length ?? 0) > 0 ||
          entry.subagentTask?.taskToolUseId !== undefined,
      ),
    [rawWorkLogEntries],
  );
  const relevantWorkLogThreads = useStore(
    useMemo(
      () =>
        createRelevantWorkLogThreadsSelector({
          workEntries: rawWorkLogEntries,
          parentThreadId: activeThread?.id ?? null,
          enabled: hasWorkLogSubagents,
        }),
      [activeThread?.id, hasWorkLogSubagents, rawWorkLogEntries],
    ),
  );
  const workLogEntries = useMemo(
    () =>
      hasWorkLogSubagents
        ? enrichSubagentWorkEntries(
            rawWorkLogEntries,
            relevantWorkLogThreads,
            activeThread?.id ?? null,
          )
        : rawWorkLogEntries,
    [activeThread?.id, hasWorkLogSubagents, rawWorkLogEntries, relevantWorkLogThreads],
  );
  const pendingApprovals = useMemo(
    () => derivePendingApprovals(threadActivities, activeLatestTurn ?? null),
    [activeLatestTurn, threadActivities],
  );
  const pendingUserInputs = useMemo(
    () => derivePendingUserInputs(threadActivities, activeLatestTurn ?? null),
    [activeLatestTurn, threadActivities],
  );
  const activePendingUserInput = pendingUserInputs[0] ?? null;
  const activePendingDraftAnswers = useMemo(
    () =>
      activePendingUserInput
        ? (pendingUserInputAnswersByRequestId[activePendingUserInput.requestId] ??
          EMPTY_PENDING_USER_INPUT_ANSWERS)
        : EMPTY_PENDING_USER_INPUT_ANSWERS,
    [activePendingUserInput, pendingUserInputAnswersByRequestId],
  );
  const activePendingQuestionIndex = activePendingUserInput
    ? (pendingUserInputQuestionIndexByRequestId[activePendingUserInput.requestId] ?? 0)
    : 0;
  const activePendingIsCollapsed = activePendingUserInput
    ? (collapsedPendingUserInputByRequestId[activePendingUserInput.requestId] ?? false)
    : false;
  const openUserInputRequestIds = useMemo(
    () => new Set(pendingUserInputs.map((pending) => String(pending.requestId))),
    [pendingUserInputs],
  );
  const activePendingProgress = useMemo(
    () =>
      activePendingUserInput
        ? derivePendingUserInputProgress(
            activePendingUserInput.questions,
            activePendingDraftAnswers,
            activePendingQuestionIndex,
          )
        : null,
    [activePendingDraftAnswers, activePendingQuestionIndex, activePendingUserInput],
  );
  const activePendingResolvedAnswers = useMemo(
    () =>
      activePendingUserInput
        ? buildPendingUserInputAnswers(activePendingUserInput.questions, activePendingDraftAnswers)
        : null,
    [activePendingDraftAnswers, activePendingUserInput],
  );
  const handoffBadgeLabel = useMemo(
    () => (activeThread ? resolveThreadHandoffBadgeLabel(activeThread) : null),
    [activeThread],
  );
  const handoffBadgeSourceProvider = activeThread?.handoff?.sourceProvider ?? null;
  const handoffBadgeTargetProvider = activeThread?.handoff
    ? activeThread.modelSelection.provider
    : null;
  const handoffTargetProvider = useMemo(
    () =>
      activeThread ? resolveHandoffTargetProvider(activeThread.modelSelection.provider) : null,
    [activeThread],
  );
  const handoffActionLabel = useMemo(() => {
    if (!activeThread) {
      return "Create handoff thread";
    }
    return `Handoff to ${PROVIDER_DISPLAY_NAMES[handoffTargetProvider ?? "codex"]}`;
  }, [activeThread, handoffTargetProvider]);
  const activePendingIsResponding = activePendingUserInput
    ? respondingUserInputRequestIds.includes(activePendingUserInput.requestId)
    : false;
  const activeProposedPlan = useMemo(() => {
    if (!latestTurnSettled) {
      return null;
    }
    return findLatestProposedPlan(
      activeThread?.proposedPlans ?? [],
      activeLatestTurn?.turnId ?? null,
    );
  }, [activeLatestTurn?.turnId, activeThread?.proposedPlans, latestTurnSettled]);
  const sidebarPlanSourceThreadId = !latestTurnSettled
    ? (activeLatestTurn?.sourceProposedPlan?.threadId ?? null)
    : null;
  const sidebarPlanSourceThread = useStore(
    useMemo(() => createThreadSelector(sidebarPlanSourceThreadId), [sidebarPlanSourceThreadId]),
  );
  const activeThreadPlanThreadId = activeThread?.id ?? null;
  const activeThreadPlanProposedPlans = activeThread?.proposedPlans;
  const sidebarPlanSourceThreadPlanId = sidebarPlanSourceThread?.id ?? null;
  const sidebarPlanSourceThreadProposedPlans = sidebarPlanSourceThread?.proposedPlans;
  const sidebarProposedPlan = useMemo(
    () =>
      findSidebarProposedPlan({
        threads: [
          ...(activeThreadPlanThreadId
            ? [
                {
                  id: activeThreadPlanThreadId,
                  proposedPlans: activeThreadPlanProposedPlans ?? [],
                },
              ]
            : []),
          ...(sidebarPlanSourceThreadPlanId &&
          sidebarPlanSourceThreadPlanId !== activeThreadPlanThreadId
            ? [
                {
                  id: sidebarPlanSourceThreadPlanId,
                  proposedPlans: sidebarPlanSourceThreadProposedPlans ?? [],
                },
              ]
            : []),
        ],
        latestTurn: activeLatestTurn,
        latestTurnSettled,
        threadId: activeThreadPlanThreadId,
      }),
    [
      activeLatestTurn,
      activeThreadPlanProposedPlans,
      activeThreadPlanThreadId,
      latestTurnSettled,
      sidebarPlanSourceThreadPlanId,
      sidebarPlanSourceThreadProposedPlans,
    ],
  );
  const planSidebarLabel = sidebarProposedPlan || interactionMode === "plan" ? "Plan" : "Tasks";
  const activePlan = useMemo(
    () =>
      latestTurnSettled
        ? null
        : deriveActivePlanState(threadActivities, activeLatestTurn?.turnId ?? undefined),
    [activeLatestTurn?.turnId, latestTurnSettled, threadActivities],
  );
  const activeBackgroundTasks = useMemo(
    () =>
      latestTurnSettled
        ? null
        : deriveActiveBackgroundTasksState(threadActivities, activeLatestTurn?.turnId ?? undefined),
    [activeLatestTurn?.turnId, latestTurnSettled, threadActivities],
  );
  const inSessionSubagentTasks = useMemo(
    () =>
      latestTurnSettled
        ? null
        : deriveActiveSubagentTasksState(threadActivities, activeLatestTurn?.turnId ?? undefined),
    [activeLatestTurn?.turnId, latestTurnSettled, threadActivities],
  );
  // Delegations are not part of the parent's turn: the agent hands work over and
  // carries on, so its own turn settling says nothing about whether the delegate
  // is still working. Only the in-session shapes are gated by the turn.
  const delegatedSubagentTasks = useStore(
    useMemo(
      () => createDelegatedSubagentTasksSelector(activeThread?.id ?? null),
      [activeThread?.id],
    ),
  );
  // Delegations that already answered while this thread sat idle. Nothing else
  // reports them: the thread's own turn ended before the report arrived.
  const delegationsAwaitingParent = useStore(
    useMemo(
      () => createDelegationsAwaitingParentSelector(activeThread?.id ?? null),
      [activeThread?.id],
    ),
  );
  const activeSubagentTasks = useMemo(
    () => combineActiveSubagentTasks(inSessionSubagentTasks, delegatedSubagentTasks),
    [delegatedSubagentTasks, inSessionSubagentTasks],
  );
  const showPlanFollowUpPrompt =
    pendingUserInputs.length === 0 &&
    interactionMode === "plan" &&
    latestTurnSettled &&
    hasActionableProposedPlan(activeProposedPlan);
  const activePendingApproval = pendingApprovals[0] ?? null;
  const serverAcknowledgedLocalDispatch = useMemo(
    () =>
      hasServerAcknowledgedLocalDispatch({
        localDispatch,
        phase,
        latestTurn: activeLatestTurn,
        session: activeThread?.session ?? null,
        hasPendingApproval: activePendingApproval !== null,
        hasPendingUserInput: activePendingUserInput !== null,
        threadError: activeThread?.error,
      }),
    [
      activeLatestTurn,
      activePendingApproval,
      activePendingUserInput,
      activeThread?.error,
      activeThread?.session,
      localDispatch,
      phase,
    ],
  );
  const isSendBusy = localDispatch !== null && !serverAcknowledgedLocalDispatch;
  const isPreparingWorktree = localDispatch?.preparingWorktree ?? false;
  const hasLiveTurn = phase === "running";
  const isWorking = hasLiveTurn || isSendBusy || isConnecting || isRevertingCheckpoint;
  const activeTurnLayoutLive = isWorking || !latestTurnSettled;
  const [keepSettledActiveTurnLayout, setKeepSettledActiveTurnLayout] = useState(false);
  const previousActiveTurnLayoutLiveRef = useRef(activeTurnLayoutLive);
  const previousActiveTurnLayoutKeyRef = useRef<string | null>(null);
  const activeWorkStartedAt = hasLiveTurnTail
    ? (activeLatestTurn?.startedAt ?? localDispatch?.startedAt ?? null)
    : deriveActiveWorkStartedAt(
        activeLatestTurn,
        activeThread?.session ?? null,
        localDispatch?.startedAt ?? null,
      );
  const activeTurnLayoutKey =
    activeThreadId === null ? null : `${activeThreadId}:${activeLatestTurn?.turnId ?? "idle"}`;
  const activeTurnInProgress = activeTurnLayoutLive || keepSettledActiveTurnLayout;
  const isComposerApprovalState = activePendingApproval !== null;
  const composerFooterHasWideActions = showPlanFollowUpPrompt || activePendingProgress !== null;
  const handoffDisabled = !(
    activeThread &&
    activeProject &&
    isServerThread &&
    canCreateThreadHandoff({
      thread: activeThread,
      isBusy: isWorking,
      hasPendingApprovals: pendingApprovals.length > 0,
      hasPendingUserInput: pendingUserInputs.length > 0,
    })
  );
  const lastSyncedPendingInputRef = useRef<{
    requestId: string | null;
    questionId: string | null;
  } | null>(null);
  useLayoutEffect(() => {
    if (previousActiveTurnLayoutKeyRef.current !== activeTurnLayoutKey) {
      previousActiveTurnLayoutKeyRef.current = activeTurnLayoutKey;
      previousActiveTurnLayoutLiveRef.current = activeTurnLayoutLive;
      setKeepSettledActiveTurnLayout(false);
      return;
    }

    const shouldStartGrace = shouldStartActiveTurnLayoutGrace({
      previousTurnLayoutLive: previousActiveTurnLayoutLiveRef.current,
      currentTurnLayoutLive: activeTurnLayoutLive,
      latestTurnStartedAt: activeLatestTurn?.startedAt ?? null,
    });
    previousActiveTurnLayoutLiveRef.current = activeTurnLayoutLive;

    if (activeTurnLayoutLive) {
      setKeepSettledActiveTurnLayout(false);
      return;
    }

    if (!shouldStartGrace) {
      return;
    }

    setKeepSettledActiveTurnLayout(true);
    const timeoutId = window.setTimeout(() => {
      setKeepSettledActiveTurnLayout(false);
    }, ACTIVE_TURN_LAYOUT_SETTLE_DELAY_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activeLatestTurn?.startedAt, activeTurnLayoutKey, activeTurnLayoutLive]);

  useEffect(() => {
    const nextCustomAnswer = activePendingProgress?.customAnswer;
    if (typeof nextCustomAnswer !== "string") {
      lastSyncedPendingInputRef.current = null;
      return;
    }
    const nextRequestId = activePendingUserInput?.requestId ?? null;
    const nextQuestionId = activePendingProgress?.activeQuestion?.id ?? null;
    const questionChanged =
      lastSyncedPendingInputRef.current?.requestId !== nextRequestId ||
      lastSyncedPendingInputRef.current?.questionId !== nextQuestionId;
    const textChangedExternally = promptRef.current !== nextCustomAnswer;

    lastSyncedPendingInputRef.current = {
      requestId: nextRequestId,
      questionId: nextQuestionId,
    };

    if (!questionChanged && !textChangedExternally) {
      return;
    }

    promptRef.current = nextCustomAnswer;
    const nextCursor = collapseExpandedComposerCursor(nextCustomAnswer, nextCustomAnswer.length);
    setComposerCursor(nextCursor);
    setComposerTrigger(
      detectComposerTrigger(
        nextCustomAnswer,
        expandCollapsedComposerCursor(nextCustomAnswer, nextCursor),
      ),
    );
    setComposerHighlightedItemId(null);
  }, [
    activePendingProgress?.customAnswer,
    activePendingUserInput?.requestId,
    activePendingProgress?.activeQuestion?.id,
  ]);
  useEffect(() => {
    attachmentPreviewHandoffByMessageIdRef.current = attachmentPreviewHandoffByMessageId;
  }, [attachmentPreviewHandoffByMessageId]);
  const clearAttachmentPreviewHandoffs = useCallback(() => {
    for (const timeoutId of Object.values(attachmentPreviewHandoffTimeoutByMessageIdRef.current)) {
      window.clearTimeout(timeoutId);
    }
    attachmentPreviewHandoffTimeoutByMessageIdRef.current = {};
    for (const previewUrlByAttachmentId of Object.values(
      attachmentPreviewHandoffByMessageIdRef.current,
    )) {
      for (const previewUrl of Object.values(previewUrlByAttachmentId)) {
        revokeBlobPreviewUrl(previewUrl);
      }
    }
    attachmentPreviewHandoffByMessageIdRef.current = {};
    setAttachmentPreviewHandoffByMessageId({});
  }, []);
  useEffect(() => {
    return () => {
      clearAttachmentPreviewHandoffs();
      for (const message of optimisticUserMessagesRef.current) {
        revokeUserMessagePreviewUrls(message);
      }
    };
  }, [clearAttachmentPreviewHandoffs]);
  const handoffAttachmentPreviews = useCallback(
    (messageId: MessageId, previewUrlByAttachmentId: Record<string, string>) => {
      const previewUrls = Object.values(previewUrlByAttachmentId);
      if (previewUrls.length === 0) return;

      const previousPreviewUrls = Object.values(
        attachmentPreviewHandoffByMessageIdRef.current[messageId] ?? {},
      );
      for (const previewUrl of previousPreviewUrls) {
        if (!previewUrls.includes(previewUrl)) {
          revokeBlobPreviewUrl(previewUrl);
        }
      }
      setAttachmentPreviewHandoffByMessageId((existing) => {
        const next = {
          ...existing,
          [messageId]: previewUrlByAttachmentId,
        };
        attachmentPreviewHandoffByMessageIdRef.current = next;
        return next;
      });

      const existingTimeout = attachmentPreviewHandoffTimeoutByMessageIdRef.current[messageId];
      if (typeof existingTimeout === "number") {
        window.clearTimeout(existingTimeout);
      }
      attachmentPreviewHandoffTimeoutByMessageIdRef.current[messageId] = window.setTimeout(() => {
        const currentPreviewUrls = attachmentPreviewHandoffByMessageIdRef.current[messageId];
        if (currentPreviewUrls) {
          for (const previewUrl of Object.values(currentPreviewUrls)) {
            revokeBlobPreviewUrl(previewUrl);
          }
        }
        setAttachmentPreviewHandoffByMessageId((existing) => {
          if (!(messageId in existing)) return existing;
          const next = { ...existing };
          delete next[messageId];
          attachmentPreviewHandoffByMessageIdRef.current = next;
          return next;
        });
        delete attachmentPreviewHandoffTimeoutByMessageIdRef.current[messageId];
      }, ATTACHMENT_PREVIEW_HANDOFF_TTL_MS);
    },
    [],
  );
  const serverMessages = activeThread?.messages;
  const timelineMessages = useMemo(() => {
    const messages = serverMessages ?? [];
    const serverMessagesWithPreviewHandoff =
      Object.keys(attachmentPreviewHandoffByMessageId).length === 0
        ? messages
        : // Spread only fires for the few messages that actually changed;
          // unchanged ones early-return their original reference.
          // In-place mutation would break React's immutable state contract.
          // oxlint-disable-next-line no-map-spread
          messages.map((message) => {
            if (
              message.role !== "user" ||
              !message.attachments ||
              message.attachments.length === 0
            ) {
              return message;
            }
            const handoffPreviewUrls = attachmentPreviewHandoffByMessageId[message.id];
            if (!handoffPreviewUrls || Object.keys(handoffPreviewUrls).length === 0) {
              return message;
            }

            let changed = false;
            const attachments = message.attachments.map((attachment) => {
              const handoffPreviewUrl = handoffPreviewUrls[attachment.id];
              if (!handoffPreviewUrl || attachment.previewUrl === handoffPreviewUrl) {
                return attachment;
              }
              changed = true;
              return {
                ...attachment,
                previewUrl: handoffPreviewUrl,
              };
            });

            return changed ? { ...message, attachments } : message;
          });

    if (optimisticUserMessages.length === 0) {
      return serverMessagesWithPreviewHandoff;
    }
    const serverIds = new Set(serverMessagesWithPreviewHandoff.map((message) => message.id));
    const pendingMessages = optimisticUserMessages.filter((message) => !serverIds.has(message.id));
    if (pendingMessages.length === 0) {
      return serverMessagesWithPreviewHandoff;
    }
    return [...serverMessagesWithPreviewHandoff, ...pendingMessages];
  }, [serverMessages, attachmentPreviewHandoffByMessageId, optimisticUserMessages]);
  const timelineEntries = useMemo(
    () =>
      deriveTimelineEntries(
        timelineMessages,
        activeThread?.proposedPlans ?? [],
        workLogEntries,
        activeThread?.contextClearedAt ?? null,
      ),
    [activeThread?.contextClearedAt, activeThread?.proposedPlans, timelineMessages, workLogEntries],
  );
  const { turnDiffSummaries, inferredCheckpointTurnCountByTurnId } =
    useTurnDiffSummaries(activeThread);
  const turnDiffSummaryByAssistantMessageId = useMemo(() => {
    const byMessageId = new Map<MessageId, TurnDiffSummary>();
    for (const summary of turnDiffSummaries) {
      if (!summary.assistantMessageId) continue;
      byMessageId.set(summary.assistantMessageId, summary);
    }
    return byMessageId;
  }, [turnDiffSummaries]);
  const revertTurnCountByUserMessageId = useMemo(() => {
    // Single forward pass instead of a user-message x following-entries nested
    // scan: `pendingUserMessageId` stands in for the inner loop's starting
    // point, so each entry is still visited at most once even though every
    // user message conceptually "looks ahead" to its own following run.
    const byUserMessageId = new Map<MessageId, number>();
    let pendingUserMessageId: MessageId | null = null;
    for (const entry of timelineEntries) {
      if (entry.kind !== "message") {
        continue;
      }
      if (entry.message.role === "user") {
        pendingUserMessageId = entry.message.id;
        continue;
      }
      if (pendingUserMessageId === null) {
        continue;
      }
      const summary = turnDiffSummaryByAssistantMessageId.get(entry.message.id);
      if (!summary) {
        continue;
      }
      const turnCount =
        summary.checkpointTurnCount ?? inferredCheckpointTurnCountByTurnId[summary.turnId];
      if (typeof turnCount !== "number") {
        pendingUserMessageId = null;
        continue;
      }
      byUserMessageId.set(pendingUserMessageId, Math.max(0, turnCount - 1));
      pendingUserMessageId = null;
    }

    return byUserMessageId;
  }, [inferredCheckpointTurnCountByTurnId, timelineEntries, turnDiffSummaryByAssistantMessageId]);

  const threadWorkspaceCwd = activeProject
    ? resolveSharedThreadWorkspaceCwd({
        projectCwd: activeProject.cwd,
        envMode: resolvedThreadEnvMode,
        worktreePath: resolvedThreadWorktreePath,
      })
    : null;
  const gitCwd = threadWorkspaceCwd;
  const gitBranchSourceCwd = activeProject
    ? resolveThreadBranchSourceCwd({
        projectCwd: activeProject.cwd,
        worktreePath: resolvedThreadWorktreePath,
      })
    : null;
  const composerTriggerKind = composerTrigger?.kind ?? null;
  const mentionTriggerQuery = composerTrigger?.kind === "mention" ? composerTrigger.query : "";
  const isMentionTrigger = composerTriggerKind === "mention";
  const skillTriggerQuery = composerTrigger?.kind === "skill" ? composerTrigger.query : "";
  const isSkillTrigger = composerTriggerKind === "skill";
  const [debouncedPathQuery, composerPathQueryDebouncer] = useDebouncedValue(
    mentionTriggerQuery,
    { wait: COMPOSER_PATH_QUERY_DEBOUNCE_MS },
    (debouncerState) => ({ isPending: debouncerState.isPending }),
  );
  const effectiveMentionQuery = mentionTriggerQuery.length > 0 ? debouncedPathQuery : "";
  const branchesQuery = useQuery(gitBranchesQueryOptions(gitBranchSourceCwd));
  const serverConfigQuery = useQuery(serverConfigQueryOptions());
  const composerSkillCwd = resolveProviderDiscoveryCwd({
    activeThreadWorktreePath: resolvedThreadWorktreePath,
    activeProjectCwd: activeProject?.cwd ?? null,
    serverCwd: serverConfigQuery.data?.cwd ?? null,
  });
  const providerComposerCapabilitiesQuery = useQuery(
    providerComposerCapabilitiesQueryOptions(selectedProvider),
  );
  const providerCommandsQuery = useQuery(
    providerCommandsQueryOptions({
      provider: selectedProvider,
      cwd: composerSkillCwd,
      threadId,
      query:
        composerTriggerKind === "slash-command" || composerTriggerKind === "slash-model"
          ? (composerTrigger?.query ?? "")
          : "",
      enabled:
        (composerTriggerKind === "slash-command" || composerTriggerKind === "slash-model") &&
        supportsNativeSlashCommandDiscovery(providerComposerCapabilitiesQuery.data) &&
        composerSkillCwd !== null,
    }),
  );
  const providerSkillsQuery = useQuery(
    providerSkillsQueryOptions({
      provider: selectedProvider,
      cwd: composerSkillCwd,
      threadId,
      query: skillTriggerQuery,
      enabled:
        isSkillTrigger &&
        supportsSkillDiscovery(providerComposerCapabilitiesQuery.data) &&
        composerSkillCwd !== null,
    }),
  );
  const providerPluginsQuery = useQuery(
    providerPluginsQueryOptions({
      provider: selectedProvider,
      cwd: composerSkillCwd,
      threadId,
      enabled:
        supportsPluginDiscovery(providerComposerCapabilitiesQuery.data) &&
        composerSkillCwd !== null,
    }),
  );
  const workspaceEntriesQuery = useQuery(
    projectSearchEntriesQueryOptions({
      cwd: gitCwd,
      query: effectiveMentionQuery,
      // The `@` picker reads the same workspace index as the Files panel, so it
      // goes with the same grant: without it the request is refused, and asking
      // would turn an absent picker into a failing one.
      enabled: isMentionTrigger && uiCapabilities.canViewFiles,
      limit: 80,
    }),
  );
  const workspaceEntries = workspaceEntriesQuery.data?.entries ?? EMPTY_PROJECT_ENTRIES;
  const activeRootBranch = useMemo(
    () =>
      resolveComposerSlashRootBranch({
        branches: branchesQuery.data?.branches,
        activeProjectCwd: activeProject?.cwd,
        activeThreadBranch: activeThread?.branch,
      }),
    [activeProject?.cwd, activeThread?.branch, branchesQuery.data?.branches],
  );
  // Keep plugin suggestions referentially stable so prompt-sync effects do not loop on rerender.
  const providerPlugins = useMemo(
    () =>
      providerPluginsQuery.data?.marketplaces.flatMap((marketplace) =>
        marketplace.plugins.map((plugin) => ({
          plugin,
          mention: {
            name: plugin.name,
            path: `plugin://${plugin.name}@${marketplace.name}`,
          } satisfies ProviderMentionReference,
        })),
      ) ?? EMPTY_COMPOSER_PLUGIN_SUGGESTIONS,
    [providerPluginsQuery.data],
  );
  const providerNativeCommands =
    providerCommandsQuery.data?.commands ?? EMPTY_PROVIDER_NATIVE_COMMANDS;
  const providerNativeCommandNames = useMemo(
    () => providerNativeCommands.map((command) => command.name),
    [providerNativeCommands],
  );
  const effectiveComposerTrigger = useMemo(() => {
    if (
      composerTrigger?.kind === "slash-model" &&
      hasProviderNativeSlashCommand(selectedProvider, providerNativeCommandNames, "model")
    ) {
      return {
        ...composerTrigger,
        kind: "slash-command" as const,
        query: "model",
      };
    }
    return composerTrigger;
  }, [composerTrigger, providerNativeCommandNames, selectedProvider]);
  const effectiveComposerTriggerKind = effectiveComposerTrigger?.kind ?? null;
  const supportsTextNativeReviewCommand = useMemo(
    () => providerNativeCommands.some((command) => command.name.toLowerCase() === "review"),
    [providerNativeCommands],
  );
  const providerSkills = providerSkillsQuery.data?.skills ?? EMPTY_PROVIDER_SKILLS;
  const selectedModelCaps = useMemo(
    () => getModelCapabilities(selectedProvider, selectedModel),
    [selectedModel, selectedProvider],
  );
  const supportsFastSlashCommand = selectedModelCaps.supportsFastMode;
  const currentProviderModelOptions = composerModelOptions?.[selectedProvider];
  const fastModeEnabled =
    supportsFastSlashCommand &&
    (currentProviderModelOptions as { fastMode?: boolean } | undefined)?.fastMode === true;
  const composerPromptWithoutActiveSlashTrigger =
    composerTrigger?.kind === "slash-command"
      ? stripComposerTriggerText(prompt, composerTrigger)
      : prompt;
  const canOfferReviewCommand =
    (branchesQuery.data?.isRepo ?? true) &&
    canOfferReviewSlashCommand({
      prompt: composerPromptWithoutActiveSlashTrigger,
      imageCount: composerAttachments.length,
      terminalContextCount: composerQuotedContexts.length,
      selectedSkillCount: selectedComposerSkills.length,
      selectedMentionCount: selectedComposerMentions.length,
    });
  const canOfferForkCommand =
    isServerThread &&
    activeThread !== undefined &&
    canOfferForkSlashCommand({
      prompt: composerPromptWithoutActiveSlashTrigger,
      imageCount: composerAttachments.length,
      terminalContextCount: composerQuotedContexts.length,
      selectedSkillCount: selectedComposerSkills.length,
      selectedMentionCount: selectedComposerMentions.length,
      interactionMode,
    });
  // A thread with a provider session can always be compacted: the request goes
  // through `provider.compactThread`, which recovers a stopped session before
  // compacting it. Only a thread that never opened one has nothing to compact.
  const canOfferCompactCommand =
    (selectedProvider === "codex" || selectedProvider === "claudeAgent") &&
    isServerThread &&
    activeThread?.session != null;
  // Fixed at mount: its `updatedAt` is already past the threshold, so the
  // preview banner stays up without dragging the clock into its own input.
  const resumeCompactionPreviewWindow = useMemo(
    () =>
      import.meta.env.DEV &&
      selectedProvider === "claudeAgent" &&
      isResumeCompactionPreviewEnabled()
        ? previewResumeCompactionWindow(new Date().toISOString())
        : null,
    [selectedProvider],
  );
  const resumeCompactionPreview = resumeCompactionPreviewWindow !== null;
  const resumeCompactionWindow = resumeCompactionPreviewWindow ?? activeContextWindow;
  // One render at the instant the thread becomes old enough, instead of a tick
  // every minute for every open thread.
  const resumeCompactionDeadlineMs = useMemo(() => {
    const updatedAtMs = Date.parse(resumeCompactionWindow?.updatedAt ?? "");
    return Number.isFinite(updatedAtMs)
      ? updatedAtMs + CLAUDE_RESUME_COMPACTION_MINUTES * 60_000
      : null;
  }, [resumeCompactionWindow?.updatedAt]);
  const resumeCompactionNow = useNowAfter(resumeCompactionDeadlineMs);
  const resumeCompactionKey =
    activeThread && resumeCompactionWindow
      ? resumeCompactionPreview
        ? `${activeThread.id}:preview`
        : `${activeThread.id}:${resumeCompactionWindow.updatedAt}`
      : null;
  const compactDisabled =
    !canOfferCompactCommand ||
    isWorking ||
    pendingApprovals.length > 0 ||
    pendingUserInputs.length > 0 ||
    showPlanFollowUpPrompt ||
    composerSendState.hasSendableContent;
  const compactDisabledReason = compactDisabled
    ? composerSendState.hasSendableContent
      ? "Envía o borra el borrador antes de compactar"
      : !canOfferCompactCommand
        ? "Abre un hilo activo del servidor antes de compactar"
        : "Ahora no se puede compactar"
    : null;
  const showResumeCompactionBanner =
    activeThread != null &&
    resumeCompactionWindow != null &&
    resumeCompactionKey !== null &&
    !dismissedResumeCompactionKeys.has(resumeCompactionKey) &&
    !resumeCompactionThreadDismissed &&
    !nativeResumeCompactionDismissed &&
    pendingUserInputs.length === 0 &&
    pendingApprovals.length === 0 &&
    !showPlanFollowUpPrompt &&
    phase !== "running" &&
    shouldOfferResumeCompaction({
      provider: selectedProvider,
      usedTokens: resumeCompactionWindow.usedTokens,
      updatedAt: resumeCompactionWindow.updatedAt,
      now: resumeCompactionNow,
    });
  const dynamicAgents = useMemo(() => {
    const query =
      selectedProvider === "claudeAgent" ? claudeDynamicAgentsQuery : codexDynamicAgentsQuery;
    return (query.data?.agents ?? []).map((a) => ({
      name: a.name,
      displayName: a.displayName,
      ...(a.description ? { description: a.description } : {}),
    }));
  }, [selectedProvider, claudeDynamicAgentsQuery.data, codexDynamicAgentsQuery.data]);
  const normalComposerMenuItems = useComposerCommandMenuItems({
    composerTrigger: effectiveComposerTrigger,
    provider: selectedProvider,
    providerPlugins,
    providerNativeCommands,
    providerSkills,
    workspaceEntries,
    searchableModelOptions,
    supportsFastSlashCommand,
    canOfferCompactCommand,
    canOfferReviewCommand,
    canOfferForkCommand,
    dynamicAgents,
  });
  const composerMenuItems = useMemo(() => {
    if (composerCommandPicker === "fork-target") {
      return [
        {
          id: "fork-target:worktree",
          type: "fork-target" as const,
          target: "worktree" as const,
          label: "Fork Into New Worktree",
          description: "Continue in a new worktree",
        },
        {
          id: "fork-target:local",
          type: "fork-target" as const,
          target: "local" as const,
          label: "Fork Into Local",
          description:
            activeThread?.worktreePath || activeThread?.envMode === "worktree"
              ? "Continue in this local worktree"
              : "Continue in the current local thread",
        },
      ];
    }
    if (composerCommandPicker === "review-target") {
      return [
        {
          id: "review-target:changes",
          type: "review-target" as const,
          target: "changes" as const,
          label: "Review Uncommitted Changes",
          description: "Review local uncommitted changes",
        },
        {
          id: "review-target:base-branch",
          type: "review-target" as const,
          target: "base-branch" as const,
          label: "Review Against Base Branch",
          description: "Review the current branch diff against its base",
        },
      ];
    }

    return normalComposerMenuItems;
  }, [
    activeThread?.envMode,
    activeThread?.worktreePath,
    composerCommandPicker,
    normalComposerMenuItems,
  ]);
  const composerMenuOpen = Boolean(composerTrigger || composerCommandPicker);
  const activeComposerMenuItem = useMemo(
    () =>
      composerMenuItems.find((item) => item.id === composerHighlightedItemId) ??
      composerMenuItems[0] ??
      null,
    [composerHighlightedItemId, composerMenuItems],
  );
  composerMenuOpenRef.current = composerMenuOpen;
  composerMenuItemsRef.current = composerMenuItems;
  activeComposerMenuItemRef.current = activeComposerMenuItem;
  const nonPersistedComposerAttachmentIdSet = useMemo(
    () => new Set(nonPersistedComposerAttachmentIds),
    [nonPersistedComposerAttachmentIds],
  );
  const keybindings = serverConfigQuery.data?.keybindings ?? EMPTY_KEYBINDINGS;
  const availableEditors = serverConfigQuery.data?.availableEditors ?? EMPTY_AVAILABLE_EDITORS;
  const activeProviderStatus = useMemo(
    () => providerStatuses.find((status) => status.provider === selectedProvider) ?? null,
    [selectedProvider, providerStatuses],
  );
  const voiceProviderStatus = useMemo(
    () => providerStatuses.find((status) => status.provider === "codex") ?? null,
    [providerStatuses],
  );
  const refreshProviderStatuses = useCallback(() => {
    const api = readNativeApi();
    if (!api) return;
    void api.server
      .refreshProviders()
      .then((result) => {
        queryClient.setQueryData(serverQueryKeys.config(), (current) =>
          current ? { ...current, providers: result.providers } : current,
        );
      })
      .catch((error) => {
        toastManager.add({
          type: "error",
          title: "Unable to refresh provider status",
          description:
            error instanceof Error ? error.message : "Unknown error refreshing provider status.",
        });
      });
  }, [queryClient, toastManager]);
  const voiceRecordingDurationLabel = useMemo(
    () => formatVoiceRecordingDuration(voiceRecordingDurationMs),
    [voiceRecordingDurationMs],
  );
  const canRenderVoiceNotes = voiceProviderStatus?.authStatus !== "unauthenticated";
  const canStartVoiceNotes =
    voiceProviderStatus?.authStatus !== "unauthenticated" &&
    voiceProviderStatus?.voiceTranscriptionAvailable !== false;
  const showVoiceNotesControl = canRenderVoiceNotes || isVoiceRecording || isVoiceTranscribing;
  const activeProjectCwd = activeProject?.cwd ?? null;
  const activeThreadWorktreePath = activeThread?.worktreePath ?? null;
  const hasNativeUserMessages = useMemo(
    () =>
      activeThread?.messages.some(
        (message) => message.role === "user" && message.source === "native",
      ) ?? false,
    [activeThread?.messages],
  );
  const threadTerminalRuntimeEnv = useMemo(() => {
    if (!activeProjectCwd) return {};
    return projectScriptRuntimeEnv({
      project: {
        cwd: activeProjectCwd,
      },
      worktreePath: activeThreadWorktreePath,
    });
  }, [activeProjectCwd, activeThreadWorktreePath]);
  // Default true while loading to avoid toolbar flicker.
  const isGitRepo = branchesQuery.data?.isRepo ?? true;
  const terminalToggleShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "terminal.toggle"),
    [keybindings],
  );
  const splitTerminalShortcutLabel = useMemo(
    () =>
      shortcutLabelForCommand(keybindings, "terminal.splitRight") ??
      shortcutLabelForCommand(keybindings, "terminal.split"),
    [keybindings],
  );
  const splitTerminalDownShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "terminal.splitDown"),
    [keybindings],
  );
  const newTerminalShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "terminal.new"),
    [keybindings],
  );
  const closeTerminalShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "terminal.close"),
    [keybindings],
  );
  const closeWorkspaceShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "terminal.workspace.closeActive"),
    [keybindings],
  );
  const diffPanelShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "diff.toggle"),
    [keybindings],
  );
  const browserPanelShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "browser.toggle"),
    [keybindings],
  );
  const chatSplitShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "chat.split"),
    [keybindings],
  );
  // Composer picker shortcuts are hard-coded in the keydown handler below
  // (Mod+Shift+M opens the model picker, Mod+Shift+E opens the reasoning picker,
  // Ctrl+S saves the draft to the composer queue).
  // Build their display labels directly so the tooltips match the handler exactly.
  const modelPickerShortcutLabel = useMemo(
    () =>
      formatShortcutLabel({
        key: "m",
        metaKey: false,
        ctrlKey: false,
        shiftKey: true,
        altKey: false,
        modKey: true,
      }),
    [],
  );
  const traitsPickerShortcutLabel = useMemo(
    () =>
      formatShortcutLabel({
        key: "e",
        metaKey: false,
        ctrlKey: false,
        shiftKey: true,
        altKey: false,
        modKey: true,
      }),
    [],
  );
  const onToggleDiff = useCallback(() => {
    if (diffEnvironmentPending && !diffOpen) {
      return;
    }
    if (onToggleDiffPanel) {
      onToggleDiffPanel();
      return;
    }
    void navigate({
      to: "/$threadId",
      params: { threadId },
      replace: true,
      search: (previous) => {
        const rest = stripDiffSearchParams(previous);
        return diffOpen
          ? { ...rest, panel: undefined, diff: undefined }
          : { ...rest, panel: "diff", diff: "1" };
      },
    });
  }, [diffEnvironmentPending, diffOpen, navigate, onToggleDiffPanel, threadId]);
  const onToggleBrowser = useCallback(() => {
    if (onToggleBrowserPanel) {
      onToggleBrowserPanel();
      return;
    }
    void navigate({
      to: "/$threadId",
      params: { threadId },
      replace: true,
      search: (previous) => {
        const rest = stripDiffSearchParams(previous);
        return browserOpen ? { ...rest, panel: undefined } : { ...rest, panel: "browser" };
      },
    });
  }, [browserOpen, navigate, onToggleBrowserPanel, threadId]);
  const onOpenSideChat = useCallback(() => {
    if (onOpenSideChatPanel) {
      onOpenSideChatPanel();
      return;
    }
    void navigate({
      to: "/$threadId",
      params: { threadId },
      replace: true,
      search: (previous) => ({ ...stripDiffSearchParams(previous), panel: "sidechat" }),
    });
  }, [navigate, onOpenSideChatPanel, threadId]);
  const onToggleFiles = useCallback(() => {
    if (onToggleFilesPanel) {
      onToggleFilesPanel();
      return;
    }
    void navigate({
      to: "/$threadId",
      params: { threadId },
      replace: true,
      search: (previous) => {
        const rest = stripDiffSearchParams(previous);
        return filesOpen ? { ...rest, panel: undefined } : { ...rest, panel: "files" };
      },
    });
  }, [filesOpen, navigate, onToggleFilesPanel, threadId]);

  const envLocked = Boolean(
    activeThread &&
    // A guest thread's workspace belongs to its environment and the server
    // refuses to move it (see `rejectGuestWorkspaceChange`). Locking the
    // control here is what keeps that refusal from being the way people find
    // out — the code moves with the environment's git tools, not per thread.
    (activeThread.environmentId ||
      activeThread.messages.length > 0 ||
      (activeThread.session !== null && activeThread.session.status !== "closed")),
  );
  const activeTerminalGroup =
    terminalState.terminalGroups.find(
      (group) => group.id === terminalState.activeTerminalGroupId,
    ) ??
    terminalState.terminalGroups.find((group) =>
      collectTerminalIdsFromLayout(group.layout).includes(terminalState.activeTerminalId),
    ) ??
    null;
  const hasReachedSplitLimit =
    (activeTerminalGroup ? collectTerminalIdsFromLayout(activeTerminalGroup.layout).length : 0) >=
    MAX_TERMINALS_PER_GROUP;
  const terminalWorkspaceOpen = shouldRenderTerminalWorkspace({
    activeProjectExists: activeProject !== undefined,
    presentationMode: terminalState.presentationMode,
    terminalOpen: terminalState.terminalOpen,
  });

  // Gate for (re-)entering a worktree-with-agent thread. The key combines the
  // thread identity with `terminalOpen` so the effect also refires when the
  // user closes the active tab (PTY dies, terminalOpen=false) and re-opens the
  // thread from the sidebar (openTerminalThreadPage flips terminalOpen back to
  // true, even though the route stays the same). The server RPC is idempotent:
  // if the PTY is still alive it returns `already-running` and skips relaunch.
  const activeAgentThreadKey =
    activeThread?.associatedAgentId &&
    activeThread.associatedWorktreePath &&
    terminalState.terminalOpen
      ? activeThread.id
      : null;
  const [agentReadyThreadId, setAgentReadyThreadId] = useState<string | null>(null);
  useEffect(() => {
    setAgentReadyThreadId(null);
    if (activeAgentThreadKey === null) {
      return;
    }
    const api = readNativeApi();
    if (!api) return;
    let cancelled = false;
    void api.workspace
      .ensureAgentThreadTerminal({ threadId: activeAgentThreadKey })
      .catch(() => {
        // Swallow — worst case, auto-attach will create a default shell.
      })
      .finally(() => {
        if (!cancelled) {
          setAgentReadyThreadId(activeAgentThreadKey);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeAgentThreadKey]);
  const agentTerminalReady =
    activeAgentThreadKey === null || agentReadyThreadId === activeAgentThreadKey;
  const terminalWorkspaceTerminalTabActive =
    terminalWorkspaceOpen &&
    (terminalState.workspaceLayout === "terminal-only" ||
      terminalState.workspaceActiveTab === "terminal");
  const terminalWorkspaceChatTabActive =
    terminalWorkspaceOpen &&
    terminalState.workspaceLayout === "both" &&
    terminalState.workspaceActiveTab === "chat";
  const setThreadError = useCallback(
    (targetThreadId: ThreadId | null, error: string | null) => {
      if (!targetThreadId) return;
      if (getThreadFromState(useStore.getState(), targetThreadId)) {
        setStoreThreadError(targetThreadId, error);
        return;
      }
      setLocalDraftErrorsByThreadId((existing) => {
        if ((existing[targetThreadId] ?? null) === error) {
          return existing;
        }
        return {
          ...existing,
          [targetThreadId]: error,
        };
      });
    },
    [setStoreThreadError],
  );

  const focusComposer = useCallback(() => {
    composerEditorRef.current?.focusAtEnd();
  }, []);
  const scheduleComposerFocus = useCallback(() => {
    window.requestAnimationFrame(() => {
      focusComposer();
    });
  }, [focusComposer]);
  // Keep the two composer picker menus mutually exclusive so shortcuts always open one surface.
  const handleModelPickerOpenChange = useCallback((open: boolean) => {
    setIsModelPickerOpen(open);
    if (open) {
      setIsTraitsPickerOpen(false);
    }
  }, []);
  const handleTraitsPickerOpenChange = useCallback((open: boolean) => {
    setIsTraitsPickerOpen(open);
    if (open) {
      setIsModelPickerOpen(false);
    }
  }, []);
  const appendVoiceTranscriptToComposer = useCallback(
    (transcript: string) => {
      const nextPrompt = appendVoiceTranscriptToPrompt(promptRef.current, transcript);
      if (!nextPrompt) {
        return;
      }

      promptRef.current = nextPrompt;
      setPrompt(nextPrompt);
      setComposerCursor(collapseExpandedComposerCursor(nextPrompt, nextPrompt.length));
      setComposerTrigger(detectComposerTrigger(nextPrompt, nextPrompt.length));
      scheduleComposerFocus();
    },
    [scheduleComposerFocus, setPrompt],
  );
  const addQuotedContextToDraft = useCallback(
    (selection: QuotedContextSelection) => {
      if (!activeThread) {
        return;
      }
      const snapshot = composerEditorRef.current?.readSnapshot() ?? {
        value: promptRef.current,
        cursor: composerCursor,
        expandedCursor: expandCollapsedComposerCursor(promptRef.current, composerCursor),
        terminalContextIds: composerQuotedContexts.map((context) => context.id),
      };
      const insertion = insertInlineQuotedContextPlaceholder(
        snapshot.value,
        snapshot.expandedCursor,
      );
      const nextCollapsedCursor = collapseExpandedComposerCursor(
        insertion.prompt,
        insertion.cursor,
      );
      const inserted = insertComposerDraftTerminalContext(
        activeThread.id,
        insertion.prompt,
        {
          id: randomUUID(),
          threadId: activeThread.id,
          createdAt: new Date().toISOString(),
          ...selection,
        },
        insertion.contextIndex,
      );
      if (!inserted) {
        return;
      }
      promptRef.current = insertion.prompt;
      setComposerCursor(nextCollapsedCursor);
      setComposerTrigger(detectComposerTrigger(insertion.prompt, insertion.cursor));
      window.requestAnimationFrame(() => {
        composerEditorRef.current?.focusAt(nextCollapsedCursor);
      });
    },
    [activeThread, composerCursor, composerQuotedContexts, insertComposerDraftTerminalContext],
  );
  const setTerminalOpen = useCallback(
    (open: boolean) => {
      if (!activeThreadId) return;
      storeSetTerminalOpen(activeThreadId, open);
    },
    [activeThreadId, storeSetTerminalOpen],
  );
  const setTerminalPresentationMode = useCallback(
    (mode: "drawer" | "workspace") => {
      if (!activeThreadId) return;
      storeSetTerminalPresentationMode(activeThreadId, mode);
    },
    [activeThreadId, storeSetTerminalPresentationMode],
  );
  const setTerminalWorkspaceLayout = useCallback(
    (layout: "both" | "terminal-only") => {
      if (!activeThreadId) return;
      storeSetTerminalWorkspaceLayout(activeThreadId, layout);
    },
    [activeThreadId, storeSetTerminalWorkspaceLayout],
  );
  const setTerminalWorkspaceTab = useCallback(
    (tab: "terminal" | "chat") => {
      if (!activeThreadId) return;
      storeSetTerminalWorkspaceTab(activeThreadId, tab);
    },
    [activeThreadId, storeSetTerminalWorkspaceTab],
  );
  const setTerminalHeight = useCallback(
    (height: number) => {
      if (!activeThreadId) return;
      storeSetTerminalHeight(activeThreadId, height);
    },
    [activeThreadId, storeSetTerminalHeight],
  );
  const toggleTerminalVisibility = useCallback(() => {
    if (!activeThreadId) return;
    if (!terminalState.terminalOpen) {
      setTerminalPresentationMode("drawer");
    }
    setTerminalOpen(!terminalState.terminalOpen);
  }, [activeThreadId, setTerminalOpen, setTerminalPresentationMode, terminalState.terminalOpen]);
  const expandTerminalWorkspace = useCallback(() => {
    if (!activeThreadId) return;
    setTerminalPresentationMode("workspace");
    setTerminalWorkspaceLayout("both");
    setTerminalWorkspaceTab("terminal");
  }, [
    activeThreadId,
    setTerminalPresentationMode,
    setTerminalWorkspaceLayout,
    setTerminalWorkspaceTab,
  ]);
  const collapseTerminalWorkspace = useCallback(() => {
    if (!activeThreadId) return;
    setTerminalPresentationMode("drawer");
  }, [activeThreadId, setTerminalPresentationMode]);
  const splitTerminalRight = useCallback(() => {
    if (!activeThreadId || hasReachedSplitLimit) return;
    const terminalId = `terminal-${randomUUID()}`;
    storeSplitTerminalRight(activeThreadId, terminalId);
    setTerminalFocusRequestId((value) => value + 1);
  }, [activeThreadId, hasReachedSplitLimit, storeSplitTerminalRight]);
  const splitTerminalLeft = useCallback(() => {
    if (!activeThreadId || hasReachedSplitLimit) return;
    const terminalId = `terminal-${randomUUID()}`;
    storeSplitTerminalLeft(activeThreadId, terminalId);
    setTerminalFocusRequestId((value) => value + 1);
  }, [activeThreadId, hasReachedSplitLimit, storeSplitTerminalLeft]);
  const splitTerminalDown = useCallback(() => {
    if (!activeThreadId || hasReachedSplitLimit) return;
    const terminalId = `terminal-${randomUUID()}`;
    storeSplitTerminalDown(activeThreadId, terminalId);
    setTerminalFocusRequestId((value) => value + 1);
  }, [activeThreadId, hasReachedSplitLimit, storeSplitTerminalDown]);
  const splitTerminalUp = useCallback(() => {
    if (!activeThreadId || hasReachedSplitLimit) return;
    const terminalId = `terminal-${randomUUID()}`;
    storeSplitTerminalUp(activeThreadId, terminalId);
    setTerminalFocusRequestId((value) => value + 1);
  }, [activeThreadId, hasReachedSplitLimit, storeSplitTerminalUp]);
  const createNewTerminal = useCallback(() => {
    if (!activeThreadId) return;
    const terminalId = `terminal-${randomUUID()}`;
    storeNewTerminal(activeThreadId, terminalId);
    setTerminalFocusRequestId((value) => value + 1);
  }, [activeThreadId, storeNewTerminal]);
  const createNewTerminalTab = useCallback(
    (targetTerminalId: string) => {
      if (!activeThreadId) return;
      const terminalId = `terminal-${randomUUID()}`;
      storeNewTerminalTab(activeThreadId, targetTerminalId, terminalId);
      setTerminalFocusRequestId((value) => value + 1);
    },
    [activeThreadId, storeNewTerminalTab],
  );
  const createTerminalFromShortcut = useCallback(() => {
    const action = resolveTerminalNewAction({
      terminalOpen: terminalState.terminalOpen,
      activeTerminalId: terminalState.activeTerminalId,
      activeTerminalGroupId: terminalState.activeTerminalGroupId,
      terminalGroups: terminalState.terminalGroups,
    });

    if (action.kind === "new-group") {
      if (!terminalState.terminalOpen) {
        setTerminalOpen(true);
      }
      createNewTerminal();
      return;
    }

    createNewTerminalTab(action.targetTerminalId);
  }, [
    createNewTerminal,
    createNewTerminalTab,
    setTerminalOpen,
    terminalState.activeTerminalGroupId,
    terminalState.activeTerminalId,
    terminalState.terminalGroups,
    terminalState.terminalOpen,
  ]);
  const moveTerminalToNewGroup = useCallback(
    (terminalId: string) => {
      if (!activeThreadId) return;
      storeNewTerminal(activeThreadId, terminalId);
      setTerminalFocusRequestId((value) => value + 1);
    },
    [activeThreadId, storeNewTerminal],
  );
  const openNewFullWidthTerminal = useCallback(() => {
    if (!activeThreadId || !activeProject) return;
    const terminalId = `terminal-${randomUUID()}`;
    storeOpenNewFullWidthTerminal(activeThreadId, terminalId);
    setTerminalFocusRequestId((value) => value + 1);
  }, [activeProject, activeThreadId, storeOpenNewFullWidthTerminal]);
  // Desktop accelerators like Cmd+T can be claimed by Electron before the page sees keydown.
  useEffect(() => {
    const onMenuAction = window.desktopBridge?.onMenuAction;
    if (typeof onMenuAction !== "function" || !isFocusedPane) {
      return;
    }

    const unsubscribe = onMenuAction((action) => {
      if (action !== "new-terminal-tab") return;
      createTerminalFromShortcut();
    });

    return () => {
      unsubscribe?.();
    };
  }, [createTerminalFromShortcut, isFocusedPane]);
  const activateTerminal = useCallback(
    (terminalId: string) => {
      if (!activeThreadId) return;
      storeSetActiveTerminal(activeThreadId, terminalId);
      setTerminalFocusRequestId((value) => value + 1);
    },
    [activeThreadId, storeSetActiveTerminal],
  );
  const closeTerminal = useCallback(
    async (terminalId: string) => {
      const api = readNativeApi();
      if (!activeThreadId || !api) return;
      const isFinalTerminal = terminalState.terminalIds.length <= 1;
      const shouldDeletePlaceholderTerminalThread = shouldAutoDeleteTerminalThreadOnLastClose({
        isLastTerminal: isFinalTerminal,
        isServerThread,
        terminalEntryPoint: terminalState.entryPoint,
        thread: activeThread,
      });
      const confirmed = await confirmTerminalTabClose({
        api,
        enabled: settings.confirmTerminalTabClose,
        terminalTitle: resolveTerminalCloseTitle({
          terminalId,
          terminalLabelsById: terminalState.terminalLabelsById,
          terminalTitleOverridesById: terminalState.terminalTitleOverridesById,
        }),
        willDeleteThread: shouldDeletePlaceholderTerminalThread,
      });
      if (!confirmed) {
        return;
      }
      const fallbackExitWrite = () =>
        api.terminal
          .write({ threadId: activeThreadId, terminalId, data: "exit\n" })
          .catch(() => undefined);
      if ("close" in api.terminal && typeof api.terminal.close === "function") {
        void (async () => {
          if (isFinalTerminal) {
            await api.terminal
              .clear({ threadId: activeThreadId, terminalId })
              .catch(() => undefined);
          }
          await api.terminal.close({
            threadId: activeThreadId,
            terminalId,
            deleteHistory: true,
          });
        })().catch(() => fallbackExitWrite());
      } else {
        void fallbackExitWrite();
      }
      storeCloseTerminal(activeThreadId, terminalId);
      setTerminalFocusRequestId((value) => value + 1);
      if (!shouldDeletePlaceholderTerminalThread) {
        return;
      }
      void (async () => {
        try {
          await api.orchestration.dispatchCommand({
            type: "thread.delete",
            commandId: newCommandId(),
            threadId: activeThreadId,
          });
          const snapshot = await api.orchestration.getSnapshot();
          syncServerReadModel(snapshot);
          useComposerDraftStore.getState().clearDraftThread(activeThreadId);
          storeClearTerminalState(activeThreadId);
          removeThreadFromSplitViews(activeThreadId);
          if (activeSplitView) {
            const nextSplitView = useSplitViewStore.getState().splitViewsById[activeSplitView.id];
            const nextThreadId = nextSplitView
              ? resolveSplitViewFocusedThreadId(nextSplitView)
              : null;
            if (nextSplitView && nextThreadId) {
              await navigate({
                to: "/$threadId",
                params: { threadId: nextThreadId },
                replace: true,
                search: () => ({ splitViewId: nextSplitView.id }),
              });
              return;
            }
          }
          await navigate({ to: "/", replace: true });
        } catch (error) {
          console.error("Failed to delete empty terminal thread after closing its last terminal", {
            threadId: activeThreadId,
            error,
          });
        }
      })();
    },
    [
      activeThread,
      activeThreadId,
      activeSplitView,
      isServerThread,
      navigate,
      removeThreadFromSplitViews,
      storeClearTerminalState,
      storeCloseTerminal,
      syncServerReadModel,
      settings.confirmTerminalTabClose,
      terminalState.entryPoint,
      terminalState.terminalIds.length,
      terminalState.terminalLabelsById,
      terminalState.terminalTitleOverridesById,
    ],
  );
  const closeActiveWorkspaceView = useCallback(() => {
    if (!activeThreadId || !terminalWorkspaceOpen) {
      return;
    }
    if (terminalState.workspaceLayout === "both" && terminalState.workspaceActiveTab === "chat") {
      storeCloseWorkspaceChat(activeThreadId);
      return;
    }
    closeTerminal(terminalState.activeTerminalId);
  }, [
    activeThreadId,
    closeTerminal,
    storeCloseWorkspaceChat,
    terminalState.activeTerminalId,
    terminalState.workspaceActiveTab,
    terminalState.workspaceLayout,
    terminalWorkspaceOpen,
  ]);
  // Hide terminal-creation chrome when the user lacks p.terminal on this
  // project. Existing terminals stay visible (only Close remains) — that
  // matches read-only access via p.thread.use.
  const canCreateTerminalInProject = useAuthStore((state) => {
    const projectId = activeProject?.id;
    if (!projectId) return false;
    return state.projectAssignments.get(projectId)?.has("p.terminal") ?? false;
  });
  const terminalDrawerProps = useMemo(
    () => ({
      threadId,
      cwd: gitCwd ?? activeProject?.cwd ?? "",
      canCreateTerminal: canCreateTerminalInProject,
      runtimeEnv: threadTerminalRuntimeEnv,
      height: terminalState.terminalHeight,
      terminalIds: terminalState.terminalIds,
      terminalLabelsById: terminalState.terminalLabelsById,
      terminalTitleOverridesById: terminalState.terminalTitleOverridesById,
      terminalCliKindsById: terminalState.terminalCliKindsById,
      terminalAttentionStatesById: terminalState.terminalAttentionStatesById ?? {},
      runningTerminalIds: terminalState.runningTerminalIds,
      activeTerminalId: terminalState.activeTerminalId,
      terminalGroups: terminalState.terminalGroups,
      activeTerminalGroupId: terminalState.activeTerminalGroupId,
      focusRequestId: terminalFocusRequestId,
      onSplitTerminal: splitTerminalRight,
      onSplitTerminalDown: splitTerminalDown,
      onNewTerminal: createNewTerminal,
      onNewTerminalTab: createNewTerminalTab,
      onMoveTerminalToGroup: moveTerminalToNewGroup,
      splitShortcutLabel: splitTerminalShortcutLabel ?? undefined,
      splitDownShortcutLabel: splitTerminalDownShortcutLabel ?? undefined,
      newShortcutLabel: newTerminalShortcutLabel ?? undefined,
      closeShortcutLabel: closeTerminalShortcutLabel ?? undefined,
      workspaceCloseShortcutLabel: closeWorkspaceShortcutLabel ?? undefined,
      onActiveTerminalChange: activateTerminal,
      onCloseTerminal: closeTerminal,
      onCloseTerminalGroup: (groupId: string) => {
        if (!activeThreadId) return;
        storeCloseTerminalGroup(activeThreadId, groupId);
      },
      onHeightChange: setTerminalHeight,
      onResizeTerminalSplit: (groupId: string, splitId: string, weights: number[]) => {
        if (!activeThreadId) return;
        storeResizeTerminalSplit(activeThreadId, groupId, splitId, weights);
      },
      onTerminalMetadataChange: (
        terminalId: string,
        metadata: { cliKind: "codex" | "claude" | null; label: string },
      ) => {
        if (!activeThreadId) return;
        storeSetTerminalMetadata(activeThreadId, terminalId, metadata);
      },
      onTerminalActivityChange: (
        terminalId: string,
        activity: {
          hasRunningSubprocess: boolean;
          agentState: "running" | "attention" | "review" | null;
        },
      ) => {
        if (!activeThreadId) return;
        storeSetTerminalActivity(activeThreadId, terminalId, activity);
      },
      onAddTerminalContext: addQuotedContextToDraft,
    }),
    [
      activeProject?.cwd,
      activateTerminal,
      addQuotedContextToDraft,
      canCreateTerminalInProject,
      closeTerminal,
      closeTerminalShortcutLabel,
      closeWorkspaceShortcutLabel,
      createNewTerminal,
      createNewTerminalTab,
      moveTerminalToNewGroup,
      gitCwd,
      activeThreadId,
      newTerminalShortcutLabel,
      setTerminalHeight,
      splitTerminalRight,
      splitTerminalDown,
      splitTerminalShortcutLabel,
      splitTerminalDownShortcutLabel,
      storeCloseTerminalGroup,
      storeResizeTerminalSplit,
      storeSetTerminalActivity,
      storeSetTerminalMetadata,
      terminalFocusRequestId,
      terminalState.activeTerminalGroupId,
      terminalState.activeTerminalId,
      terminalState.terminalAttentionStatesById,
      terminalState.terminalCliKindsById,
      terminalState.terminalGroups,
      terminalState.terminalHeight,
      terminalState.terminalIds,
      terminalState.terminalLabelsById,
      terminalState.terminalTitleOverridesById,
      terminalState.runningTerminalIds,
      threadId,
      threadTerminalRuntimeEnv,
    ],
  );
  /**
   * The header's primary action button follows whatever the user ran last, so
   * every kind of action has to report itself here — not just terminal ones.
   */
  const rememberLastInvokedScript = useCallback(
    (script: ProjectScript) => {
      if (!activeProject) return;
      setLastInvokedScriptByProjectId((current) => {
        if (current[activeProject.id] === script.id) return current;
        return { ...current, [activeProject.id]: script.id };
      });
    },
    [activeProject, setLastInvokedScriptByProjectId],
  );
  const runProjectScript = useCallback(
    async (
      script: ProjectScript,
      options?: {
        cwd?: string;
        env?: Record<string, string>;
        worktreePath?: string | null;
        preferNewTerminal?: boolean;
        rememberAsLastInvoked?: boolean;
      },
    ) => {
      const api = readNativeApi();
      if (!api || !activeThreadId || !activeProject || !activeThread) return;
      if (options?.rememberAsLastInvoked !== false) {
        rememberLastInvokedScript(script);
      }
      const targetCwd = options?.cwd ?? gitCwd ?? activeProject.cwd;
      const baseTerminalId =
        terminalState.activeTerminalId ||
        terminalState.terminalIds[0] ||
        DEFAULT_THREAD_TERMINAL_ID;
      const isBaseTerminalBusy = terminalState.runningTerminalIds.includes(baseTerminalId);
      const wantsNewTerminal = Boolean(options?.preferNewTerminal) || isBaseTerminalBusy;
      const shouldCreateNewTerminal = wantsNewTerminal;
      const targetTerminalId = shouldCreateNewTerminal
        ? `terminal-${randomUUID()}`
        : baseTerminalId;

      setTerminalOpen(true);
      if (shouldCreateNewTerminal) {
        storeNewTerminal(activeThreadId, targetTerminalId);
      } else {
        storeSetActiveTerminal(activeThreadId, targetTerminalId);
      }
      setTerminalFocusRequestId((value) => value + 1);

      const runtimeEnv = projectScriptRuntimeEnv({
        project: {
          cwd: activeProject.cwd,
        },
        worktreePath: options?.worktreePath ?? activeThread.worktreePath ?? null,
        ...(options?.env ? { extraEnv: options.env } : {}),
      });
      const openTerminalInput: Parameters<typeof api.terminal.open>[0] = shouldCreateNewTerminal
        ? {
            threadId: activeThreadId,
            terminalId: targetTerminalId,
            cwd: targetCwd,
            env: runtimeEnv,
            cols: SCRIPT_TERMINAL_COLS,
            rows: SCRIPT_TERMINAL_ROWS,
          }
        : {
            threadId: activeThreadId,
            terminalId: targetTerminalId,
            cwd: targetCwd,
            env: runtimeEnv,
          };

      try {
        const terminalCommandIdentity = deriveTerminalCommandIdentity(script.command);
        await api.terminal.open(openTerminalInput);
        if (terminalCommandIdentity) {
          storeSetTerminalMetadata(activeThreadId, targetTerminalId, {
            cliKind: terminalCommandIdentity.cliKind,
            label: terminalCommandIdentity.title,
          });
        }
        await api.terminal.write({
          threadId: activeThreadId,
          terminalId: targetTerminalId,
          data: `${script.command}\r`,
        });
      } catch (error) {
        setThreadError(
          activeThreadId,
          error instanceof Error ? error.message : `Failed to run script "${script.name}".`,
        );
      }
    },
    [
      activeProject,
      activeThread,
      activeThreadId,
      gitCwd,
      setTerminalOpen,
      setThreadError,
      storeNewTerminal,
      storeSetActiveTerminal,
      storeSetTerminalMetadata,
      rememberLastInvokedScript,
      terminalState.activeTerminalId,
      terminalState.runningTerminalIds,
      terminalState.terminalIds,
    ],
  );
  /**
   * Hand a background action to the server, which runs it in the thread's
   * worktree and reports progress on the `workspace.projectScriptStatus`
   * channel — the toast bridge at the app root turns those into the toast and
   * the output modal, so there is nothing to render here.
   *
   * Only identifiers travel: the server resolves the command from the project's
   * persisted actions, which is what keeps this from being a way around not
   * having a terminal.
   *
   * Thread and project come in explicitly because the automatic setup paths
   * call this for a thread the server has only just learned about, where the
   * `isServerThread` of the current render is still reading the old value.
   */
  const runProjectScriptInBackgroundFor = useCallback(
    async (input: { script: ProjectScript; threadId: string; projectId: ProjectId }) => {
      const api = readNativeApi();
      if (!api) return;
      try {
        await api.workspace.runProjectScript({
          threadId: ThreadId.makeUnsafe(input.threadId),
          projectId: input.projectId,
          scriptId: input.script.id,
        });
      } catch (error) {
        toastManager.add({
          type: "error",
          title: `Could not run "${input.script.name}"`,
          description: error instanceof Error ? error.message : "An unexpected error occurred.",
        });
      }
    },
    [],
  );
  /**
   * Run the project's setup action right after a worktree was created for the
   * active thread, whichever way the worktree came to be.
   *
   * Honouring `runMode` here is the entire point: an action is marked
   * `background` precisely because its owner does not want (or cannot have) a
   * terminal on screen, and every automatic path used to open one anyway.
   */
  const runSetupProjectScript = useCallback(
    async (script: ProjectScript, options: { worktreePath?: string | null | undefined }) => {
      if (!activeProject || !activeThreadId) return;
      if (script.runMode === "background") {
        await runProjectScriptInBackgroundFor({
          script,
          threadId: activeThreadId,
          projectId: activeProject.id,
        });
        return;
      }
      const worktreePath = options.worktreePath ?? null;
      const terminalOptions: Parameters<typeof runProjectScript>[1] = {
        worktreePath,
        rememberAsLastInvoked: false,
      };
      if (worktreePath) {
        terminalOptions.cwd = worktreePath;
      }
      await runProjectScript(script, terminalOptions);
    },
    [activeProject, activeThreadId, runProjectScript, runProjectScriptInBackgroundFor],
  );
  /**
   * Run the setup action the "New thread" dialog queued for this thread.
   *
   * The dialog creates the worktree itself, so the first-message path below
   * never sees `baseBranchForWorktree` and would not run it. It has to happen
   * here because a `terminal` action needs this component's PTY plumbing.
   * `takePendingSetupScript` clears the note, so a remount cannot re-run it.
   */
  useEffect(() => {
    if (!activeThreadId || !activeProject || !isServerThread) return;
    const worktreePath = activeThread?.worktreePath ?? null;
    if (!worktreePath) return;
    const pendingScriptId = usePendingSetupScriptStore
      .getState()
      .takePendingSetupScript(activeThreadId);
    if (!pendingScriptId) return;
    const script = activeProject.scripts.find((entry) => entry.id === pendingScriptId);
    if (!script) return;
    void runSetupProjectScript(script, { worktreePath });
  }, [
    activeThreadId,
    activeProject,
    activeThread?.worktreePath,
    isServerThread,
    runSetupProjectScript,
  ]);

  /**
   * Run a provider's re-authentication command in a thread terminal.
   *
   * The CLI login flows are interactive (they print a URL plus a code), which
   * is exactly what a PTY rendered in the browser can carry — so this works
   * identically against a remote server, with no shell access to the host.
   */
  const reauthenticateProvider = useCallback(
    async (command: string) => {
      const api = readNativeApi();
      if (!api || !activeThreadId || !activeProject) return;

      // Terminals belong to a thread that exists server-side. A draft thread
      // has no such record, and opening one fails with a "not visible" error
      // that says nothing to the operator — so explain it instead.
      if (!isServerThread) {
        toastManager.add({
          type: "info",
          title: "Open a thread first",
          description:
            "Signing in runs in a thread terminal. Send a message in this thread, or open an existing one, and try again.",
        });
        return;
      }

      setIsReauthenticatingProvider(true);
      setReauthPollDeadline(Date.now() + REAUTH_POLL_WINDOW_MS);
      const targetTerminalId = `terminal-${randomUUID()}`;
      const targetCwd = gitCwd ?? activeProject.cwd;

      setTerminalOpen(true);
      storeNewTerminal(activeThreadId, targetTerminalId);
      setTerminalFocusRequestId((value) => value + 1);

      try {
        await api.terminal.open({
          threadId: activeThreadId,
          terminalId: targetTerminalId,
          cwd: targetCwd,
        });
        await api.terminal.write({
          threadId: activeThreadId,
          terminalId: targetTerminalId,
          data: `${command}\r`,
        });
      } catch (error) {
        setThreadError(
          activeThreadId,
          error instanceof Error ? error.message : `Failed to run "${command}".`,
        );
      } finally {
        setIsReauthenticatingProvider(false);
      }
    },
    [
      activeProject,
      activeThreadId,
      gitCwd,
      isServerThread,
      setTerminalOpen,
      setThreadError,
      storeNewTerminal,
      setTerminalFocusRequestId,
      toastManager,
    ],
  );

  // Poll provider health while a CLI login is in flight so the banner clears
  // itself once the operator finishes in the terminal. Stops as soon as the
  // provider reports healthy, or when the window elapses.
  useEffect(() => {
    if (reauthPollDeadline === null) return;
    if (activeProviderStatus?.authStatus !== "unauthenticated") {
      setReauthPollDeadline(null);
      return;
    }
    if (Date.now() >= reauthPollDeadline) {
      setReauthPollDeadline(null);
      return;
    }
    const timer = setTimeout(() => {
      refreshProviderStatuses();
      // Bumping the tick re-runs this effect, which re-arms the next poll.
      // Re-setting the deadline would not: React bails out on an equal value.
      setReauthPollTick((value) => value + 1);
    }, REAUTH_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [
    activeProviderStatus?.authStatus,
    reauthPollDeadline,
    reauthPollTick,
    refreshProviderStatuses,
  ]);

  /** Settings hosts the thread-independent sign-in flow for every provider. */
  const openProviderSettings = useCallback(() => {
    void navigate({ to: "/settings", search: { section: "models" } });
  }, [navigate]);

  /**
   * `/clear` empties the conversation view in place. It does not create a
   * thread and does not navigate: the worktree, the branch and the working
   * files stay exactly where they are, and only the provider's memory and the
   * rendered history are dropped.
   *
   * Confirmation is mandatory rather than a preference, because the view can
   * never be brought back — the rows survive in the event log, but the UI
   * offers no way to them, by design.
   */
  const clearActiveThreadContext = useCallback(async () => {
    const api = readNativeApi();
    const threadId = activeThread?.id ?? null;
    if (threadId) {
      setThreadError(threadId, null);
    }
    await requestClearConversation({
      threadId,
      isServerThread: isServerThread && api !== null,
      confirm: (message) => api?.dialogs.confirm(message) ?? Promise.resolve(false),
      dispatchCommand: (command) =>
        api?.orchestration.dispatchCommand(command) ?? Promise.resolve(undefined),
      newCommandId,
      now: () => new Date().toISOString(),
      onUnavailable: () => {
        toastManager.add({
          type: "warning",
          title: "Clear is unavailable",
          description: "Open a server thread before clearing its conversation.",
        });
      },
      onError: (error) => {
        if (!threadId) return;
        setThreadError(
          threadId,
          error instanceof Error ? error.message : "Failed to clear the conversation.",
        );
      },
    });
  }, [activeThread?.id, isServerThread, setThreadError]);

  const stopActiveThreadSession = useCallback(async () => {
    const api = readNativeApi();
    if (
      !api ||
      !isServerThread ||
      !activeThread ||
      activeThread.session === null ||
      activeThread.session.status === "closed"
    ) {
      return;
    }

    await api.orchestration.dispatchCommand({
      type: "thread.session.stop",
      commandId: newCommandId(),
      threadId: activeThread.id,
      createdAt: new Date().toISOString(),
    });
  }, [activeThread, isServerThread]);
  const {
    handoffBusy,
    worktreeHandoffDialogOpen,
    setWorktreeHandoffDialogOpen,
    worktreeHandoffName,
    setWorktreeHandoffName,
    onHandoffToWorktree,
    onHandoffToLocal,
    confirmWorktreeHandoff,
  } = useThreadWorkspaceHandoff({
    activeProject,
    activeThread,
    activeRootBranch,
    activeThreadAssociatedWorktree,
    isServerThread,
    stopActiveThreadSession,
    runSetupScript: runSetupProjectScript,
    setStoreThreadWorkspace,
    syncServerReadModel,
  });
  const persistProjectScripts = useCallback(
    async (input: {
      projectId: ProjectId;
      projectCwd: string;
      previousScripts: ProjectScript[];
      nextScripts: ProjectScript[];
      keybinding?: string | null;
      keybindingCommand: KeybindingCommand;
    }) => {
      const api = readNativeApi();
      if (!api) return;

      await api.orchestration.dispatchCommand({
        type: "project.meta.update",
        commandId: newCommandId(),
        projectId: input.projectId,
        scripts: input.nextScripts,
      });

      const keybindingRule = decodeProjectScriptKeybindingRule({
        keybinding: input.keybinding,
        command: input.keybindingCommand,
      });

      if (isElectron && keybindingRule) {
        await api.server.upsertKeybinding(keybindingRule);
        await queryClient.invalidateQueries({ queryKey: serverQueryKeys.all });
      }
    },
    [queryClient],
  );
  const saveProjectScript = useCallback(
    async (input: NewProjectScriptInput) => {
      if (!activeProject) return;
      const nextId = nextProjectScriptId(
        input.name,
        activeProject.scripts.map((script) => script.id),
      );
      const nextScript: ProjectScript = {
        id: nextId,
        name: input.name,
        kind: input.kind,
        command: input.command,
        runMode: input.runMode,
        icon: input.icon,
        runOnWorktreeCreate: input.runOnWorktreeCreate,
        runOnThreadArchive: input.runOnThreadArchive,
      };
      // Setup is exclusive — one action runs on worktree creation — while
      // archive is not: a teardown is often several commands the user wants in
      // order, so nothing is cleared here for `runOnThreadArchive`.
      const nextScripts = input.runOnWorktreeCreate
        ? [
            ...activeProject.scripts.map((script) =>
              script.runOnWorktreeCreate ? { ...script, runOnWorktreeCreate: false } : script,
            ),
            nextScript,
          ]
        : [...activeProject.scripts, nextScript];

      await persistProjectScripts({
        projectId: activeProject.id,
        projectCwd: activeProject.cwd,
        previousScripts: activeProject.scripts,
        nextScripts,
        keybinding: input.keybinding,
        keybindingCommand: commandForProjectScript(nextId),
      });
    },
    [activeProject, persistProjectScripts],
  );
  const updateProjectScript = useCallback(
    async (scriptId: string, input: NewProjectScriptInput) => {
      if (!activeProject) return;
      const existingScript = activeProject.scripts.find((script) => script.id === scriptId);
      if (!existingScript) {
        throw new Error("Script not found.");
      }

      const updatedScript: ProjectScript = {
        ...existingScript,
        name: input.name,
        kind: input.kind,
        command: input.command,
        runMode: input.runMode,
        icon: input.icon,
        runOnWorktreeCreate: input.runOnWorktreeCreate,
        runOnThreadArchive: input.runOnThreadArchive,
      };
      const nextScripts = activeProject.scripts.map((script) =>
        script.id === scriptId
          ? updatedScript
          : input.runOnWorktreeCreate
            ? { ...script, runOnWorktreeCreate: false }
            : script,
      );

      await persistProjectScripts({
        projectId: activeProject.id,
        projectCwd: activeProject.cwd,
        previousScripts: activeProject.scripts,
        nextScripts,
        keybinding: input.keybinding,
        keybindingCommand: commandForProjectScript(scriptId),
      });
    },
    [activeProject, persistProjectScripts],
  );
  const deleteProjectScript = useCallback(
    async (scriptId: string) => {
      if (!activeProject) return;
      const nextScripts = activeProject.scripts.filter((script) => script.id !== scriptId);

      const deletedName = activeProject.scripts.find((s) => s.id === scriptId)?.name;

      try {
        await persistProjectScripts({
          projectId: activeProject.id,
          projectCwd: activeProject.cwd,
          previousScripts: activeProject.scripts,
          nextScripts,
          keybinding: null,
          keybindingCommand: commandForProjectScript(scriptId),
        });
        toastManager.add({
          type: "success",
          title: `Deleted action "${deletedName ?? "Unknown"}"`,
        });
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Could not delete action",
          description: error instanceof Error ? error.message : "An unexpected error occurred.",
        });
      }
    },
    [activeProject, persistProjectScripts],
  );

  const handleRuntimeModeChange = useCallback(
    (mode: RuntimeMode) => {
      if (mode === runtimeMode) return;
      setComposerDraftRuntimeMode(threadId, mode);
      if (isLocalDraftThread) {
        setDraftThreadContext(threadId, { runtimeMode: mode });
      }
      scheduleComposerFocus();
    },
    [
      isLocalDraftThread,
      runtimeMode,
      scheduleComposerFocus,
      setComposerDraftRuntimeMode,
      setDraftThreadContext,
      threadId,
    ],
  );

  const handleInteractionModeChange = useCallback(
    (mode: ProviderInteractionMode) => {
      if (mode === interactionMode) return;
      setComposerDraftInteractionMode(threadId, mode);
      if (isLocalDraftThread) {
        setDraftThreadContext(threadId, { interactionMode: mode });
      }
      scheduleComposerFocus();
    },
    [
      interactionMode,
      isLocalDraftThread,
      scheduleComposerFocus,
      setComposerDraftInteractionMode,
      setDraftThreadContext,
      threadId,
    ],
  );
  const toggleInteractionMode = useCallback(() => {
    handleInteractionModeChange(interactionMode === "plan" ? "default" : "plan");
  }, [handleInteractionModeChange, interactionMode]);
  const togglePlanSidebar = useCallback(() => {
    setPlanSidebarOpen((open) => {
      if (open) {
        planSidebarDismissedForTurnRef.current =
          activePlan?.turnId ?? sidebarProposedPlan?.turnId ?? "__dismissed__";
      } else {
        planSidebarDismissedForTurnRef.current = null;
      }
      return !open;
    });
  }, [activePlan?.turnId, sidebarProposedPlan?.turnId]);
  const setPlanMode = useCallback(
    (enabled: boolean) => {
      handleInteractionModeChange(enabled ? "plan" : "default");
    },
    [handleInteractionModeChange],
  );
  const persistThreadSettingsForNextTurn = useCallback(
    async (input: {
      threadId: ThreadId;
      createdAt: string;
      modelSelection?: ModelSelection;
      runtimeMode: RuntimeMode;
      interactionMode: ProviderInteractionMode;
    }) => {
      if (!serverThread) {
        return;
      }
      const api = readNativeApi();
      if (!api) {
        return;
      }

      // Shared with the server's queued-message dispatcher, which has to make the same call about
      // the same pre-command. Two copies of this comparison drifting apart would send a queued
      // message under a model its author never picked.
      if (
        input.modelSelection !== undefined &&
        modelSelectionDiffers(input.modelSelection, serverThread.modelSelection)
      ) {
        await api.orchestration.dispatchCommand({
          type: "thread.meta.update",
          commandId: newCommandId(),
          threadId: input.threadId,
          modelSelection: input.modelSelection,
        });
      }

      if (input.runtimeMode !== serverThread.runtimeMode) {
        await api.orchestration.dispatchCommand({
          type: "thread.runtime-mode.set",
          commandId: newCommandId(),
          threadId: input.threadId,
          runtimeMode: input.runtimeMode,
          createdAt: input.createdAt,
        });
      }

      if (input.interactionMode !== serverThread.interactionMode) {
        await api.orchestration.dispatchCommand({
          type: "thread.interaction-mode.set",
          commandId: newCommandId(),
          threadId: input.threadId,
          interactionMode: input.interactionMode,
          createdAt: input.createdAt,
        });
      }
    },
    [serverThread],
  );

  // Scroll behavior is isolated in a dedicated controller so the renderer tree only wires events.
  const messageCount = timelineEntries.length;
  const {
    messagesScrollElement,
    showScrollToBottom,
    setMessagesBottomAnchorRef,
    setMessagesScrollContainerRef,
    forceStickToBottom,
    onTimelineHeightChange,
    onComposerHeightChange,
    onMessagesClickCapture,
    onMessagesPointerCancel,
    onMessagesPointerDown,
    onMessagesPointerUp,
    onMessagesScroll,
    onMessagesTouchEnd,
    onMessagesTouchMove,
    onMessagesTouchStart,
    onMessagesWheel,
  } = useChatAutoScrollController({
    threadId: activeThread?.id ?? null,
    isStreaming: isWorking,
    messageCount,
  });

  // Listens on the scroll container rather than taking props on ChatTranscriptPane: the pane is
  // memoized to survive every keystroke in the composer, and selection state changes far too often
  // to push through it.
  const { active: transcriptSelection, dismiss: dismissTranscriptSelection } =
    useTranscriptSelection(messagesScrollElement);

  const onReplyToTranscriptSelection = useCallback(() => {
    if (!transcriptSelection) {
      return;
    }
    addQuotedContextToDraft({
      kind: "message",
      messageId: transcriptSelection.selection.messageId,
      messageRole: transcriptSelection.selection.messageRole,
      text: transcriptSelection.selection.text,
    });
    dismissTranscriptSelection();
    window.getSelection()?.removeAllRanges();
  }, [addQuotedContextToDraft, dismissTranscriptSelection, transcriptSelection]);

  const setSideChatAnchor = useSideChatAnchorStore((store) => store.setSideChatAnchor);
  const hasSideChatAgent = useMemo(
    () => resolveSideChatAgents(visibleManagedAgents).length > 0,
    [visibleManagedAgents],
  );
  const onStartSideChatFromSelection = useCallback(() => {
    if (!transcriptSelection) {
      return;
    }
    // The anchor travels through its own store rather than the panel state: it
    // only matters until the first message is sent, after which the quote lives
    // in the child thread's transcript.
    setSideChatAnchor(threadId, {
      kind: "message",
      messageId: transcriptSelection.selection.messageId,
      messageRole: transcriptSelection.selection.messageRole,
      text: transcriptSelection.selection.text,
    });
    // A new anchor means a new conversation; the surface that owns the panel
    // state is what clears the previous side chat, in `onOpenSideChatPanel`.
    onOpenSideChat();
    dismissTranscriptSelection();
    window.getSelection()?.removeAllRanges();
  }, [
    dismissTranscriptSelection,
    onOpenSideChat,
    setSideChatAnchor,
    threadId,
    transcriptSelection,
  ]);

  useLayoutEffect(() => {
    const composerForm = composerFormRef.current;
    if (!composerForm) return;
    const measureComposerFormWidth = () => composerForm.clientWidth;

    composerFormHeightRef.current = composerForm.getBoundingClientRect().height;
    setIsComposerFooterCompact(
      shouldUseCompactComposerFooter(measureComposerFormWidth(), {
        hasWideActions: composerFooterHasWideActions,
      }),
    );
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      const nextCompact = shouldUseCompactComposerFooter(measureComposerFormWidth(), {
        hasWideActions: composerFooterHasWideActions,
      });
      setIsComposerFooterCompact((previous) => (previous === nextCompact ? previous : nextCompact));

      const nextHeight = entry.contentRect.height;
      const previousHeight = composerFormHeightRef.current;
      composerFormHeightRef.current = nextHeight;

      onComposerHeightChange(previousHeight, nextHeight);
    });

    observer.observe(composerForm);
    return () => {
      observer.disconnect();
    };
  }, [activeThread?.id, composerFooterHasWideActions, onComposerHeightChange]);

  useEffect(() => {
    setExpandedWorkGroups({});
    if (planSidebarOpenOnNextThreadRef.current) {
      planSidebarOpenOnNextThreadRef.current = false;
      setPlanSidebarOpen(true);
    } else {
      setPlanSidebarOpen(false);
    }
    planSidebarDismissedForTurnRef.current = null;
  }, [activeThread?.id]);

  useEffect(() => {
    if (!composerMenuOpen) {
      setComposerHighlightedItemId(null);
      return;
    }
    setComposerHighlightedItemId((existing) =>
      existing && composerMenuItems.some((item) => item.id === existing)
        ? existing
        : (composerMenuItems[0]?.id ?? null),
    );
  }, [composerMenuItems, composerMenuOpen]);

  useEffect(() => {
    setIsRevertingCheckpoint(false);
  }, [activeThread?.id]);

  useEffect(() => {
    if (!activeThread?.id || terminalState.terminalOpen) return;
    const frame = window.requestAnimationFrame(() => {
      focusComposer();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [activeThread?.id, focusComposer, terminalState.terminalOpen]);

  useEffect(() => {
    composerAttachmentsRef.current = composerAttachments;
  }, [composerAttachments]);

  useEffect(() => {
    composerTerminalContextsRef.current = composerQuotedContexts;
  }, [composerQuotedContexts]);

  useEffect(() => {
    if (!activeThread?.id) return;
    if (activeThread.messages.length === 0) {
      return;
    }
    const serverIds = new Set(activeThread.messages.map((message) => message.id));
    const removedMessages = optimisticUserMessages.filter((message) => serverIds.has(message.id));
    if (removedMessages.length === 0) {
      return;
    }
    const timer = window.setTimeout(() => {
      setOptimisticUserMessages((existing) =>
        existing.filter((message) => !serverIds.has(message.id)),
      );
    }, 0);
    for (const removedMessage of removedMessages) {
      const previewUrlByAttachmentId = collectUserMessageBlobPreviewUrls(removedMessage);
      if (Object.keys(previewUrlByAttachmentId).length > 0) {
        handoffAttachmentPreviews(removedMessage.id, previewUrlByAttachmentId);
        continue;
      }
      revokeUserMessagePreviewUrls(removedMessage);
    }
    return () => {
      window.clearTimeout(timer);
    };
  }, [activeThread?.id, activeThread?.messages, handoffAttachmentPreviews, optimisticUserMessages]);

  useEffect(() => {
    promptRef.current = prompt;
    setComposerCursor((existing) => clampCollapsedComposerCursor(prompt, existing));
  }, [prompt]);

  useEffect(() => {
    setSelectedComposerSkills((existing) =>
      existing.filter((skill) => promptIncludesSkillMention(prompt, skill.name, selectedProvider)),
    );
  }, [prompt, selectedProvider]);

  useEffect(() => {
    setSelectedComposerMentions((existing) => {
      const nextMentions = resolvePromptPluginMentions({
        prompt,
        existingMentions: existing,
        providerPlugins,
      });
      return providerMentionReferencesEqual(existing, nextMentions) ? existing : nextMentions;
    });
  }, [prompt, providerPlugins]);

  // Clear selected skills when switching providers — skills are provider-specific.
  useEffect(() => {
    setSelectedComposerSkills([]);
    setSelectedComposerMentions([]);
  }, [selectedProvider]);

  useEffect(() => {
    voiceTranscriptionRequestIdRef.current += 1;
    voiceRecordingStartedAtRef.current = null;
    void cancelVoiceRecording();
    setIsVoiceTranscribing(false);
    setOptimisticUserMessages((existing) => {
      for (const message of existing) {
        revokeUserMessagePreviewUrls(message);
      }
      return [];
    });
    setLocalDispatch(null);
    setComposerHighlightedItemId(null);
    setComposerCursor(collapseExpandedComposerCursor(promptRef.current, promptRef.current.length));
    setComposerTrigger(detectComposerTrigger(promptRef.current, promptRef.current.length));
    setSelectedComposerSkills([]);
    setSelectedComposerMentions([]);
    dragDepthRef.current = 0;
    setIsDragOverComposer(false);
    setExpandedImage(null);
  }, [cancelVoiceRecording, threadId]);

  useEffect(() => {
    if (canStartVoiceNotes || !isVoiceRecording) {
      return;
    }
    warnVoiceGuard("cancelled active voice recording because voice became unavailable", {
      authStatus: voiceProviderStatus?.authStatus ?? null,
      voiceTranscriptionAvailable: voiceProviderStatus?.voiceTranscriptionAvailable ?? null,
      isVoiceRecording,
    });
    voiceTranscriptionRequestIdRef.current += 1;
    voiceRecordingStartedAtRef.current = null;
    void cancelVoiceRecording();
    setIsVoiceTranscribing(false);
  }, [
    canStartVoiceNotes,
    cancelVoiceRecording,
    isVoiceRecording,
    voiceProviderStatus?.authStatus,
    voiceProviderStatus?.voiceTranscriptionAvailable,
  ]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (composerAttachments.length === 0) {
        clearComposerDraftPersistedAttachments(threadId);
        return;
      }
      const getPersistedAttachmentsForThread = () =>
        useComposerDraftStore.getState().draftsByThreadId[threadId]?.persistedAttachments ?? [];
      try {
        const currentPersistedAttachments = getPersistedAttachmentsForThread();
        const existingPersistedById = new Map(
          currentPersistedAttachments.map((attachment) => [attachment.id, attachment]),
        );
        const stagedAttachmentById = new Map<string, PersistedComposerAttachment>();
        await Promise.all(
          composerAttachments.map(async (attachment) => {
            try {
              const dataUrl = await readFileAsDataUrl(attachment.file);
              stagedAttachmentById.set(attachment.id, {
                id: attachment.id,
                name: attachment.name,
                mimeType: attachment.mimeType,
                sizeBytes: attachment.sizeBytes,
                type: attachment.type,
                dataUrl,
              });
            } catch {
              const existingPersisted = existingPersistedById.get(attachment.id);
              if (existingPersisted) {
                stagedAttachmentById.set(attachment.id, existingPersisted);
              }
            }
          }),
        );
        const serialized = Array.from(stagedAttachmentById.values());
        if (cancelled) {
          return;
        }
        // Stage attachments in persisted draft state first so persist middleware can write them.
        syncComposerDraftPersistedAttachments(threadId, serialized);
      } catch {
        const currentImageIds = new Set(composerAttachments.map((image) => image.id));
        const fallbackPersistedAttachments = getPersistedAttachmentsForThread();
        const fallbackPersistedIds = fallbackPersistedAttachments
          .map((attachment) => attachment.id)
          .filter((id) => currentImageIds.has(id));
        const fallbackPersistedIdSet = new Set(fallbackPersistedIds);
        const fallbackAttachments = fallbackPersistedAttachments.filter((attachment) =>
          fallbackPersistedIdSet.has(attachment.id),
        );
        if (cancelled) {
          return;
        }
        syncComposerDraftPersistedAttachments(threadId, fallbackAttachments);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    clearComposerDraftPersistedAttachments,
    composerAttachments,
    syncComposerDraftPersistedAttachments,
    threadId,
  ]);

  const closeExpandedImage = useCallback(() => {
    setExpandedImage(null);
  }, []);
  const navigateExpandedImage = useCallback((direction: -1 | 1) => {
    setExpandedImage((existing) => {
      if (!existing || existing.images.length <= 1) {
        return existing;
      }
      const nextIndex =
        (existing.index + direction + existing.images.length) % existing.images.length;
      if (nextIndex === existing.index) {
        return existing;
      }
      return { ...existing, index: nextIndex };
    });
  }, []);

  useEffect(() => {
    if (!expandedImage) {
      return;
    }

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeExpandedImage();
        return;
      }
      if (expandedImage.images.length <= 1) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        event.stopPropagation();
        navigateExpandedImage(-1);
        return;
      }
      if (event.key !== "ArrowRight") return;
      event.preventDefault();
      event.stopPropagation();
      navigateExpandedImage(1);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeExpandedImage, expandedImage, navigateExpandedImage]);

  useEffect(() => {
    if (!composerMenuOpen) {
      return;
    }

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setComposerCommandPicker(null);
      setComposerHighlightedItemId(null);
      setComposerTrigger(null);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [composerMenuOpen]);

  const activeWorktreePath = activeThread?.worktreePath;
  const envMode: DraftThreadEnvMode = isServerThread
    ? resolveThreadEnvironmentMode({
        envMode: activeThread?.envMode,
        worktreePath: activeWorktreePath ?? null,
      })
    : (draftThread?.envMode ?? "local");
  const envState = resolveThreadWorkspaceState({
    envMode: resolvedThreadEnvMode,
    worktreePath: resolvedThreadWorktreePath,
  });

  const beginLocalDispatch = useCallback(
    (options?: { preparingWorktree?: boolean }) => {
      const preparingWorktree = Boolean(options?.preparingWorktree);
      setLocalDispatch((current) => {
        if (current) {
          return current.preparingWorktree === preparingWorktree
            ? current
            : { ...current, preparingWorktree };
        }
        return createLocalDispatchSnapshot(activeThread, options);
      });
    },
    [activeThread],
  );

  const resetLocalDispatch = useCallback(() => {
    setLocalDispatch(null);
  }, []);

  useEffect(() => {
    if (!serverAcknowledgedLocalDispatch) {
      return;
    }
    resetLocalDispatch();
  }, [resetLocalDispatch, serverAcknowledgedLocalDispatch]);

  useEffect(() => {
    if (!activeThreadId) return;
    const previous = terminalOpenByThreadRef.current[activeThreadId] ?? false;
    const current = Boolean(terminalState.terminalOpen);

    if (!previous && current) {
      terminalOpenByThreadRef.current[activeThreadId] = current;
      setTerminalFocusRequestId((value) => value + 1);
      return;
    } else if (previous && !current) {
      terminalOpenByThreadRef.current[activeThreadId] = current;
      const frame = window.requestAnimationFrame(() => {
        focusComposer();
      });
      return () => {
        window.cancelAnimationFrame(frame);
      };
    }

    terminalOpenByThreadRef.current[activeThreadId] = current;
  }, [activeThreadId, focusComposer, terminalState.terminalOpen]);

  useEffect(() => {
    if (!activeThreadId) {
      activatedThreadIdRef.current = null;
      return;
    }
    if (activatedThreadIdRef.current === activeThreadId) {
      return;
    }
    activatedThreadIdRef.current = activeThreadId;
    if (terminalState.entryPoint !== "terminal") {
      return;
    }
    storeOpenTerminalThreadPage(activeThreadId);
  }, [activeThreadId, storeOpenTerminalThreadPage, terminalState.entryPoint]);

  useEffect(() => {
    if (!terminalWorkspaceOpen) {
      return;
    }

    if (terminalState.workspaceActiveTab === "terminal") {
      setTerminalFocusRequestId((value) => value + 1);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      focusComposer();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [focusComposer, terminalState.workspaceActiveTab, terminalWorkspaceOpen]);

  const onInterrupt = useCallback(async () => {
    const api = readNativeApi();
    if (!api || !activeThread) return;
    await api.orchestration.dispatchCommand({
      type: "thread.turn.interrupt",
      commandId: newCommandId(),
      threadId: activeThread.id,
      createdAt: new Date().toISOString(),
    });
  }, [activeThread]);

  useEffect(() => {
    if (surfaceMode === "split" && !isFocusedPane) {
      return;
    }

    const handler = (event: globalThis.KeyboardEvent) => {
      if (!activeThreadId || event.defaultPrevented) return;
      // Mirror terminal interrupt semantics without stealing regular copy shortcuts.
      if (
        hasLiveTurn &&
        isMacPlatform(navigator.platform) &&
        event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.shiftKey &&
        event.key.toLowerCase() === "c" &&
        eventTargetsComposer(event, composerFormRef.current)
      ) {
        event.preventDefault();
        event.stopPropagation();
        void onInterrupt();
        return;
      }
      // Ctrl+S (Cmd+S too, see `isSaveComposerToQueueShortcut`) saves the draft to the queue.
      // `preventDefault` is not optional: without it the browser opens its save-page dialog.
      if (
        !isTerminalFocused() &&
        isSaveComposerToQueueShortcut(event) &&
        eventTargetsComposer(event, composerFormRef.current)
      ) {
        event.preventDefault();
        event.stopPropagation();
        onSaveComposerToQueueRef.current();
        return;
      }
      const useMetaForMod = isMacPlatform(navigator.platform);
      const composerPickerShortcutActive =
        !isTerminalFocused() &&
        !isVoiceRecording &&
        !isVoiceTranscribing &&
        !isComposerApprovalState &&
        canHandleComposerPickerShortcut(event, composerFormRef.current);
      if (
        composerPickerShortcutActive &&
        event.shiftKey &&
        !event.altKey &&
        event.metaKey === useMetaForMod &&
        event.ctrlKey === !useMetaForMod
      ) {
        const normalizedKey = event.key.toLowerCase();
        if (normalizedKey === "m") {
          event.preventDefault();
          event.stopPropagation();
          handleModelPickerOpenChange(true);
          scheduleComposerFocus();
          return;
        }
        if (normalizedKey === "e") {
          event.preventDefault();
          event.stopPropagation();
          handleTraitsPickerOpenChange(true);
          scheduleComposerFocus();
          return;
        }
      }
      const shortcutContext = {
        terminalFocus: isTerminalFocused(),
        terminalOpen: Boolean(terminalState.terminalOpen),
        terminalWorkspaceOpen,
        terminalWorkspaceTerminalOnly: terminalState.workspaceLayout === "terminal-only",
        terminalWorkspaceTerminalTabActive,
        terminalWorkspaceChatTabActive,
      };

      const command = resolveShortcutCommand(event, keybindings, {
        context: shortcutContext,
      });
      if (!command) return;

      if (command === "terminal.toggle") {
        event.preventDefault();
        event.stopPropagation();
        toggleTerminalVisibility();
        return;
      }

      if (command === "terminal.split" || command === "terminal.splitRight") {
        event.preventDefault();
        event.stopPropagation();
        if (!terminalState.terminalOpen) {
          setTerminalOpen(true);
        }
        splitTerminalRight();
        return;
      }

      if (command === "terminal.splitLeft") {
        event.preventDefault();
        event.stopPropagation();
        if (!terminalState.terminalOpen) {
          setTerminalOpen(true);
        }
        splitTerminalLeft();
        return;
      }

      if (command === "terminal.splitDown") {
        event.preventDefault();
        event.stopPropagation();
        if (!terminalState.terminalOpen) {
          setTerminalOpen(true);
        }
        splitTerminalDown();
        return;
      }

      if (command === "terminal.splitUp") {
        event.preventDefault();
        event.stopPropagation();
        if (!terminalState.terminalOpen) {
          setTerminalOpen(true);
        }
        splitTerminalUp();
        return;
      }

      if (command === "terminal.close") {
        event.preventDefault();
        event.stopPropagation();
        if (!terminalState.terminalOpen) return;
        closeTerminal(terminalState.activeTerminalId);
        return;
      }

      if (command === "terminal.new") {
        event.preventDefault();
        event.stopPropagation();
        createTerminalFromShortcut();
        return;
      }

      if (command === "terminal.workspace.newFullWidth") {
        event.preventDefault();
        event.stopPropagation();
        openNewFullWidthTerminal();
        return;
      }

      if (command === "terminal.workspace.closeActive") {
        event.preventDefault();
        event.stopPropagation();
        closeActiveWorkspaceView();
        return;
      }

      if (command === "terminal.workspace.terminal") {
        event.preventDefault();
        event.stopPropagation();
        if (!terminalWorkspaceOpen) return;
        setTerminalWorkspaceTab("terminal");
        return;
      }

      if (command === "terminal.workspace.chat") {
        event.preventDefault();
        event.stopPropagation();
        if (!terminalWorkspaceOpen) return;
        setTerminalWorkspaceTab("chat");
        return;
      }

      if (command === "diff.toggle") {
        event.preventDefault();
        event.stopPropagation();
        onToggleDiff();
        return;
      }

      if (command === "browser.toggle") {
        event.preventDefault();
        event.stopPropagation();
        if (!isElectron) return;
        onToggleBrowser();
        return;
      }

      if (command === "chat.split") {
        event.preventDefault();
        event.stopPropagation();
        if (surfaceMode === "single" && onSplitSurface) {
          onSplitSurface();
        }
        return;
      }

      const scriptId = projectScriptIdFromCommand(command);
      if (!scriptId || !activeProject) return;
      const script = activeProject.scripts.find((entry) => entry.id === scriptId);
      if (!script) return;
      event.preventDefault();
      event.stopPropagation();
      runProjectScriptActionRef.current(script);
    };
    window.addEventListener("keydown", handler, { capture: true });
    return () => window.removeEventListener("keydown", handler, { capture: true });
  }, [
    activeProject,
    terminalState.terminalOpen,
    terminalState.activeTerminalId,
    terminalState.workspaceLayout,
    activeThreadId,
    closeTerminal,
    closeActiveWorkspaceView,
    createTerminalFromShortcut,
    setTerminalOpen,
    openNewFullWidthTerminal,
    keybindings,
    splitTerminalDown,
    splitTerminalLeft,
    splitTerminalRight,
    splitTerminalUp,
    terminalWorkspaceChatTabActive,
    terminalWorkspaceOpen,
    terminalWorkspaceTerminalTabActive,
    onToggleBrowser,
    onToggleDiff,
    onInterrupt,
    onSplitSurface,
    isFocusedPane,
    hasLiveTurn,
    handleModelPickerOpenChange,
    handleTraitsPickerOpenChange,
    isComposerApprovalState,
    isVoiceRecording,
    isVoiceTranscribing,
    setTerminalWorkspaceTab,
    surfaceMode,
    scheduleComposerFocus,
    toggleTerminalVisibility,
  ]);

  const startComposerVoiceRecording = useCallback(async () => {
    if (!activeProject) {
      return;
    }
    if (voiceProviderStatus?.authStatus === "unauthenticated") {
      toastManager.add({
        type: "error",
        title: "Sign in to ChatGPT in Codex before using voice notes.",
      });
      return;
    }
    if (!canStartVoiceNotes) {
      toastManager.add({
        type: "error",
        title: "Voice notes require a ChatGPT-authenticated Codex session.",
      });
      return;
    }
    if (pendingUserInputs.length > 0) {
      toastManager.add({
        type: "error",
        title: "Answer plan questions before recording a voice note.",
      });
      return;
    }

    try {
      await startVoiceRecording();
      voiceRecordingStartedAtRef.current = performance.now();
    } catch (error) {
      toastManager.add({
        type: "error",
        title: "Could not start recording",
        description: describeVoiceRecordingStartError(error),
      });
    }
  }, [
    activeProject,
    canStartVoiceNotes,
    pendingUserInputs.length,
    startVoiceRecording,
    voiceProviderStatus?.authStatus,
  ]);

  const submitComposerVoiceRecording = useCallback(async () => {
    if (!activeProject || !isVoiceRecording) {
      return;
    }
    const recordedForMs =
      voiceRecordingStartedAtRef.current === null
        ? null
        : Math.round(performance.now() - voiceRecordingStartedAtRef.current);
    if (
      recordedForMs !== null &&
      recordedForMs >= 0 &&
      recordedForMs < VOICE_RECORDER_ACTION_ARM_DELAY_MS
    ) {
      warnVoiceGuard("ignored recorder action immediately after start", {
        recordedForMs,
      });
      return;
    }

    const api = readNativeApi();
    if (!api) {
      toastManager.add({
        type: "error",
        title: "Voice transcription is unavailable right now.",
      });
      void cancelVoiceRecording();
      return;
    }

    setIsVoiceTranscribing(true);
    const requestId = voiceTranscriptionRequestIdRef.current + 1;
    voiceTranscriptionRequestIdRef.current = requestId;
    const requestThreadId = threadId;
    const requestProvider = selectedProvider;
    const isCurrentVoiceRequest = () =>
      voiceTranscriptionRequestIdRef.current === requestId &&
      voiceThreadIdRef.current === requestThreadId &&
      voiceProviderRef.current === requestProvider;

    try {
      const payload = await stopVoiceRecording();
      if (!isCurrentVoiceRequest()) {
        return;
      }
      if (!payload) {
        toastManager.add({
          type: "warning",
          title: "No audio was captured.",
        });
        return;
      }
      const result = await api.server.transcribeVoice({
        provider: "codex",
        cwd: activeProject.cwd,
        ...(activeThread ? { threadId: activeThread.id } : {}),
        ...payload,
      });
      if (!isCurrentVoiceRequest()) {
        return;
      }
      appendVoiceTranscriptToComposer(result.text);
    } catch (error) {
      if (!isCurrentVoiceRequest()) {
        return;
      }
      const description =
        error instanceof Error
          ? sanitizeVoiceErrorMessage(error.message)
          : "The voice note could not be transcribed.";
      const authExpired = isVoiceAuthExpiredMessage(description);
      if (authExpired) {
        refreshProviderStatuses();
      }
      toastManager.add({
        type: "error",
        title: authExpired ? "Sign in to ChatGPT again" : "Couldn't transcribe voice note",
        description: authExpired
          ? "Voice transcription uses your ChatGPT session in Codex. That session was rejected, so sign in again there and retry."
          : description,
        ...(authExpired
          ? {
              actionProps: {
                children: "Refresh status",
                onClick: refreshProviderStatuses,
              },
            }
          : {}),
      });
    } finally {
      if (isCurrentVoiceRequest()) {
        voiceRecordingStartedAtRef.current = null;
        setIsVoiceTranscribing(false);
      }
    }
  }, [
    activeProject,
    activeThread,
    appendVoiceTranscriptToComposer,
    cancelVoiceRecording,
    isVoiceRecording,
    refreshProviderStatuses,
    selectedProvider,
    stopVoiceRecording,
    threadId,
  ]);

  const cancelComposerVoiceRecording = useCallback(() => {
    const recordedForMs =
      voiceRecordingStartedAtRef.current === null
        ? null
        : Math.round(performance.now() - voiceRecordingStartedAtRef.current);
    if (
      recordedForMs !== null &&
      recordedForMs >= 0 &&
      recordedForMs < VOICE_RECORDER_ACTION_ARM_DELAY_MS
    ) {
      warnVoiceGuard("ignored recorder action immediately after start", {
        recordedForMs,
      });
      return;
    }
    voiceTranscriptionRequestIdRef.current += 1;
    voiceRecordingStartedAtRef.current = null;
    setIsVoiceTranscribing(false);
    void cancelVoiceRecording();
  }, [cancelVoiceRecording]);

  // Preserve the original "single mic button" contract:
  // first click starts recording, the next click submits/transcribes.
  const toggleComposerVoiceRecording = useCallback(() => {
    if (isVoiceTranscribing) {
      return;
    }
    if (isVoiceRecording) {
      void submitComposerVoiceRecording();
      return;
    }
    void startComposerVoiceRecording();
  }, [
    isVoiceRecording,
    isVoiceTranscribing,
    startComposerVoiceRecording,
    submitComposerVoiceRecording,
  ]);

  // --- Composer attachment entry points -------------------------------------
  const addComposerAttachments = (files: File[]) => {
    if (!activeThreadId || files.length === 0) return;

    if (pendingUserInputs.length > 0) {
      toastManager.add({
        type: "error",
        title: "Attach files after answering plan questions.",
      });
      return;
    }

    const nextAttachments: ComposerAttachment[] = [];
    let nextAttachmentCount = composerAttachmentsRef.current.length;
    // The aggregate limit is reached by files that are each perfectly legal, so
    // it has to be counted against what is already staged rather than checked
    // per file. Refusing here is the only place it can be said clearly: past
    // this the frame is oversized and the socket closes instead of answering.
    let nextTotalBytes = composerAttachmentsRef.current.reduce(
      (total, attachment) => total + attachment.sizeBytes,
      0,
    );
    let error: string | null = null;
    for (const file of files) {
      // Same verdict the server will reach on the bytes; asking here only
      // saves the round trip. `file.type` is empty for plenty of source files,
      // which is why the resolver also reads the name.
      const resolution = resolveAttachmentType({ mimeType: file.type, fileName: file.name });
      if (!resolution.ok) {
        error =
          resolution.rejection.reason === "denied-extension"
            ? `'${file.name}' cannot be attached: ${resolution.rejection.extension} files are not allowed.`
            : `Unsupported file type for '${file.name}'.`;
        continue;
      }

      const { kind } = resolution.verdict;
      const maxBytes = attachmentMaxBytes(kind);
      if (file.size > maxBytes) {
        error = `'${file.name}' exceeds the ${formatAttachmentSize(maxBytes)} attachment limit.`;
        continue;
      }
      if (nextAttachmentCount >= PROVIDER_SEND_TURN_MAX_ATTACHMENTS) {
        error = `You can attach up to ${PROVIDER_SEND_TURN_MAX_ATTACHMENTS} files per message.`;
        break;
      }
      if (nextTotalBytes + file.size > PROVIDER_SEND_TURN_MAX_ATTACHMENTS_TOTAL_BYTES) {
        error = `'${file.name}' would put this message over the ${formatAttachmentSize(PROVIDER_SEND_TURN_MAX_ATTACHMENTS_TOTAL_BYTES)} total attachment limit.`;
        continue;
      }

      const previewUrl = URL.createObjectURL(file);
      nextAttachments.push({
        type: kind === "image" ? "image" : "file",
        id: randomUUID(),
        name: file.name || (kind === "image" ? "image" : "file"),
        // The canonical type, not what the browser guessed: the server stores
        // this one, and a chip that says `text/plain` for a `.rs` file is less
        // confusing than one that says nothing at all.
        mimeType: resolution.verdict.mimeType,
        sizeBytes: file.size,
        previewUrl,
        file,
      });
      nextAttachmentCount += 1;
      nextTotalBytes += file.size;
    }

    if (nextAttachments.length === 1 && nextAttachments[0]) {
      addComposerAttachment(nextAttachments[0]);
    } else if (nextAttachments.length > 1) {
      addComposerAttachmentsToDraft(nextAttachments);
    }
    setThreadError(activeThreadId, error);
  };

  const removeComposerAttachment = (attachmentId: string) => {
    removeComposerAttachmentFromDraft(attachmentId);
  };

  const onComposerPaste = (event: React.ClipboardEvent<HTMLElement>) => {
    const files = Array.from(event.clipboardData.files);
    if (files.length === 0) {
      return;
    }
    // Pasted content that is not a file at all (plain text, HTML) must keep
    // falling through to the textarea, so only claim the event once something
    // in the payload is actually attachable.
    const attachable = files.filter(
      (file) => resolveAttachmentType({ mimeType: file.type, fileName: file.name }).ok,
    );
    if (attachable.length === 0) {
      return;
    }
    event.preventDefault();
    addComposerAttachments(attachable);
  };

  const onComposerDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) {
      return;
    }
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOverComposer(true);
  };

  const onComposerDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setIsDragOverComposer(true);
  };

  const onComposerDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) {
      return;
    }
    event.preventDefault();
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return;
    }
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragOverComposer(false);
    }
  };

  const onComposerDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) {
      return;
    }
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOverComposer(false);
    const files = Array.from(event.dataTransfer.files);
    addComposerAttachments(files);
    focusComposer();
  };

  const onRevertToTurnCount = useCallback(
    async (turnCount: number) => {
      const api = readNativeApi();
      if (!api || !activeThread || isRevertingCheckpoint) return;

      if (hasLiveTurn || isSendBusy || isConnecting) {
        setThreadError(activeThread.id, "Interrupt the current turn before reverting checkpoints.");
        return;
      }
      const confirmed = await api.dialogs.confirm(
        [
          `Revert this thread to checkpoint ${turnCount}?`,
          "This will discard newer messages and turn diffs in this thread.",
          "This action cannot be undone.",
        ].join("\n"),
      );
      if (!confirmed) {
        return;
      }

      setIsRevertingCheckpoint(true);
      setThreadError(activeThread.id, null);
      try {
        await api.orchestration.dispatchCommand({
          type: "thread.checkpoint.revert",
          commandId: newCommandId(),
          threadId: activeThread.id,
          turnCount,
          createdAt: new Date().toISOString(),
        });
      } catch (err) {
        setThreadError(
          activeThread.id,
          err instanceof Error ? err.message : "Failed to revert thread state.",
        );
      }
      setIsRevertingCheckpoint(false);
    },
    [activeThread, hasLiveTurn, isConnecting, isRevertingCheckpoint, isSendBusy, setThreadError],
  );

  const onCreateHandoffThread = useCallback(async () => {
    if (!activeThread || handoffDisabled) {
      return;
    }

    try {
      await createThreadHandoff(activeThread);
    } catch (error) {
      toastManager.add({
        type: "error",
        title: "Could not create handoff thread",
        description:
          error instanceof Error
            ? error.message
            : "An error occurred while creating the handoff thread.",
      });
    }
  }, [activeThread, createThreadHandoff, handoffDisabled]);

  const clearComposerInput = useCallback(
    (threadId: ThreadId) => {
      promptRef.current = "";
      clearComposerDraftContent(threadId);
      setSelectedComposerSkills([]);
      setSelectedComposerMentions([]);
      setComposerHighlightedItemId(null);
      setComposerCursor(0);
      setComposerTrigger(null);
    },
    [clearComposerDraftContent],
  );

  /**
   * Rebuild a queued message's composer attachments from the server copy.
   *
   * Throws if any file cannot be fetched, and the caller leaves the row alone in that case: the
   * row is the only copy of what the person wrote, so it must outlive a failed download.
   */
  const downloadQueuedAttachments = useCallback(
    async (item: ComposerQueuedMessage): Promise<ComposerAttachment[]> => {
      return Promise.all(
        item.attachments.map(async (attachment) => {
          const response = await fetch(resolveAttachmentPreviewUrl(attachment.id));
          if (!response.ok) {
            throw new Error(`Could not download "${attachment.name}".`);
          }
          const blob = await response.blob();
          const file = new File([blob], attachment.name, { type: attachment.mimeType });
          return {
            type: attachment.type,
            id: attachment.id,
            name: attachment.name,
            mimeType: attachment.mimeType,
            sizeBytes: attachment.sizeBytes,
            previewUrl: URL.createObjectURL(file),
            file,
          } satisfies ComposerAttachment;
        }),
      );
    },
    [],
  );

  const restoreQueuedMessageToComposer = useCallback(
    (item: ComposerQueuedMessage, restoredImages: ReadonlyArray<ComposerAttachment>) => {
      if (!activeThread) {
        return;
      }
      const { composer, turn } = item.payload;
      const nextPrompt = composer.kind === "chat" ? composer.prompt : composer.text;
      promptRef.current = nextPrompt;
      clearComposerDraftContent(activeThread.id);
      setComposerDraftPrompt(activeThread.id, nextPrompt);
      // Editing a queued message should recreate the draft state it was queued from.
      setDraftThreadContext(activeThread.id, {
        runtimeMode: turn.runtimeMode,
        interactionMode: turn.interactionMode,
        ...(composer.kind === "chat" ? { envMode: composer.envMode } : {}),
      });
      if (composer.kind === "chat") {
        if (restoredImages.length > 0) {
          addComposerAttachmentsToDraft([...restoredImages]);
        }
        if (composer.quotedContexts.length > 0) {
          addComposerTerminalContextsToDraft([...composer.quotedContexts]);
        }
        setSelectedComposerSkills([...composer.skills]);
        setSelectedComposerMentions([...composer.mentions]);
      } else {
        setSelectedComposerSkills([]);
        setSelectedComposerMentions([]);
      }
      setComposerDraftModelSelection(activeThread.id, turn.modelSelection);
      setComposerDraftRuntimeMode(activeThread.id, turn.runtimeMode);
      setComposerDraftInteractionMode(activeThread.id, turn.interactionMode);
      setComposerCursor(collapseExpandedComposerCursor(nextPrompt, nextPrompt.length));
      setComposerTrigger(detectComposerTrigger(nextPrompt, nextPrompt.length));
      scheduleComposerFocus();
    },
    [
      activeThread,
      addComposerAttachmentsToDraft,
      addComposerTerminalContextsToDraft,
      clearComposerDraftContent,
      scheduleComposerFocus,
      setDraftThreadContext,
      setComposerDraftInteractionMode,
      setComposerDraftModelSelection,
      setComposerDraftPrompt,
      setComposerDraftRuntimeMode,
    ],
  );

  /**
   * The one path into the queue, shared by Enter-with-a-live-turn and Ctrl+S.
   *
   * The composer is cleared only after the server has the message. Clearing first would lose the
   * text on any failed round trip, and the row is the only copy of it.
   */
  const queueComposerMessage = useCallback(
    async (request: QueueComposerMessageRequest): Promise<boolean> => {
      if (!activeThread) {
        return false;
      }
      if (!isServerThread) {
        toastManager.add({
          type: "warning",
          title: "Nothing to queue against yet",
          description: "Send the first message so the thread exists, then queue follow-ups.",
        });
        return false;
      }

      const common = {
        threadId: activeThread.id,
        selectedProvider,
        selectedModel,
        selectedPromptEffort,
        modelSelection: selectedModelSelection,
        ...(providerOptionsForDispatch ? { providerOptions: providerOptionsForDispatch } : {}),
        assistantDeliveryMode: (settings.enableAssistantStreaming
          ? "streaming"
          : "buffered") as AssistantDeliveryMode,
        runtimeMode,
        // Explicit rather than leaning on the server default: queueing must never be mistaken for
        // "send this when you can". Turning it on is always a deliberate second act.
        autoSend: false,
      };

      try {
        const input =
          request.kind === "plan-follow-up"
            ? buildComposerQueueEnqueueInput({
                ...common,
                source: {
                  kind: "plan-follow-up",
                  text: request.text,
                  interactionMode: request.interactionMode,
                  ...(request.interactionMode === "default" && activeProposedPlan
                    ? {
                        sourceProposedPlan: {
                          threadId: activeThread.id,
                          planId: activeProposedPlan.id,
                        },
                      }
                    : {}),
                },
              })
            : buildComposerQueueEnqueueInput({
                ...common,
                source: {
                  kind: "chat",
                  prompt: request.prompt,
                  trimmedPrompt: request.trimmedPrompt,
                  attachments: await Promise.all(
                    request.attachments.map(async (attachment) => ({
                      type: attachment.type,
                      name: attachment.name,
                      mimeType: attachment.mimeType,
                      sizeBytes: attachment.sizeBytes,
                      dataUrl: await readFileAsDataUrl(attachment.file),
                    })),
                  ),
                  quotedContexts: request.quotedContexts,
                  skills: selectedComposerSkills,
                  mentions: selectedComposerMentions,
                  providerPlugins,
                  envMode,
                  interactionMode,
                },
              });
        await enqueueComposerQueueMessage(input);
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Could not queue the message",
          description: error instanceof Error ? error.message : "The server rejected the request.",
        });
        return false;
      }

      clearComposerInput(activeThread.id);
      return true;
    },
    [
      activeProposedPlan,
      activeThread,
      clearComposerInput,
      enqueueComposerQueueMessage,
      envMode,
      interactionMode,
      isServerThread,
      providerOptionsForDispatch,
      providerPlugins,
      runtimeMode,
      selectedComposerMentions,
      selectedComposerSkills,
      selectedModel,
      selectedModelSelection,
      selectedPromptEffort,
      selectedProvider,
      settings.enableAssistantStreaming,
    ],
  );

  const onSend = async (
    e?: { preventDefault: () => void },
    dispatchMode: "queue" | "steer" = "queue",
  ): Promise<boolean> => {
    e?.preventDefault();
    const api = readNativeApi();
    if (
      !api ||
      !activeThread ||
      isSendBusy ||
      isConnecting ||
      isVoiceTranscribing ||
      sendInFlightRef.current
    ) {
      return false;
    }
    if (activePendingProgress) {
      onAdvanceActivePendingUserInput();
      return true;
    }
    // A queued message is never replayed through here any more: the server dispatches it. What
    // reaches this point is always the live composer.
    const liveComposerSnapshot = composerEditorRef.current?.readSnapshot() ?? null;
    const promptForSend = liveComposerSnapshot?.value ?? promptRef.current;
    const {
      trimmedPrompt: trimmed,
      sendableQuotedContexts: sendableComposerQuotedContexts,
      expiredTerminalContextCount,
      hasSendableContent,
    } = deriveComposerSendState({
      prompt: promptForSend,
      imageCount: composerAttachments.length,
      quotedContexts: composerQuotedContexts,
    });
    if (showPlanFollowUpPrompt && activeProposedPlan) {
      const followUp = resolvePlanFollowUpSubmission({
        draftText: trimmed,
        planMarkdown: activeProposedPlan.planMarkdown,
      });
      if (hasLiveTurn && dispatchMode === "queue") {
        return queueComposerMessage({
          kind: "plan-follow-up",
          text: followUp.text,
          interactionMode: followUp.interactionMode,
        });
      }
      clearComposerInput(activeThread.id);
      return onSubmitPlanFollowUp({
        text: followUp.text,
        interactionMode: followUp.interactionMode,
        dispatchMode,
      });
    }
    if (composerAttachments.length === 0 && sendableComposerQuotedContexts.length === 0) {
      const handledSlashCommand = await handleStandaloneSlashCommand(trimmed);
      if (handledSlashCommand) {
        return true;
      }
    }
    if (!hasSendableContent) {
      if (expiredTerminalContextCount > 0) {
        const toastCopy = buildExpiredTerminalContextToastCopy(
          expiredTerminalContextCount,
          "empty",
        );
        toastManager.add({
          type: "warning",
          title: toastCopy.title,
          description: toastCopy.description,
        });
      }
      return false;
    }
    if (!activeProject) return false;
    if (hasLiveTurn && dispatchMode === "queue") {
      return queueComposerMessage({
        kind: "chat",
        prompt: promptForSend,
        trimmedPrompt: trimmed,
        attachments: composerAttachments,
        quotedContexts: sendableComposerQuotedContexts,
      });
    }
    const threadIdForSend = activeThread.id;
    const isFirstMessage = !isServerThread || !hasNativeUserMessages;
    const baseBranchForWorktree =
      isFirstMessage && envMode === "worktree" && !activeThread.worktreePath
        ? activeThread.branch
        : null;

    // In worktree mode, require an explicit base branch so we don't silently
    // fall back to local execution when branch selection is missing.
    const shouldCreateWorktree =
      isFirstMessage && envMode === "worktree" && !activeThread.worktreePath;
    if (shouldCreateWorktree && !activeThread.branch) {
      setStoreThreadError(
        threadIdForSend,
        "Select a base branch before sending in New worktree mode.",
      );
      return false;
    }

    sendInFlightRef.current = true;
    beginLocalDispatch({ preparingWorktree: Boolean(baseBranchForWorktree) });

    const composerAttachmentsSnapshot = [...composerAttachments];
    const composerTerminalContextsSnapshot = [...sendableComposerQuotedContexts];
    const composerSkillsSnapshot = [...selectedComposerSkills];
    const composerMentionsSnapshot = [...selectedComposerMentions];
    const messageTextForSend = appendQuotedContextsToPrompt(
      promptForSend,
      composerTerminalContextsSnapshot,
    );
    const messageIdForSend = newMessageId();
    const messageCreatedAt = new Date().toISOString();
    const outgoingMessageText = formatOutgoingPrompt({
      provider: selectedProvider,
      model: selectedModel,
      effort: selectedPromptEffort,
      text: messageTextForSend || attachmentOnlyBootstrapPrompt(composerAttachmentsSnapshot),
    });
    const mentionedSkillsForSend = selectedComposerSkills.filter((skill) =>
      promptIncludesSkillMention(outgoingMessageText, skill.name, selectedProvider),
    );
    const mentionedPluginMentionsForSend = resolvePromptPluginMentions({
      prompt: outgoingMessageText,
      existingMentions: selectedComposerMentions,
      providerPlugins,
    });
    const turnAttachmentsPromise = Promise.all(
      composerAttachmentsSnapshot.map(async (attachment) => ({
        type: attachment.type,
        name: attachment.name,
        mimeType: attachment.mimeType,
        sizeBytes: attachment.sizeBytes,
        dataUrl: await readFileAsDataUrl(attachment.file),
      })),
    );
    const optimisticAttachments = composerAttachmentsSnapshot.map((attachment) => ({
      type: attachment.type,
      id: attachment.id,
      name: attachment.name,
      mimeType: attachment.mimeType,
      sizeBytes: attachment.sizeBytes,
      previewUrl: attachment.previewUrl,
    }));
    setOptimisticUserMessages((existing) => [
      ...existing,
      {
        id: messageIdForSend,
        role: "user",
        text: outgoingMessageText,
        ...(optimisticAttachments.length > 0 ? { attachments: optimisticAttachments } : {}),
        createdAt: messageCreatedAt,
        streaming: false,
        source: "native",
        ...optimisticAuthorFields,
      },
    ]);
    // Sending a message should always bring the latest user turn into view.
    forceStickToBottom();

    setThreadError(threadIdForSend, null);
    if (expiredTerminalContextCount > 0) {
      const toastCopy = buildExpiredTerminalContextToastCopy(
        expiredTerminalContextCount,
        "omitted",
      );
      toastManager.add({
        type: "warning",
        title: toastCopy.title,
        description: toastCopy.description,
      });
    }
    promptRef.current = "";
    clearComposerDraftContent(threadIdForSend);
    setComposerHighlightedItemId(null);
    setComposerCursor(0);
    setComposerTrigger(null);

    let createdServerThreadForLocalDraft = false;
    let turnStartSucceeded = false;
    let nextThreadBranch = activeThread.branch;
    let nextThreadWorktreePath = activeThread.worktreePath;
    await (async () => {
      // On first message: lock in branch + create worktree if needed.
      if (baseBranchForWorktree) {
        beginLocalDispatch({ preparingWorktree: true });
        const result = await createWorktreeMutation.mutateAsync({
          cwd: activeProject.cwd,
          branch: baseBranchForWorktree,
          newBranch: buildTemporaryWorktreeBranchName(),
        });
        nextThreadBranch = result.worktree.branch;
        nextThreadWorktreePath = result.worktree.path;
        // Starting from a stale base looks exactly like starting from a fresh
        // one until the merge conflicts show up, so say it out loud instead of
        // leaving it to the server log.
        if (result.base?.source === "local" && result.base.reason !== "configured-local") {
          toastManager.add({
            type: "warning",
            title: `Se partió de la copia local de "${baseBranchForWorktree}"`,
            description: `${describeWorktreeBaseFallback(result.base.reason)} La rama puede estar por detrás del remoto.`,
          });
        }
        const nextAssociatedWorktree = deriveAssociatedWorktreeMetadata({
          branch: result.worktree.branch,
          worktreePath: result.worktree.path,
        });
        if (isServerThread) {
          await api.orchestration.dispatchCommand({
            type: "thread.meta.update",
            commandId: newCommandId(),
            threadId: threadIdForSend,
            envMode: "worktree",
            branch: result.worktree.branch,
            worktreePath: result.worktree.path,
            associatedWorktreePath: nextAssociatedWorktree.associatedWorktreePath,
            associatedWorktreeBranch: nextAssociatedWorktree.associatedWorktreeBranch,
            associatedWorktreeRef: nextAssociatedWorktree.associatedWorktreeRef,
          });
          // Keep local thread state in sync immediately so terminal drawer opens
          // with the worktree cwd/env instead of briefly using the project root.
          setStoreThreadWorkspace(threadIdForSend, {
            branch: result.worktree.branch,
            worktreePath: result.worktree.path,
            ...nextAssociatedWorktree,
          });
        }
      }

      let firstComposerImageName: string | null = null;
      if (composerAttachmentsSnapshot.length > 0) {
        const firstComposerImage = composerAttachmentsSnapshot[0];
        if (firstComposerImage) {
          firstComposerImageName = firstComposerImage.name;
        }
      }
      let titleSeed = trimmed;
      if (!titleSeed) {
        if (firstComposerImageName) {
          titleSeed = `Image: ${firstComposerImageName}`;
        } else if (composerTerminalContextsSnapshot.length > 0) {
          titleSeed = formatQuotedContextLabel(composerTerminalContextsSnapshot[0]!);
        } else {
          titleSeed = GENERIC_CHAT_THREAD_TITLE;
        }
      }
      // Keep the optimistic label short while the server asks Codex for a better summary.
      const title = buildPromptThreadTitleFallback(titleSeed);
      // Spread-based rebuild so `agentId` (and any future selection field)
      // survives the promotion of a local draft into a real thread. See
      // buildThreadCreateModelSelection for the failure this prevents.
      const threadCreateModelSelection: ModelSelection = buildThreadCreateModelSelection(
        selectedModelSelection,
        selectedModel ||
          activeProject.defaultModelSelection?.model ||
          DEFAULT_MODEL_BY_PROVIDER.codex,
      );

      if (isLocalDraftThread) {
        // The Plane issue rides from the draft into the creation command: this
        // is the moment the thread stops being local, and the only chance to
        // record what it was opened to work.
        const draftPlaneIssue = draftThread?.planeIssue ?? null;
        const draftPullRequest = draftThread?.pullRequest ?? null;
        await api.orchestration.dispatchCommand({
          type: "thread.create",
          commandId: newCommandId(),
          threadId: threadIdForSend,
          projectId: activeProject.id,
          title,
          modelSelection: threadCreateModelSelection,
          runtimeMode: runtimeMode,
          interactionMode: interactionMode,
          envMode: envMode,
          branch: nextThreadBranch,
          worktreePath: nextThreadWorktreePath,
          ...(draftPlaneIssue
            ? { planeIssueRef: draftPlaneIssue.ref, planeIssueId: draftPlaneIssue.id }
            : {}),
          createdAt: activeThread.createdAt,
        });
        // Second command, after the thread exists: unlike the Plane link, which the decider folds
        // into creation, a pull request link is written by `thread.pull-request.set` and nothing
        // else — one writer for one fact, and the same command the panel will use to change it
        // later. It is awaited after `thread.create` because the decider refuses to link a thread
        // it cannot find.
        if (draftPullRequest) {
          await api.orchestration.dispatchCommand({
            type: "thread.pull-request.set",
            commandId: newCommandId(),
            threadId: threadIdForSend,
            pullRequest: draftPullRequest,
            createdAt: activeThread.createdAt,
          });
        }
        createdServerThreadForLocalDraft = true;
      }

      // A thread with a message in it is no longer disposable. Without this,
      // the disposable lifecycle would delete it — worktree included — the
      // moment the user switched to another thread, because it only looks at
      // the temporary flag and never at whether anything was said.
      if (isFirstMessage) {
        promoteThreadFromTemporary(threadIdForSend);
      }

      let setupScript: ProjectScript | null = null;
      if (baseBranchForWorktree) {
        setupScript = setupProjectScript(activeProject.scripts);
      }
      if (setupScript) {
        let shouldRunSetupScript = false;
        if (isServerThread) {
          shouldRunSetupScript = true;
        } else {
          if (createdServerThreadForLocalDraft) {
            shouldRunSetupScript = true;
          }
        }
        if (shouldRunSetupScript) {
          await runSetupProjectScript(setupScript, { worktreePath: nextThreadWorktreePath });
        }
      }

      if (isServerThread) {
        await persistThreadSettingsForNextTurn({
          threadId: threadIdForSend,
          createdAt: messageCreatedAt,
          ...(selectedModel ? { modelSelection: selectedModelSelection } : {}),
          runtimeMode: runtimeMode,
          interactionMode: interactionMode,
        });
      }

      beginLocalDispatch();
      const turnAttachments = await turnAttachmentsPromise;
      await api.orchestration.dispatchCommand({
        type: "thread.turn.start",
        commandId: newCommandId(),
        threadId: threadIdForSend,
        message: {
          messageId: messageIdForSend,
          role: "user",
          text: outgoingMessageText,
          attachments: turnAttachments,
          ...(mentionedSkillsForSend.length > 0 ? { skills: mentionedSkillsForSend } : {}),
          ...(mentionedPluginMentionsForSend.length > 0
            ? { mentions: mentionedPluginMentionsForSend }
            : {}),
        },
        modelSelection: selectedModelSelection,
        ...(providerOptionsForDispatch ? { providerOptions: providerOptionsForDispatch } : {}),
        assistantDeliveryMode: settings.enableAssistantStreaming ? "streaming" : "buffered",
        dispatchMode,
        runtimeMode: runtimeMode,
        interactionMode: interactionMode,
        createdAt: messageCreatedAt,
      });
      turnStartSucceeded = true;
    })().catch(async (err: unknown) => {
      if (createdServerThreadForLocalDraft && !turnStartSucceeded) {
        await api.orchestration
          .dispatchCommand({
            type: "thread.delete",
            commandId: newCommandId(),
            threadId: threadIdForSend,
          })
          .catch(() => undefined);
      }
      if (
        !turnStartSucceeded &&
        promptRef.current.length === 0 &&
        composerAttachmentsRef.current.length === 0 &&
        composerTerminalContextsRef.current.length === 0
      ) {
        setOptimisticUserMessages((existing) => {
          const removed = existing.filter((message) => message.id === messageIdForSend);
          for (const message of removed) {
            revokeUserMessagePreviewUrls(message);
          }
          const next = existing.filter((message) => message.id !== messageIdForSend);
          return next.length === existing.length ? existing : next;
        });
        promptRef.current = promptForSend;
        setPrompt(promptForSend);
        setComposerCursor(collapseExpandedComposerCursor(promptForSend, promptForSend.length));
        addComposerAttachmentsToDraft(composerAttachmentsSnapshot.map(cloneComposerImageForRetry));
        addComposerTerminalContextsToDraft(composerTerminalContextsSnapshot);
        setSelectedComposerSkills(composerSkillsSnapshot);
        setSelectedComposerMentions(composerMentionsSnapshot);
        setComposerTrigger(detectComposerTrigger(promptForSend, promptForSend.length));
      }
      setThreadError(
        threadIdForSend,
        err instanceof Error ? err.message : "Failed to send message.",
      );
    });
    sendInFlightRef.current = false;
    if (!turnStartSucceeded) {
      resetLocalDispatch();
    }
    return turnStartSucceeded;
  };

  const onRespondToApproval = useCallback(
    async (requestId: ApprovalRequestId, decision: ProviderApprovalDecision) => {
      const api = readNativeApi();
      if (!api || !activeThreadId) return;

      setRespondingRequestIds((existing) =>
        existing.includes(requestId) ? existing : [...existing, requestId],
      );
      await api.orchestration
        .dispatchCommand({
          type: "thread.approval.respond",
          commandId: newCommandId(),
          threadId: activeThreadId,
          requestId,
          decision,
          createdAt: new Date().toISOString(),
        })
        .catch((err: unknown) => {
          setStoreThreadError(
            activeThreadId,
            err instanceof Error ? err.message : "Failed to submit approval decision.",
          );
        });
      setRespondingRequestIds((existing) => existing.filter((id) => id !== requestId));
    },
    [activeThreadId, setStoreThreadError],
  );

  const onRespondToUserInput = useCallback(
    async (requestId: ApprovalRequestId, answers: Record<string, unknown>) => {
      const api = readNativeApi();
      if (!api || !activeThreadId) return;

      setRespondingUserInputRequestIds((existing) =>
        existing.includes(requestId) ? existing : [...existing, requestId],
      );
      await api.orchestration
        .dispatchCommand({
          type: "thread.user-input.respond",
          commandId: newCommandId(),
          threadId: activeThreadId,
          requestId,
          answers,
          createdAt: new Date().toISOString(),
        })
        .catch((err: unknown) => {
          setStoreThreadError(
            activeThreadId,
            err instanceof Error ? err.message : "Failed to submit user input.",
          );
        });
      setRespondingUserInputRequestIds((existing) => existing.filter((id) => id !== requestId));
    },
    [activeThreadId, setStoreThreadError],
  );

  const onToggleActivePendingUserInputCollapsed = useCallback(() => {
    if (!activePendingUserInput) {
      return;
    }
    setCollapsedPendingUserInputByRequestId((existing) => ({
      ...existing,
      [activePendingUserInput.requestId]: !(existing[activePendingUserInput.requestId] ?? false),
    }));
  }, [activePendingUserInput]);

  /**
   * Reopen a question from its row in the transcript.
   *
   * The row is the only trace left once the panel is folded away, and it is
   * where the user looks when they wonder what the agent is waiting on.
   */
  const onFocusPendingUserInput = useCallback((requestId: string) => {
    setCollapsedPendingUserInputByRequestId((existing) => ({ ...existing, [requestId]: false }));
    composerFormRef.current?.scrollIntoView({ block: "nearest" });
  }, []);

  const setActivePendingUserInputQuestionIndex = useCallback(
    (nextQuestionIndex: number) => {
      if (!activePendingUserInput) {
        return;
      }
      setPendingUserInputQuestionIndexByRequestId((existing) => ({
        ...existing,
        [activePendingUserInput.requestId]: nextQuestionIndex,
      }));
    },
    [activePendingUserInput],
  );

  const onToggleActivePendingUserInputOption = useCallback(
    (questionId: string, optionLabel: string) => {
      if (!activePendingUserInput) {
        return;
      }
      const question = activePendingUserInput.questions.find((entry) => entry.id === questionId);
      if (!question) {
        return;
      }
      setPendingUserInputAnswersByRequestId((existing) => ({
        ...existing,
        [activePendingUserInput.requestId]: {
          ...existing[activePendingUserInput.requestId],
          [questionId]: togglePendingUserInputOptionSelection(
            question,
            existing[activePendingUserInput.requestId]?.[questionId],
            optionLabel,
          ),
        },
      }));
      promptRef.current = "";
      setComposerCursor(0);
      setComposerTrigger(null);
    },
    [activePendingUserInput],
  );

  const onChangeActivePendingUserInputCustomAnswer = useCallback(
    (
      questionId: string,
      value: string,
      nextCursor: number,
      expandedCursor: number,
      cursorAdjacentToMention: boolean,
    ) => {
      if (!activePendingUserInput) {
        return;
      }
      promptRef.current = value;
      setPendingUserInputAnswersByRequestId((existing) => ({
        ...existing,
        [activePendingUserInput.requestId]: {
          ...existing[activePendingUserInput.requestId],
          [questionId]: setPendingUserInputCustomAnswer(
            existing[activePendingUserInput.requestId]?.[questionId],
            value,
          ),
        },
      }));
      setComposerCursor(nextCursor);
      setComposerTrigger(
        cursorAdjacentToMention ? null : detectComposerTrigger(value, expandedCursor),
      );
    },
    [activePendingUserInput],
  );

  const onAdvanceActivePendingUserInput = useCallback(() => {
    if (!activePendingUserInput || !activePendingProgress) {
      return;
    }
    if (activePendingProgress.isLastQuestion) {
      if (activePendingResolvedAnswers) {
        void onRespondToUserInput(activePendingUserInput.requestId, activePendingResolvedAnswers);
      }
      return;
    }
    setActivePendingUserInputQuestionIndex(activePendingProgress.questionIndex + 1);
  }, [
    activePendingProgress,
    activePendingResolvedAnswers,
    activePendingUserInput,
    onRespondToUserInput,
    setActivePendingUserInputQuestionIndex,
  ]);

  const onPreviousActivePendingUserInputQuestion = useCallback(() => {
    if (!activePendingProgress) {
      return;
    }
    setActivePendingUserInputQuestionIndex(Math.max(activePendingProgress.questionIndex - 1, 0));
  }, [activePendingProgress, setActivePendingUserInputQuestionIndex]);

  async function onSubmitPlanFollowUp({
    text,
    interactionMode: nextInteractionMode,
    dispatchMode,
  }: {
    text: string;
    interactionMode: "default" | "plan";
    dispatchMode: "queue" | "steer";
  }): Promise<boolean> {
    const api = readNativeApi();
    if (
      !api ||
      !activeThread ||
      !isServerThread ||
      isSendBusy ||
      isConnecting ||
      sendInFlightRef.current
    ) {
      return false;
    }

    const trimmed = text.trim();
    if (!trimmed) {
      return false;
    }

    const threadIdForSend = activeThread.id;
    const messageIdForSend = newMessageId();
    const messageCreatedAt = new Date().toISOString();
    const outgoingMessageText = formatOutgoingPrompt({
      provider: selectedProvider,
      model: selectedModel,
      effort: selectedPromptEffort,
      text: trimmed,
    });

    sendInFlightRef.current = true;
    beginLocalDispatch();
    setThreadError(threadIdForSend, null);
    setOptimisticUserMessages((existing) => [
      ...existing,
      {
        id: messageIdForSend,
        role: "user",
        text: outgoingMessageText,
        createdAt: messageCreatedAt,
        streaming: false,
        source: "native",
        ...optimisticAuthorFields,
      },
    ]);
    forceStickToBottom();

    try {
      await persistThreadSettingsForNextTurn({
        threadId: threadIdForSend,
        createdAt: messageCreatedAt,
        modelSelection: selectedModelSelection,
        runtimeMode: runtimeMode,
        interactionMode: nextInteractionMode,
      });

      // Keep the mode toggle and plan-follow-up banner in sync immediately
      // while the same-thread implementation turn is starting.
      setComposerDraftInteractionMode(threadIdForSend, nextInteractionMode);

      await api.orchestration.dispatchCommand({
        type: "thread.turn.start",
        commandId: newCommandId(),
        threadId: threadIdForSend,
        message: {
          messageId: messageIdForSend,
          role: "user",
          text: outgoingMessageText,
          attachments: [],
        },
        modelSelection: selectedModelSelection,
        ...(providerOptionsForDispatch ? { providerOptions: providerOptionsForDispatch } : {}),
        assistantDeliveryMode: settings.enableAssistantStreaming ? "streaming" : "buffered",
        dispatchMode,
        runtimeMode,
        interactionMode: nextInteractionMode,
        ...(nextInteractionMode === "default" && activeProposedPlan
          ? {
              sourceProposedPlan: {
                threadId: activeThread.id,
                planId: activeProposedPlan.id,
              },
            }
          : {}),
        createdAt: messageCreatedAt,
      });
      // Optimistically open the plan sidebar when implementing (not refining).
      // "default" mode here means the agent is executing the plan, which produces
      // step-tracking activities that the sidebar will display.
      if (nextInteractionMode === "default") {
        planSidebarDismissedForTurnRef.current = null;
        setPlanSidebarOpen(true);
      }
      sendInFlightRef.current = false;
      return true;
    } catch (err) {
      setOptimisticUserMessages((existing) =>
        existing.filter((message) => message.id !== messageIdForSend),
      );
      setThreadError(
        threadIdForSend,
        err instanceof Error ? err.message : "Failed to send plan follow-up.",
      );
      sendInFlightRef.current = false;
      resetLocalDispatch();
      return false;
    }
  }

  /**
   * Sends a canned prompt (a project action of kind "chat") as a user turn on
   * the active thread.
   *
   * Deliberately never reads or clears the composer: the user may be mid-draft
   * when they fire the action, and losing that draft would be worse than the
   * action itself. If a turn is already running the command still goes out with
   * `dispatchMode: "queue"` — the decider parks it as `thread.turn-queued` and
   * the provider reactor dispatches it when the live turn ends.
   */
  async function sendPredefinedPrompt(text: string): Promise<boolean> {
    const api = readNativeApi();
    if (
      !api ||
      !activeThread ||
      !isServerThread ||
      isSendBusy ||
      isConnecting ||
      sendInFlightRef.current
    ) {
      return false;
    }

    const trimmed = text.trim();
    if (!trimmed) {
      return false;
    }

    const threadIdForSend = activeThread.id;
    const messageIdForSend = newMessageId();
    const messageCreatedAt = new Date().toISOString();
    const outgoingMessageText = formatOutgoingPrompt({
      provider: selectedProvider,
      model: selectedModel,
      effort: selectedPromptEffort,
      text: trimmed,
    });

    sendInFlightRef.current = true;
    beginLocalDispatch();
    setThreadError(threadIdForSend, null);
    setOptimisticUserMessages((existing) => [
      ...existing,
      {
        id: messageIdForSend,
        role: "user",
        text: outgoingMessageText,
        createdAt: messageCreatedAt,
        streaming: false,
        source: "native",
        ...optimisticAuthorFields,
      },
    ]);
    forceStickToBottom();

    try {
      await persistThreadSettingsForNextTurn({
        threadId: threadIdForSend,
        createdAt: messageCreatedAt,
        modelSelection: selectedModelSelection,
        runtimeMode,
        interactionMode,
      });

      await api.orchestration.dispatchCommand({
        type: "thread.turn.start",
        commandId: newCommandId(),
        threadId: threadIdForSend,
        message: {
          messageId: messageIdForSend,
          role: "user",
          text: outgoingMessageText,
          attachments: [],
        },
        modelSelection: selectedModelSelection,
        ...(providerOptionsForDispatch ? { providerOptions: providerOptionsForDispatch } : {}),
        assistantDeliveryMode: settings.enableAssistantStreaming ? "streaming" : "buffered",
        dispatchMode: "queue",
        runtimeMode,
        interactionMode,
        createdAt: messageCreatedAt,
      });
      sendInFlightRef.current = false;
      return true;
    } catch (err) {
      setOptimisticUserMessages((existing) =>
        existing.filter((message) => message.id !== messageIdForSend),
      );
      setThreadError(
        threadIdForSend,
        err instanceof Error ? err.message : "Failed to send the action prompt.",
      );
      sendInFlightRef.current = false;
      resetLocalDispatch();
      return false;
    }
  }

  const onSendRef = useRef(onSend);
  const onSubmitPlanFollowUpRef = useRef(onSubmitPlanFollowUp);
  onSendRef.current = onSend;
  onSubmitPlanFollowUpRef.current = onSubmitPlanFollowUp;

  // The composer queue is drained by the server (`ComposerQueueReactor`), never from here. A
  // client-side auto-dispatch would race it and send the same message twice.

  const onSetQueuedMessageAutoSend = useCallback(
    (queuedId: string, autoSend: boolean) => {
      void setComposerQueueAutoSend(threadId, queuedId, autoSend).catch(() => undefined);
    },
    [setComposerQueueAutoSend, threadId],
  );

  const onRemoveQueuedMessage = useCallback(
    (queuedId: string) => {
      void removeComposerQueueMessage(threadId, queuedId).catch(() => undefined);
    },
    [removeComposerQueueMessage, threadId],
  );

  const sendQueuedMessage = useCallback(
    async (queuedId: string, dispatchMode: "queue" | "steer") => {
      try {
        // "queue" lets the server decide: it dispatches on an idle thread and flips the row to
        // auto-send on a busy one. The client never has to guess which.
        const result = await sendComposerQueueMessage(threadId, queuedId, dispatchMode);
        if (result.outcome === "failed") {
          toastManager.add({
            type: "error",
            title: "Could not send the queued message",
            description: result.message ?? "The server rejected the dispatch.",
          });
        }
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Could not send the queued message",
          description: error instanceof Error ? error.message : "The request failed.",
        });
      }
    },
    [sendComposerQueueMessage, threadId],
  );

  const onSendQueuedMessage = useCallback(
    (queuedId: string) => {
      void sendQueuedMessage(queuedId, "queue");
    },
    [sendQueuedMessage],
  );

  const onSteerQueuedMessage = useCallback(
    (queuedId: string) => {
      void sendQueuedMessage(queuedId, "steer");
    },
    [sendQueuedMessage],
  );

  const onEditQueuedMessage = useCallback(
    (item: ComposerQueuedMessage) => {
      void (async () => {
        let restoredImages: ComposerAttachment[];
        try {
          // Download first, delete second. The row is the only copy of the message, so a failed
          // fetch must leave it exactly where it was.
          restoredImages = await downloadQueuedAttachments(item);
        } catch (error) {
          toastManager.add({
            type: "error",
            title: "Could not open the queued message",
            description:
              error instanceof Error
                ? `${error.message} It is still in the queue.`
                : "Its attachments could not be downloaded. It is still in the queue.",
          });
          return;
        }
        try {
          await removeComposerQueueMessage(threadId, item.queuedId);
        } catch (error) {
          for (const image of restoredImages) {
            revokeBlobPreviewUrl(image.previewUrl);
          }
          toastManager.add({
            type: "error",
            title: "Could not open the queued message",
            description:
              error instanceof Error
                ? `${error.message} It is still in the queue.`
                : "It is still in the queue.",
          });
          return;
        }
        setQueuedMessagesOpen(false);
        restoreQueuedMessageToComposer(item, restoredImages);
      })();
    },
    [
      downloadQueuedAttachments,
      removeComposerQueueMessage,
      restoreQueuedMessageToComposer,
      threadId,
    ],
  );

  const onSaveComposerToQueue = useCallback((): boolean => {
    if (!composerSendState.hasSendableContent) {
      toastManager.add({
        type: "warning",
        title: "Nothing to queue",
        description: "Write a message first.",
      });
      return false;
    }
    if (showPlanFollowUpPrompt && activeProposedPlan) {
      const followUp = resolvePlanFollowUpSubmission({
        draftText: composerSendState.trimmedPrompt,
        planMarkdown: activeProposedPlan.planMarkdown,
      });
      void queueComposerMessage({
        kind: "plan-follow-up",
        text: followUp.text,
        interactionMode: followUp.interactionMode,
      });
      return true;
    }
    void queueComposerMessage({
      kind: "chat",
      prompt: promptRef.current,
      trimmedPrompt: composerSendState.trimmedPrompt,
      attachments: composerAttachments,
      quotedContexts: composerSendState.sendableQuotedContexts,
    });
    return true;
  }, [
    activeProposedPlan,
    composerAttachments,
    composerSendState.hasSendableContent,
    composerSendState.sendableQuotedContexts,
    composerSendState.trimmedPrompt,
    queueComposerMessage,
    showPlanFollowUpPrompt,
  ]);

  const onSaveComposerToQueueRef = useRef(onSaveComposerToQueue);
  onSaveComposerToQueueRef.current = onSaveComposerToQueue;

  // The queue is per (person, thread), so it is fetched per thread and only for threads the
  // server knows about — a local draft has nowhere to keep one.
  useEffect(() => {
    if (!isServerThread || threadId.length === 0) {
      return;
    }
    void loadComposerQueue(threadId);
  }, [isServerThread, loadComposerQueue, threadId]);

  const onImplementPlanInNewThread = useCallback(async () => {
    const api = readNativeApi();
    if (
      !api ||
      !activeThread ||
      !activeProject ||
      !activeProposedPlan ||
      !isServerThread ||
      isSendBusy ||
      isConnecting ||
      sendInFlightRef.current
    ) {
      return;
    }

    const createdAt = new Date().toISOString();
    const nextThreadId = newThreadId();
    const planMarkdown = activeProposedPlan.planMarkdown;
    const implementationPrompt = buildPlanImplementationPrompt(planMarkdown);
    const outgoingImplementationPrompt = formatOutgoingPrompt({
      provider: selectedProvider,
      model: selectedModel,
      effort: selectedPromptEffort,
      text: implementationPrompt,
    });
    const nextThreadTitle = truncateTitle(buildPlanImplementationThreadTitle(planMarkdown));
    const nextThreadModelSelection: ModelSelection = selectedModelSelection;

    sendInFlightRef.current = true;
    beginLocalDispatch();
    const finish = () => {
      sendInFlightRef.current = false;
      resetLocalDispatch();
    };

    await api.orchestration
      .dispatchCommand({
        type: "thread.create",
        commandId: newCommandId(),
        threadId: nextThreadId,
        projectId: activeProject.id,
        title: nextThreadTitle,
        modelSelection: nextThreadModelSelection,
        runtimeMode,
        interactionMode: "default",
        envMode: activeThread.envMode ?? (activeThread.worktreePath ? "worktree" : "local"),
        branch: activeThread.branch,
        worktreePath: activeThread.worktreePath,
        associatedWorktreePath: activeThreadAssociatedWorktree.associatedWorktreePath,
        associatedWorktreeBranch: activeThreadAssociatedWorktree.associatedWorktreeBranch,
        associatedWorktreeRef: activeThreadAssociatedWorktree.associatedWorktreeRef,
        createdAt,
      })
      .then(() => {
        return api.orchestration.dispatchCommand({
          type: "thread.turn.start",
          commandId: newCommandId(),
          threadId: nextThreadId,
          message: {
            messageId: newMessageId(),
            role: "user",
            text: outgoingImplementationPrompt,
            attachments: [],
          },
          modelSelection: selectedModelSelection,
          ...(providerOptionsForDispatch ? { providerOptions: providerOptionsForDispatch } : {}),
          assistantDeliveryMode: settings.enableAssistantStreaming ? "streaming" : "buffered",
          dispatchMode: "queue",
          runtimeMode,
          interactionMode: "default",
          createdAt,
        });
      })
      .then(() => api.orchestration.getSnapshot())
      .then((snapshot) => {
        syncServerReadModel(snapshot);
        // Signal that the plan sidebar should open on the new thread.
        planSidebarOpenOnNextThreadRef.current = true;
        return navigate({
          to: "/$threadId",
          params: { threadId: nextThreadId },
        });
      })
      .catch(async (err) => {
        await api.orchestration
          .dispatchCommand({
            type: "thread.delete",
            commandId: newCommandId(),
            threadId: nextThreadId,
          })
          .catch(() => undefined);
        await api.orchestration
          .getSnapshot()
          .then((snapshot) => {
            syncServerReadModel(snapshot);
          })
          .catch(() => undefined);
        toastManager.add({
          type: "error",
          title: "Could not start implementation thread",
          description:
            err instanceof Error ? err.message : "An error occurred while creating the new thread.",
        });
      })
      .then(finish, finish);
  }, [
    activeProject,
    activeProposedPlan,
    activeThread,
    activeThreadAssociatedWorktree,
    beginLocalDispatch,
    isConnecting,
    isSendBusy,
    isServerThread,
    navigate,
    resetLocalDispatch,
    runtimeMode,
    selectedPromptEffort,
    selectedModelSelection,
    providerOptionsForDispatch,
    selectedProvider,
    settings.enableAssistantStreaming,
    syncServerReadModel,
    selectedModel,
  ]);

  const onProviderModelSelect = useCallback(
    (provider: ProviderKind, model: ModelSlug, agentId?: ManagedAgentId) => {
      if (!activeThread) return;
      if (lockedProvider !== null && provider !== lockedProvider) {
        scheduleComposerFocus();
        return;
      }
      const resolvedModel = resolveAppModelSelection(provider, customModelsByProvider, model);
      // `agentId` is only ever set when the caller picked an agent — picking a
      // plain model must not carry a previously-selected agent forward, so the
      // key is present only in the agent case, never `agentId: undefined`.
      //
      // Picking an agent also takes over the traits: its pinned effort travels
      // with the selection, and the composer's own effort controls go read-only.
      // The server imposes the same block anyway, so computing it here through
      // the shared builder is what keeps the two from disagreeing and dispatching
      // a `thread.meta.update` before every turn.
      const pickedAgent = agentId
        ? (visibleManagedAgents.find((candidate) => candidate.id === agentId) ?? null)
        : null;
      const nextModelSelection: ModelSelection = pickedAgent
        ? buildAgentModelSelection({ ...pickedAgent, model: resolvedModel })
        : {
            provider,
            model: resolvedModel,
            ...(agentId ? { agentId } : {}),
          };
      setComposerDraftModelSelection(activeThread.id, nextModelSelection);
      setStickyComposerModelSelection(nextModelSelection);
      scheduleComposerFocus();
    },
    [
      activeThread,
      lockedProvider,
      scheduleComposerFocus,
      setComposerDraftModelSelection,
      setStickyComposerModelSelection,
      customModelsByProvider,
      visibleManagedAgents,
    ],
  );
  const setPromptFromTraits = useCallback(
    (nextPrompt: string) => {
      const currentPrompt = promptRef.current;
      if (nextPrompt === currentPrompt) {
        scheduleComposerFocus();
        return;
      }
      promptRef.current = nextPrompt;
      setPrompt(nextPrompt);
      const nextCursor = collapseExpandedComposerCursor(nextPrompt, nextPrompt.length);
      setComposerCursor(nextCursor);
      setComposerTrigger(detectComposerTrigger(nextPrompt, nextPrompt.length));
      scheduleComposerFocus();
    },
    [scheduleComposerFocus, setPrompt],
  );
  const selectedProviderModelOptions = composerModelOptions?.[selectedProvider];
  const composerTraitSelection = getComposerTraitSelection(
    selectedProvider,
    selectedModel,
    prompt,
    selectedProviderModelOptions,
  );
  // When a managed agent is selected its definition owns the traits, so the
  // picker goes read-only and says whose they are. Resolved from the visible
  // roster rather than carried on the selection: the selection only stores the
  // id, and the person needs the name.
  const traitsPinnedByAgentName = selectedAgentId
    ? (visibleManagedAgents.find((agent) => agent.id === selectedAgentId)?.name ?? null)
    : null;
  const providerTraitsPicker = renderProviderTraitsPicker({
    provider: selectedProvider,
    threadId,
    model: selectedModel,
    modelOptions: selectedProviderModelOptions,
    prompt,
    includeFastMode: false,
    pinnedByAgentName: traitsPinnedByAgentName,
    open: isTraitsPickerOpen,
    onOpenChange: handleTraitsPickerOpenChange,
    shortcutLabel: traitsPickerShortcutLabel,
    onPromptChange: setPromptFromTraits,
  });
  const toggleFastMode = useCallback(() => {
    if (!composerTraitSelection.caps.supportsFastMode) {
      scheduleComposerFocus();
      return;
    }
    setComposerDraftProviderModelOptions(
      threadId,
      selectedProvider,
      buildNextProviderOptions(selectedProvider, selectedProviderModelOptions, {
        fastMode: !composerTraitSelection.fastModeEnabled,
      }),
      { persistSticky: true },
    );
    scheduleComposerFocus();
  }, [
    composerTraitSelection.caps.supportsFastMode,
    composerTraitSelection.fastModeEnabled,
    scheduleComposerFocus,
    selectedProvider,
    selectedProviderModelOptions,
    setComposerDraftProviderModelOptions,
    threadId,
  ]);
  const onEnvModeChange = useCallback(
    (mode: DraftThreadEnvMode) => {
      const nextBranch =
        mode === "worktree"
          ? (activeThread?.branch ?? draftThread?.branch ?? activeRootBranch ?? null)
          : (activeThread?.branch ?? draftThread?.branch ?? null);
      if (isLocalDraftThread) {
        setDraftThreadContext(threadId, {
          envMode: mode,
          ...(nextBranch ? { branch: nextBranch } : {}),
        });
      }
      if (isServerThread && activeThread && !hasNativeUserMessages && !activeThread.session) {
        const api = readNativeApi();
        if (api) {
          void api.orchestration.dispatchCommand({
            type: "thread.meta.update",
            commandId: newCommandId(),
            threadId,
            envMode: mode,
            ...(nextBranch ? { branch: nextBranch } : {}),
            ...(mode === "local" ? { worktreePath: null } : {}),
          });
        }
      }
      scheduleComposerFocus();
    },
    [
      activeThread,
      activeRootBranch,
      draftThread?.branch,
      hasNativeUserMessages,
      isLocalDraftThread,
      isServerThread,
      scheduleComposerFocus,
      setDraftThreadContext,
      threadId,
    ],
  );

  const applyPromptReplacement = useCallback(
    (
      rangeStart: number,
      rangeEnd: number,
      replacement: string,
      options?: { expectedText?: string; cursorOffset?: number },
    ): number | false => {
      const currentText = promptRef.current;
      const safeStart = Math.max(0, Math.min(currentText.length, rangeStart));
      const safeEnd = Math.max(safeStart, Math.min(currentText.length, rangeEnd));
      if (
        options?.expectedText !== undefined &&
        currentText.slice(safeStart, safeEnd) !== options.expectedText
      ) {
        return false;
      }
      const next = replaceTextRange(promptRef.current, rangeStart, rangeEnd, replacement);
      let nextCursor = collapseExpandedComposerCursor(next.text, next.cursor);
      // Apply cursor offset if specified (e.g., -1 to position inside parentheses)
      if (options?.cursorOffset !== undefined) {
        nextCursor = Math.max(0, nextCursor + options.cursorOffset);
      }
      promptRef.current = next.text;
      const activePendingQuestion = activePendingProgress?.activeQuestion;
      if (activePendingQuestion && activePendingUserInput) {
        setPendingUserInputAnswersByRequestId((existing) => ({
          ...existing,
          [activePendingUserInput.requestId]: {
            ...existing[activePendingUserInput.requestId],
            [activePendingQuestion.id]: setPendingUserInputCustomAnswer(
              existing[activePendingUserInput.requestId]?.[activePendingQuestion.id],
              next.text,
            ),
          },
        }));
      } else {
        setPrompt(next.text);
      }
      setComposerCursor(nextCursor);
      setComposerTrigger(
        detectComposerTrigger(next.text, expandCollapsedComposerCursor(next.text, nextCursor)),
      );
      window.requestAnimationFrame(() => {
        composerEditorRef.current?.focusAt(nextCursor);
      });
      return nextCursor;
    },
    [activePendingProgress?.activeQuestion, activePendingUserInput, setPrompt],
  );

  const readComposerSnapshot = useCallback((): {
    value: string;
    cursor: number;
    expandedCursor: number;
    terminalContextIds: string[];
  } => {
    const editorSnapshot = composerEditorRef.current?.readSnapshot();
    if (editorSnapshot) {
      return editorSnapshot;
    }
    return {
      value: promptRef.current,
      cursor: composerCursor,
      expandedCursor: expandCollapsedComposerCursor(promptRef.current, composerCursor),
      terminalContextIds: composerQuotedContexts.map((context) => context.id),
    };
  }, [composerCursor, composerQuotedContexts]);

  const resolveActiveComposerTrigger = useCallback((): {
    snapshot: { value: string; cursor: number; expandedCursor: number };
    trigger: ComposerTrigger | null;
  } => {
    const snapshot = readComposerSnapshot();
    return {
      snapshot,
      trigger: detectComposerTrigger(snapshot.value, snapshot.expandedCursor),
    };
  }, [readComposerSnapshot]);

  const setComposerPromptValue = useCallback(
    (nextPrompt: string) => {
      promptRef.current = nextPrompt;
      setPrompt(nextPrompt);
      const nextCursor = collapseExpandedComposerCursor(nextPrompt, nextPrompt.length);
      setComposerCursor(nextCursor);
      setComposerTrigger(detectComposerTrigger(nextPrompt, nextPrompt.length));
      setComposerHighlightedItemId(null);
      window.requestAnimationFrame(() => {
        composerEditorRef.current?.focusAt(nextCursor);
      });
    },
    [setPrompt],
  );

  /**
   * Single entry point for running a project action, whatever its kind.
   *
   * Declared as a function (not a `useCallback`) because it has to reach both
   * backwards — the global keydown handler wired far above — and forwards to
   * `setComposerPromptValue`; callers reach it through
   * `runProjectScriptActionRef` so they keep a stable identity.
   */
  function runProjectScriptAction(script: ProjectScript): void {
    // The keybindings reach this without passing through the control, so the
    // grant is checked here too — a shortcut that still fired an action the
    // header no longer offers would be the same hole with extra steps.
    if (!uiCapabilities.canRunActions) return;
    if (script.kind === "chat" || script.kind === "compose") {
      rememberLastInvokedScript(script);
    }
    if (script.kind === "chat") {
      // A draft thread has no server-side thread to start a turn on. Dropping
      // the prompt on the floor would look like a broken button, so it lands in
      // the composer instead and the user's first send creates the thread.
      if (!isServerThread) {
        setComposerPromptValue(script.command);
        toastManager.add({
          type: "warning",
          title: "Send a first message to start this conversation",
          description: `"${script.name}" was placed in the composer instead.`,
        });
        return;
      }
      void sendPredefinedPrompt(script.command);
      return;
    }
    if (script.kind === "compose") {
      // Replaces whatever the composer holds — same contract as every other
      // programmatic composer fill in this view.
      setComposerPromptValue(script.command);
      return;
    }
    if (script.runMode === "background") {
      void runProjectScriptInBackground(script);
      return;
    }
    void runProjectScript(script);
  }

  /**
   * User-initiated background run: same server call as the automatic setup
   * path, plus the two things only a manual invocation needs — the draft-thread
   * guard and remembering the action as the last one invoked.
   */
  async function runProjectScriptInBackground(script: ProjectScript): Promise<void> {
    if (!activeProject || !activeThreadId) return;
    // The server needs a thread row to resolve the worktree; a draft thread has
    // none yet, and "thread not found" is not an answer the user can act on.
    if (!isServerThread) {
      toastManager.add({
        type: "warning",
        title: "Send a first message before running background actions",
        description: `"${script.name}" needs a started conversation to run in.`,
      });
      return;
    }
    rememberLastInvokedScript(script);
    await runProjectScriptInBackgroundFor({
      script,
      threadId: activeThreadId,
      projectId: activeProject.id,
    });
  }

  const runProjectScriptActionRef = useRef(runProjectScriptAction);
  runProjectScriptActionRef.current = runProjectScriptAction;

  const clearComposerSlashDraft = useCallback(() => {
    promptRef.current = "";
    clearComposerDraftContent(threadId);
    setComposerHighlightedItemId(null);
    setComposerCursor(0);
    setComposerTrigger(null);
    scheduleComposerFocus();
  }, [clearComposerDraftContent, scheduleComposerFocus, threadId]);

  const slashEditorActions = useMemo(
    () => ({
      resolveActiveComposerTrigger,
      applyPromptReplacement,
      extendReplacementRangeForTrailingSpace,
      clearComposerSlashDraft,
      setComposerPromptValue,
      scheduleComposerFocus,
      setComposerHighlightedItemId,
    }),
    [
      applyPromptReplacement,
      clearComposerSlashDraft,
      resolveActiveComposerTrigger,
      scheduleComposerFocus,
      setComposerPromptValue,
    ],
  );

  const {
    compactActiveThread,
    handleForkTargetSelection,
    handleReviewTargetSelection,
    isSlashStatusDialogOpen,
    setIsSlashStatusDialogOpen,
    handleStandaloneSlashCommand,
    handleSlashCommandSelection,
    handleProviderNativeCommandSelection,
  } = useComposerSlashCommands({
    activeProject,
    activeThread,
    activeRootBranch,
    isServerThread,
    supportsFastSlashCommand,
    canOfferCompactCommand,
    supportsTextNativeReviewCommand,
    fastModeEnabled,
    providerNativeCommands,
    providerCommandDiscoveryCwd: composerSkillCwd,
    selectedProvider,
    currentProviderModelOptions,
    selectedModelSelection,
    runtimeMode,
    interactionMode,
    threadId,
    syncServerReadModel,
    navigateToThread: (nextThreadId) =>
      navigate({
        to: "/$threadId",
        params: { threadId: nextThreadId },
      }),
    handleClearConversation: clearActiveThreadContext,
    handleInteractionModeChange,
    openForkTargetPicker: () => {
      setComposerCommandPicker("fork-target");
      setComposerHighlightedItemId("fork-target:worktree");
    },
    openReviewTargetPicker: () => {
      setComposerCommandPicker("review-target");
      setComposerHighlightedItemId("review-target:changes");
    },
    setComposerDraftProviderModelOptions,
    editorActions: slashEditorActions,
  });

  const onSelectComposerItem = useCallback(
    (item: ComposerCommandItem) => {
      if (composerSelectLockRef.current) return;
      composerSelectLockRef.current = true;
      window.requestAnimationFrame(() => {
        composerSelectLockRef.current = false;
      });
      if (item.type === "fork-target") {
        setComposerCommandPicker(null);
        setComposerHighlightedItemId(null);
        void handleForkTargetSelection(item.target);
        return;
      }
      if (item.type === "review-target") {
        setComposerCommandPicker(null);
        setComposerHighlightedItemId(null);
        void handleReviewTargetSelection(item.target);
        return;
      }
      const { snapshot, trigger } = resolveActiveComposerTrigger();
      if (!trigger) return;
      if (item.type === "path") {
        const replacement = `@${item.path} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied !== false) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "slash-command") {
        handleSlashCommandSelection(item);
        return;
      }
      if (item.type === "provider-native-command") {
        // Our layer gets the first look: a native `/clear` (or one of its
        // aliases) runs our confirmed in-place clear, and a native `/compact`
        // runs our compaction request, instead of being typed into the composer
        // for the provider to interpret.
        if (handleProviderNativeCommandSelection(item)) {
          return;
        }
        const replacement = `/${item.command} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied !== false) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "skill") {
        const replacement = `${skillMentionPrefix(selectedProvider)}${item.skill.name} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied !== false) {
          setSelectedComposerSkills((existing) => {
            const nextSkill = {
              name: item.skill.name,
              path: item.skill.path,
            } satisfies ProviderSkillReference;
            return existing.some(
              (skill) => skill.name === nextSkill.name && skill.path === nextSkill.path,
            )
              ? existing
              : [...existing, nextSkill];
          });
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "plugin") {
        const replacement = `@${item.plugin.name} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied !== false) {
          setSelectedComposerMentions((existing) => {
            const nextMention = item.mention;
            const nextWithoutSameName = existing.filter(
              (mention) => mention.name !== nextMention.name,
            );
            return [...nextWithoutSameName, nextMention];
          });
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "model") {
        onProviderModelSelect(item.provider, item.model);
        const applied = applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, "", {
          expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
        });
        if (applied !== false) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "agent") {
        // Insert @alias() and position cursor inside parentheses
        const replacement = `@${item.alias}()`;
        const applied = applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, replacement, {
          expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
          cursorOffset: -1, // Move cursor back 1 to be inside the parentheses
        });
        if (applied !== false) {
          setComposerHighlightedItemId(null);
        }
      }
    },
    [
      applyPromptReplacement,
      composerCursor,
      handleForkTargetSelection,
      handleReviewTargetSelection,
      handleSlashCommandSelection,
      handleProviderNativeCommandSelection,
      onProviderModelSelect,
      setComposerCommandPicker,
      selectedProvider,
      setSelectedComposerMentions,
      setSelectedComposerSkills,
      resolveActiveComposerTrigger,
    ],
  );
  const onComposerMenuItemHighlighted = useCallback((itemId: string | null) => {
    setComposerHighlightedItemId(itemId);
  }, []);
  const nudgeComposerMenuHighlight = useCallback(
    (key: "ArrowDown" | "ArrowUp") => {
      if (composerMenuItems.length === 0) {
        return;
      }
      const highlightedIndex = composerMenuItems.findIndex(
        (item) => item.id === composerHighlightedItemId,
      );
      const normalizedIndex =
        highlightedIndex >= 0 ? highlightedIndex : key === "ArrowDown" ? -1 : 0;
      const offset = key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        (normalizedIndex + offset + composerMenuItems.length) % composerMenuItems.length;
      const nextItem = composerMenuItems[nextIndex];
      setComposerHighlightedItemId(nextItem?.id ?? null);
    },
    [composerHighlightedItemId, composerMenuItems],
  );
  const isComposerMenuLoading =
    (composerTriggerKind === "mention" &&
      ((mentionTriggerQuery.length > 0 && composerPathQueryDebouncer.state.isPending) ||
        workspaceEntriesQuery.isLoading ||
        workspaceEntriesQuery.isFetching ||
        providerPluginsQuery.isLoading ||
        providerPluginsQuery.isFetching)) ||
    (composerTriggerKind === "slash-command" &&
      (providerCommandsQuery.isLoading || providerCommandsQuery.isFetching));

  const onPromptChange = useCallback(
    (
      nextPrompt: string,
      nextCursor: number,
      expandedCursor: number,
      cursorAdjacentToMention: boolean,
      terminalContextIds: string[],
    ) => {
      if (activePendingProgress?.activeQuestion && activePendingUserInput) {
        onChangeActivePendingUserInputCustomAnswer(
          activePendingProgress.activeQuestion.id,
          nextPrompt,
          nextCursor,
          expandedCursor,
          cursorAdjacentToMention,
        );
        return;
      }
      promptRef.current = nextPrompt;
      setPrompt(nextPrompt);
      if (composerCommandPicker !== null && nextPrompt.trim().length > 0) {
        setComposerCommandPicker(null);
      }
      if (!terminalContextIdListsEqual(composerQuotedContexts, terminalContextIds)) {
        setComposerDraftTerminalContexts(
          threadId,
          syncTerminalContextsByIds(composerQuotedContexts, terminalContextIds),
        );
      }
      setComposerCursor(nextCursor);
      setComposerTrigger(
        cursorAdjacentToMention ? null : detectComposerTrigger(nextPrompt, expandedCursor),
      );
    },
    [
      activePendingProgress?.activeQuestion,
      activePendingUserInput,
      composerQuotedContexts,
      composerCommandPicker,
      onChangeActivePendingUserInputCustomAnswer,
      setPrompt,
      setComposerDraftTerminalContexts,
      setComposerCommandPicker,
      threadId,
    ],
  );

  const onComposerCommandKey = (
    key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
    event: KeyboardEvent,
  ) => {
    if (key === "Tab" && event.shiftKey) {
      toggleInteractionMode();
      return true;
    }

    const { trigger } = resolveActiveComposerTrigger();
    const menuIsActive = composerMenuOpenRef.current || trigger !== null;

    if (menuIsActive) {
      const currentItems = composerMenuItemsRef.current;
      if (key === "ArrowDown" && currentItems.length > 0) {
        nudgeComposerMenuHighlight("ArrowDown");
        return true;
      }
      if (key === "ArrowUp" && currentItems.length > 0) {
        nudgeComposerMenuHighlight("ArrowUp");
        return true;
      }
      if (key === "Tab" || key === "Enter") {
        const selectedItem = activeComposerMenuItemRef.current ?? currentItems[0];
        if (selectedItem) {
          onSelectComposerItem(selectedItem);
          return true;
        }
      }
    }

    if (key === "Enter" && !event.shiftKey) {
      void onSend(undefined, event.metaKey || event.ctrlKey ? "steer" : "queue");
      return true;
    }
    return false;
  };
  const onToggleWorkGroup = useCallback((groupId: string) => {
    setExpandedWorkGroups((existing) => ({
      ...existing,
      [groupId]: !existing[groupId],
    }));
  }, []);
  const onToggleWorkEntry = useCallback((entryId: string) => {
    setExpandedWorkEntries((existing) => ({
      ...existing,
      [entryId]: !existing[entryId],
    }));
  }, []);
  const onExpandTimelineImage = useCallback((preview: ExpandedImagePreview) => {
    setExpandedImage(preview);
  }, []);
  const expandedImageItem = expandedImage ? expandedImage.images[expandedImage.index] : null;
  const onScrollToBottom = useCallback(() => {
    forceStickToBottom("smooth");
  }, [forceStickToBottom]);
  const onOpenTurnDiff = useCallback(
    (turnId: TurnId, filePath?: string) => {
      if (diffEnvironmentPending) {
        return;
      }
      if (onOpenTurnDiffPanel) {
        onOpenTurnDiffPanel(turnId, filePath);
        return;
      }
      void navigate({
        to: "/$threadId",
        params: { threadId },
        search: (previous) => {
          const rest = stripDiffSearchParams(previous);
          return filePath
            ? { ...rest, panel: "diff", diff: "1", diffTurnId: turnId, diffFilePath: filePath }
            : { ...rest, panel: "diff", diff: "1", diffTurnId: turnId };
        },
      });
    },
    [diffEnvironmentPending, navigate, onOpenTurnDiffPanel, threadId],
  );
  const onNavigateToThread = useCallback(
    (nextThreadId: ThreadId) => {
      void navigate({
        to: "/$threadId",
        params: { threadId: nextThreadId },
        search: (previous) => stripDiffSearchParams(previous),
      });
    },
    [navigate],
  );
  const onRevertUserMessage = useCallback(
    (messageId: MessageId) => {
      const targetTurnCount = revertTurnCountByUserMessageId.get(messageId);
      if (typeof targetTurnCount !== "number") {
        return;
      }
      void onRevertToTurnCount(targetTurnCount);
    },
    [onRevertToTurnCount, revertTurnCountByUserMessageId],
  );
  const onRunProjectScriptFromHeader = useCallback((script: ProjectScript) => {
    runProjectScriptActionRef.current(script);
  }, []);
  const dismissActiveThreadError = useCallback(() => {
    if (!activeThread) return;
    setThreadError(activeThread.id, null);
  }, [activeThread, setThreadError]);

  // Empty state: no active thread
  if (!activeThread) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background text-muted-foreground/40">
        {!isElectron && (
          <header className="border-b border-border px-3 py-2 md:hidden">
            <div className="flex items-center gap-2">
              <SidebarHeaderTrigger className="size-7 shrink-0" />
              <span className="text-sm font-medium text-foreground">Threads</span>
            </div>
          </header>
        )}
        {isElectron && (
          <div
            className={cn(
              "drag-region flex h-[52px] shrink-0 items-center border-b border-border px-5",
              settings.sidebarSide === "right" && "pl-[90px]",
            )}
          >
            <SidebarHeaderTrigger className="size-7 shrink-0" />
            <span className="text-xs text-muted-foreground/50">No active thread</span>
          </div>
        )}
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <p className="text-sm">Select a thread or create a new one to get started.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background">
      {/* Top bar */}
      <header
        className={cn(
          "border-b border-border px-3 sm:px-5",
          isElectron ? "drag-region flex h-[52px] items-center" : "py-2 sm:py-3",
          isElectron && settings.sidebarSide === "right" && "pl-[90px] sm:pl-[90px]",
        )}
      >
        <ChatHeader
          activeThreadId={activeThread.id}
          activeThreadTitle={
            activeThread.parentThreadId
              ? resolveSubagentPresentationForThread({
                  thread: activeThread,
                  threads: threadLineageThreads,
                }).fullLabel
              : activeThread.title
          }
          activeProjectName={activeProject?.name}
          activeProjectId={activeProject?.id ?? null}
          // Both badges judge the selection that would actually be dispatched,
          // not the thread's stored one: on a thread that has not started those
          // differ, and warning about a model the user is no longer about to send
          // is how "Modelo no autorizado" ended up shown next to a permitted
          // agent.
          isCurrentModelAllowed={isSelectionAllowedByPolicy({
            selection: selectedModelSelection,
            allowedModelKeys,
            agents: visibleManagedAgents,
          })}
          isCurrentAgentAllowed={
            selectedModelSelection.agentId && allowedAgentIds.size > 0
              ? allowedAgentIds.has(selectedModelSelection.agentId)
              : true
          }
          threadBreadcrumbs={threadBreadcrumbs}
          hideHandoffControls={terminalWorkspaceTerminalTabActive}
          isGitRepo={isGitRepo}
          openInCwd={threadWorkspaceCwd}
          activeProjectScripts={activeProject?.scripts}
          preferredScriptId={
            activeProject ? (lastInvokedScriptByProjectId[activeProject.id] ?? null) : null
          }
          keybindings={keybindings}
          availableEditors={availableEditors}
          terminalAvailable={activeProject !== undefined}
          terminalOpen={terminalState.terminalOpen}
          terminalToggleShortcutLabel={terminalToggleShortcutLabel}
          browserToggleShortcutLabel={browserPanelShortcutLabel}
          diffToggleShortcutLabel={diffPanelShortcutLabel}
          handoffBadgeLabel={handoffBadgeLabel}
          planeIssue={activeThreadPlaneIssue}
          environmentLabel={activeThreadEnvironmentLabel}
          environmentTrialLabel={activeThreadTrialLabel}
          handoffActionLabel={handoffActionLabel}
          handoffDisabled={handoffDisabled}
          handoffActionTargetProvider={handoffTargetProvider}
          handoffBadgeSourceProvider={handoffBadgeSourceProvider}
          handoffBadgeTargetProvider={handoffBadgeTargetProvider}
          browserOpen={resolvedBrowserOpen}
          gitCwd={threadWorkspaceCwd}
          diffOpen={resolvedDiffOpen}
          diffDisabledReason={diffDisabledReason}
          filesOpen={resolvedFilesOpen}
          filesDisabled={!threadWorkspaceCwd}
          surfaceMode={surfaceMode}
          chatLayoutAction={
            surfaceMode === "single" && onSplitSurface
              ? {
                  kind: "split",
                  label: "Split chat",
                  shortcutLabel: chatSplitShortcutLabel,
                  onClick: onSplitSurface,
                }
              : surfaceMode === "split" && isFocusedPane && onMaximizeSurface
                ? {
                    kind: "maximize",
                    label: "Expand this chat",
                    shortcutLabel: null,
                    onClick: onMaximizeSurface,
                  }
                : null
          }
          onRunProjectScript={onRunProjectScriptFromHeader}
          onAddProjectScript={saveProjectScript}
          onUpdateProjectScript={updateProjectScript}
          onDeleteProjectScript={deleteProjectScript}
          onToggleTerminal={toggleTerminalVisibility}
          onToggleDiff={onToggleDiff}
          onToggleBrowser={onToggleBrowser}
          onToggleFiles={onToggleFiles}
          onCreateHandoff={onCreateHandoffThread}
          onNavigateToThread={onNavigateToThread}
        />
      </header>

      {/* Error banner */}
      <ProviderHealthBanner
        status={activeProviderStatus}
        onReauthenticate={reauthenticateProvider}
        isReauthenticating={isReauthenticatingProvider}
      />
      <ThreadErrorBanner error={activeThread.error} onDismiss={dismissActiveThreadError} />
      <RateLimitBanner rateLimitStatus={activeRateLimitStatus} />
      {terminalWorkspaceOpen ? (
        <TerminalWorkspaceTabs
          activeTab={terminalState.workspaceActiveTab}
          isWorking={isWorking}
          terminalHasRunningActivity={terminalState.runningTerminalIds.length > 0}
          terminalCount={terminalState.terminalIds.length}
          workspaceLayout={terminalState.workspaceLayout}
          onSelectTab={setTerminalWorkspaceTab}
        />
      ) : null}
      {/* Main content area with optional plan sidebar */}
      <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
        {/* Chat column */}
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div
            aria-hidden={terminalWorkspaceTerminalTabActive}
            className={cn(
              "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden",
              terminalWorkspaceTerminalTabActive ? "pointer-events-none invisible" : "",
            )}
          >
            {transcriptSelection ? (
              <MessageSelectionToolbar
                position={transcriptSelection.position}
                onReply={onReplyToTranscriptSelection}
                {...(hasSideChatAgent
                  ? { onStartSideChat: onStartSideChatFromSelection }
                  : {
                      sideChatDisabledReason:
                        "No agent is enabled for side chats. An admin can enable one in Settings → Advanced.",
                    })}
              />
            ) : null}
            <ChatTranscriptPane
              activeThreadId={activeThread.id}
              hasMessages={timelineEntries.length > 0}
              hasMoreHistory={hasMoreHistory}
              isLoadingOlderHistory={isLoadingOlderHistory}
              onLoadOlderHistory={loadOlderHistory}
              isLoadingTranscript={isTranscriptLoading}
              isWorking={isWorking}
              activeTurnInProgress={activeTurnInProgress}
              activeTurnStartedAt={activeWorkStartedAt}
              messagesScrollElement={messagesScrollElement}
              setMessagesBottomAnchorRef={setMessagesBottomAnchorRef}
              setMessagesScrollContainerRef={setMessagesScrollContainerRef}
              timelineEntries={timelineEntries}
              latestTurn={activeLatestTurn}
              latestTurnSettled={latestTurnSettled}
              recap={receivesRecap ? (activeThread?.recap ?? null) : null}
              turnDiffSummaryByAssistantMessageId={turnDiffSummaryByAssistantMessageId}
              expandedWorkGroups={expandedWorkGroups}
              onToggleWorkGroup={onToggleWorkGroup}
              expandedWorkEntries={expandedWorkEntries}
              onToggleWorkEntry={onToggleWorkEntry}
              onOpenTurnDiff={onOpenTurnDiff}
              onOpenThread={onNavigateToThread}
              openUserInputRequestIds={openUserInputRequestIds}
              onReopenUserInput={onFocusPendingUserInput}
              revertTurnCountByUserMessageId={revertTurnCountByUserMessageId}
              onRevertUserMessage={onRevertUserMessage}
              isRevertingCheckpoint={isRevertingCheckpoint}
              onExpandTimelineImage={onExpandTimelineImage}
              onTimelineHeightChange={onTimelineHeightChange}
              markdownCwd={threadWorkspaceCwd ?? undefined}
              resolvedTheme={resolvedTheme}
              chatFontSizePx={settings.chatFontSizePx}
              timestampFormat={timestampFormat}
              workspaceRoot={activeProject?.cwd ?? undefined}
              emptyStateProjectName={activeProject?.name}
              terminalWorkspaceTerminalTabActive={terminalWorkspaceTerminalTabActive}
              onMessagesScroll={onMessagesScroll}
              onMessagesClickCapture={onMessagesClickCapture}
              onMessagesWheel={onMessagesWheel}
              onMessagesPointerDown={onMessagesPointerDown}
              onMessagesPointerUp={onMessagesPointerUp}
              onMessagesPointerCancel={onMessagesPointerCancel}
              onMessagesTouchStart={onMessagesTouchStart}
              onMessagesTouchMove={onMessagesTouchMove}
              onMessagesTouchEnd={onMessagesTouchEnd}
              scrollButtonVisible={showScrollToBottom}
              onScrollToBottom={onScrollToBottom}
            />

            {snoozeReturnAt ? (
              // Drawn like the transcript's own dividers — a hairline with the
              // note centred on it — rather than as a banner. It is the system
              // remarking on the conversation, not something demanding an
              // answer, and a coloured box reads as the second.
              <div className="flex items-center gap-3 px-3 pb-1.5 sm:px-5">
                <span className="h-px flex-1 bg-border" />
                <span className="inline-flex items-center gap-1.5 font-system-ui text-[length:var(--app-font-size-ui-sm,11px)] text-muted-foreground/80">
                  <AlarmClockIcon aria-hidden="true" className="size-3 opacity-70" />
                  Vuelve de estar pospuesto ·{" "}
                  {formatSnoozeReturn(Date.parse(snoozeReturnAt), Date.now())}
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>
            ) : null}

            {/* Input bar */}
            <div className={cn("px-3 pt-0 sm:px-5 sm:pt-0", isGitRepo ? "pb-1" : "pb-2.5 sm:pb-3")}>
              {activeSubagentTasks ||
              delegationsAwaitingParent.length > 0 ||
              (activePlan && !planSidebarOpen) ? (
                <div className="mx-auto w-full max-w-3xl">
                  <div className="mx-auto flex w-11/12 flex-col gap-2">
                    {activeSubagentTasks || delegationsAwaitingParent.length > 0 ? (
                      <ActiveSubagentsIndicator
                        activeSubagentTasks={activeSubagentTasks}
                        awaitingParentTasks={delegationsAwaitingParent}
                        onScrollToBottom={onScrollToBottom}
                        onOpenThread={onNavigateToThread}
                      />
                    ) : null}
                    {activePlan && !planSidebarOpen ? (
                      <ActivePlanCard
                        activePlan={activePlan}
                        backgroundTaskCount={activeBackgroundTasks?.activeCount ?? 0}
                        onOpenSidebar={() => setPlanSidebarOpen(true)}
                      />
                    ) : null}
                  </div>
                </div>
              ) : null}
              <form
                ref={composerFormRef}
                onSubmit={onSend}
                className="relative z-10 mx-auto w-full min-w-0 max-w-3xl"
                data-chat-composer-form="true"
                data-chat-pane-scope={paneScopeId}
              >
                <QueuedComposerTurnsControl
                  summary={queuedComposerSummary}
                  items={queuedComposerMessages}
                  pendingIds={composerQueueEntry.pendingIds}
                  error={composerQueueEntry.error}
                  hasLiveTurn={hasLiveTurn}
                  canSaveDraft={isServerThread && composerSendState.hasSendableContent}
                  saveDraftHint={
                    isServerThread
                      ? undefined
                      : "Send the first message so the thread exists, then queue follow-ups."
                  }
                  open={queuedMessagesOpen}
                  onOpenChange={setQueuedMessagesOpen}
                  onSaveDraft={() => {
                    setQueuedMessagesOpen(false);
                    onSaveComposerToQueue();
                  }}
                  onSetAutoSend={onSetQueuedMessageAutoSend}
                  onSend={onSendQueuedMessage}
                  onSteer={onSteerQueuedMessage}
                  onEdit={onEditQueuedMessage}
                  onRemove={onRemoveQueuedMessage}
                />
                <div
                  className={cn(
                    "group rounded-3xl p-px transition-colors duration-200",
                    composerProviderState.composerFrameClassName,
                  )}
                  onDragEnter={onComposerDragEnter}
                  onDragOver={onComposerDragOver}
                  onDragLeave={onComposerDragLeave}
                  onDrop={onComposerDrop}
                >
                  <div
                    className={cn(
                      "chat-composer-surface rounded-2xl border bg-card transition-colors duration-200 focus-within:border-neutral-500/15",
                      isDragOverComposer ? "border-primary/50 bg-accent/20" : "border-border/60",
                      composerProviderState.composerSurfaceClassName,
                    )}
                  >
                    {activePendingApproval ? (
                      <div className={COMPOSER_TOP_PANEL_CLASSNAME}>
                        <ComposerPendingApprovalPanel
                          approval={activePendingApproval}
                          pendingCount={pendingApprovals.length}
                        />
                      </div>
                    ) : pendingUserInputs.length > 0 ? (
                      <div className={COMPOSER_TOP_PANEL_CLASSNAME}>
                        <ComposerPendingUserInputPanel
                          pendingUserInputs={pendingUserInputs}
                          respondingRequestIds={respondingRequestIds}
                          answers={activePendingDraftAnswers}
                          questionIndex={activePendingQuestionIndex}
                          isCollapsed={activePendingIsCollapsed}
                          onToggleOption={onToggleActivePendingUserInputOption}
                          onAdvance={onAdvanceActivePendingUserInput}
                          onToggleCollapsed={onToggleActivePendingUserInputCollapsed}
                        />
                      </div>
                    ) : showPlanFollowUpPrompt && activeProposedPlan ? (
                      <div className={COMPOSER_TOP_PANEL_CLASSNAME}>
                        <ComposerPlanFollowUpBanner
                          key={activeProposedPlan.id}
                          planTitle={proposedPlanTitle(activeProposedPlan.planMarkdown) ?? null}
                        />
                      </div>
                    ) : showResumeCompactionBanner &&
                      resumeCompactionWindow &&
                      resumeCompactionKey ? (
                      <div className={COMPOSER_TOP_PANEL_CLASSNAME}>
                        <ComposerResumeCompactionBanner
                          tokenLabel={formatContextWindowTokens(resumeCompactionWindow.usedTokens)}
                          compactDisabled={compactDisabled}
                          compactDisabledReason={compactDisabledReason}
                          onCompact={() => {
                            // Retire this offer as soon as it is taken. A
                            // compaction that reports no `post_tokens` (or that
                            // fails) leaves the context window snapshot
                            // untouched, so without this the same banner comes
                            // back after the turn and every click queues
                            // another `/compact`.
                            setDismissedResumeCompactionKeys((keys) =>
                              new Set(keys).add(resumeCompactionKey),
                            );
                            void compactActiveThread();
                          }}
                          onDismiss={() =>
                            setDismissedResumeCompactionKeys((keys) =>
                              new Set(keys).add(resumeCompactionKey),
                            )
                          }
                        />
                      </div>
                    ) : null}
                    <div className={cn("relative px-3.5 pb-1 pt-3")}>
                      {composerMenuOpen && !isComposerApprovalState && (
                        <div className="absolute inset-x-0 bottom-full z-20 mb-2 px-1">
                          <ComposerCommandMenu
                            items={composerMenuItems}
                            resolvedTheme={resolvedTheme}
                            isLoading={isComposerMenuLoading}
                            triggerKind={
                              composerCommandPicker !== null
                                ? "slash-command"
                                : effectiveComposerTriggerKind
                            }
                            activeItemId={activeComposerMenuItem?.id ?? null}
                            onHighlightedItemChange={onComposerMenuItemHighlighted}
                            onSelect={onSelectComposerItem}
                          />
                        </div>
                      )}

                      {!isComposerApprovalState &&
                        pendingUserInputs.length === 0 &&
                        composerAttachments.length > 0 && (
                          <div className="mb-2.5 flex flex-wrap gap-2">
                            {composerAttachments.map((attachment) => (
                              <ComposerAttachmentChip
                                key={attachment.id}
                                attachment={attachment}
                                attachments={composerAttachments}
                                nonPersisted={nonPersistedComposerAttachmentIdSet.has(
                                  attachment.id,
                                )}
                                resolvedTheme={resolvedTheme}
                                onExpandImage={setExpandedImage}
                                onRemoveAttachment={removeComposerAttachment}
                              />
                            ))}
                          </div>
                        )}
                      <ComposerPromptEditor
                        ref={composerEditorRef}
                        value={
                          isComposerApprovalState
                            ? ""
                            : activePendingProgress
                              ? activePendingProgress.customAnswer
                              : prompt
                        }
                        cursor={composerCursor}
                        quotedContexts={
                          !isComposerApprovalState && pendingUserInputs.length === 0
                            ? composerQuotedContexts
                            : []
                        }
                        onRemoveTerminalContext={removeComposerTerminalContextFromDraft}
                        onChange={onPromptChange}
                        onCommandKeyDown={onComposerCommandKey}
                        onPaste={onComposerPaste}
                        placeholder={
                          isComposerApprovalState
                            ? (activePendingApproval?.detail ??
                              "Resolve this approval request to continue")
                            : activePendingProgress
                              ? "Type your own answer, or leave this blank to use the selected option"
                              : showPlanFollowUpPrompt && activeProposedPlan
                                ? "Add feedback to refine the plan, or leave this blank to implement it"
                                : hasLiveTurn
                                  ? "Ask for follow-up changes"
                                  : phase === "disconnected"
                                    ? "Ask for follow-up changes or attach files"
                                    : "Ask anything, @tag files/folders, or use / to show available commands"
                        }
                        disabled={isConnecting || isComposerApprovalState}
                      />
                    </div>

                    {/* Bottom toolbar */}
                    {activePendingApproval ? (
                      <div className="flex items-center justify-end gap-2 px-3 pb-2">
                        <ComposerPendingApprovalActions
                          requestId={activePendingApproval.requestId}
                          isResponding={respondingRequestIds.includes(
                            activePendingApproval.requestId,
                          )}
                          onRespondToApproval={onRespondToApproval}
                        />
                      </div>
                    ) : (
                      <div
                        data-chat-composer-footer="true"
                        className={cn(
                          "flex items-end justify-between px-2.5 pb-2",
                          isComposerFooterCompact
                            ? "gap-1.5"
                            : "flex-wrap gap-1.5 sm:flex-nowrap sm:gap-0",
                        )}
                      >
                        <div
                          className={cn(
                            "flex items-center",
                            isVoiceRecording || isVoiceTranscribing
                              ? "min-w-0 shrink-0 gap-1"
                              : isComposerFooterCompact
                                ? "min-w-0 flex-1 gap-1 overflow-hidden"
                                : "-m-1 min-w-0 flex-1 gap-1 overflow-x-auto p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:min-w-max sm:overflow-visible",
                          )}
                        >
                          <ComposerExtrasMenu
                            interactionMode={interactionMode}
                            supportsFastMode={composerTraitSelection.caps.supportsFastMode}
                            fastModeEnabled={composerTraitSelection.fastModeEnabled}
                            onAddFiles={addComposerAttachments}
                            onToggleFastMode={toggleFastMode}
                            onSetPlanMode={setPlanMode}
                          />

                          {!isVoiceRecording && !isVoiceTranscribing ? (
                            <>
                              {/* Provider/model picker. Hidden for an account
                                  pinned to one agent — the server resolves the
                                  selection for them either way. */}
                              {uiCapabilities.canSelectModel ? (
                                <ProviderModelPicker
                                  compact={isComposerFooterCompact}
                                  provider={selectedProvider}
                                  model={selectedModelForPickerWithCustomFallback}
                                  lockedProvider={lockedProvider}
                                  providers={providerStatuses}
                                  modelOptionsByProvider={modelOptionsByProvider}
                                  agents={visibleManagedAgents}
                                  selectedAgentId={selectedAgentId}
                                  open={isModelPickerOpen}
                                  onOpenChange={handleModelPickerOpenChange}
                                  shortcutLabel={modelPickerShortcutLabel}
                                  {...(composerProviderState.modelPickerIconClassName
                                    ? {
                                        activeProviderIconClassName:
                                          composerProviderState.modelPickerIconClassName,
                                      }
                                    : {})}
                                  onProviderModelChange={onProviderModelSelect}
                                  onOpenProviderSettings={openProviderSettings}
                                />
                              ) : null}

                              {providerTraitsPicker ? (
                                <>
                                  <Separator
                                    orientation="vertical"
                                    className="mx-0.5 hidden h-4 sm:block"
                                  />
                                  {providerTraitsPicker}
                                </>
                              ) : null}

                              {interactionMode === "plan" ? (
                                <>
                                  <Separator
                                    orientation="vertical"
                                    className="mx-0.5 hidden h-4 sm:block"
                                  />
                                  <Button
                                    variant="ghost"
                                    className="shrink-0 whitespace-nowrap px-2 text-[length:var(--app-font-size-ui-sm,11px)] sm:text-[length:var(--app-font-size-ui-sm,11px)] font-normal text-blue-400 hover:text-blue-300 sm:px-3"
                                    size="sm"
                                    type="button"
                                    onClick={toggleInteractionMode}
                                    title="Plan mode — click to return to normal chat mode"
                                  >
                                    <GoTasklist className="size-3.5" />
                                    <span className="sr-only sm:not-sr-only">Plan</span>
                                  </Button>
                                </>
                              ) : null}

                              {uiCapabilities.canViewPlan &&
                              (activePlan || sidebarProposedPlan || planSidebarOpen) ? (
                                <>
                                  <Separator
                                    orientation="vertical"
                                    className="mx-0.5 hidden h-4 sm:block"
                                  />
                                  <Button
                                    variant="ghost"
                                    className="shrink-0 whitespace-nowrap px-2 text-[length:var(--app-font-size-ui-sm,11px)] sm:text-[length:var(--app-font-size-ui-sm,11px)] font-normal sm:px-3"
                                    size="sm"
                                    type="button"
                                    onClick={togglePlanSidebar}
                                    title={
                                      planSidebarOpen
                                        ? `Hide ${planSidebarLabel.toLowerCase()} sidebar`
                                        : `Show ${planSidebarLabel.toLowerCase()} sidebar`
                                    }
                                  >
                                    <GoTasklist className="size-3.5" />
                                    <span className="sr-only sm:not-sr-only">
                                      {planSidebarOpen
                                        ? `Hide ${planSidebarLabel}`
                                        : planSidebarLabel}
                                    </span>
                                  </Button>
                                </>
                              ) : null}
                            </>
                          ) : null}
                        </div>

                        {/* Right side: send / stop button */}
                        <div
                          data-chat-composer-actions="right"
                          className={cn(
                            "flex gap-2",
                            isVoiceRecording || isVoiceTranscribing ? "items-center" : "items-end",
                            isVoiceRecording || isVoiceTranscribing ? "min-w-0 flex-1" : "shrink-0",
                          )}
                        >
                          {isPreparingWorktree ? (
                            <span className="text-muted-foreground/70 text-xs">
                              Preparing worktree...
                            </span>
                          ) : null}
                          {showVoiceNotesControl && (isVoiceRecording || isVoiceTranscribing) ? (
                            <ComposerVoiceRecorderBar
                              disabled={isComposerApprovalState || isConnecting || isSendBusy}
                              isRecording={isVoiceRecording}
                              isTranscribing={isVoiceTranscribing}
                              durationLabel={voiceRecordingDurationLabel}
                              waveformLevels={voiceWaveformLevels}
                              onCancel={() => {
                                if (isVoiceRecording) {
                                  void submitComposerVoiceRecording();
                                  return;
                                }
                                cancelComposerVoiceRecording();
                              }}
                              onSubmit={() => {
                                void submitComposerVoiceRecording();
                              }}
                            />
                          ) : null}
                          {activePendingProgress ? (
                            <div className="flex items-center gap-2">
                              {activePendingProgress.questionIndex > 0 ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full"
                                  onClick={onPreviousActivePendingUserInputQuestion}
                                  disabled={activePendingIsResponding}
                                >
                                  Previous
                                </Button>
                              ) : null}
                              <Button
                                type="submit"
                                size="sm"
                                className="rounded-full px-4"
                                disabled={
                                  activePendingIsResponding ||
                                  (activePendingProgress.isLastQuestion
                                    ? !activePendingResolvedAnswers
                                    : !activePendingProgress.canAdvance)
                                }
                              >
                                {activePendingIsResponding
                                  ? "Submitting..."
                                  : activePendingProgress.isLastQuestion
                                    ? "Submit answers"
                                    : "Next question"}
                              </Button>
                            </div>
                          ) : phase === "running" ? (
                            <button
                              type="button"
                              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-foreground text-background transition-all duration-150 hover:scale-105 sm:h-[26px] sm:w-[26px]"
                              onClick={() => void onInterrupt()}
                              aria-label="Stop generation"
                              title="Stop the current response. On Mac, press Ctrl+C to interrupt."
                            >
                              <span
                                aria-hidden="true"
                                className="block size-2 rounded-[2px] bg-current"
                              />
                            </button>
                          ) : pendingUserInputs.length === 0 &&
                            !isVoiceRecording &&
                            !isVoiceTranscribing ? (
                            showPlanFollowUpPrompt ? (
                              prompt.trim().length > 0 ? (
                                <Button
                                  type="submit"
                                  size="sm"
                                  className="h-9 rounded-full px-4 sm:h-8"
                                  disabled={isSendBusy || isConnecting}
                                >
                                  {isConnecting || isSendBusy ? "Sending..." : "Refine"}
                                </Button>
                              ) : (
                                <div className="flex items-center">
                                  <Button
                                    type="submit"
                                    size="sm"
                                    className="h-9 rounded-l-full rounded-r-none px-4 sm:h-8"
                                    disabled={isSendBusy || isConnecting}
                                  >
                                    {isConnecting || isSendBusy ? "Sending..." : "Implement"}
                                  </Button>
                                  <Menu>
                                    <MenuTrigger
                                      render={
                                        <Button
                                          size="sm"
                                          variant="default"
                                          className="h-9 rounded-l-none rounded-r-full border-l-white/12 px-2 sm:h-8"
                                          aria-label="Implementation actions"
                                          disabled={isSendBusy || isConnecting}
                                        />
                                      }
                                    >
                                      <ChevronDownIcon className="size-3.5" />
                                    </MenuTrigger>
                                    <MenuPopup align="end" side="top">
                                      <MenuItem
                                        disabled={isSendBusy || isConnecting}
                                        onClick={() => void onImplementPlanInNewThread()}
                                      >
                                        Implement in a new thread
                                      </MenuItem>
                                    </MenuPopup>
                                  </Menu>
                                </div>
                              )
                            ) : (
                              <>
                                {showVoiceNotesControl ? (
                                  <ComposerVoiceButton
                                    disabled={isComposerApprovalState || isConnecting || isSendBusy}
                                    isRecording={isVoiceRecording}
                                    isTranscribing={isVoiceTranscribing}
                                    durationLabel={voiceRecordingDurationLabel}
                                    onClick={toggleComposerVoiceRecording}
                                  />
                                ) : null}
                                <button
                                  type="submit"
                                  className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground text-background transition-all duration-150 hover:scale-105 disabled:opacity-20 disabled:hover:scale-100 sm:h-8 sm:w-8"
                                  disabled={
                                    isSendBusy ||
                                    isConnecting ||
                                    isVoiceTranscribing ||
                                    !composerSendState.hasSendableContent
                                  }
                                  aria-label={
                                    isConnecting
                                      ? "Connecting"
                                      : isVoiceTranscribing
                                        ? "Transcribing voice note"
                                        : isPreparingWorktree
                                          ? "Preparing worktree"
                                          : isSendBusy
                                            ? "Sending"
                                            : "Send message"
                                  }
                                >
                                  {isConnecting || isSendBusy ? (
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 14 14"
                                      fill="none"
                                      className="animate-spin"
                                      aria-hidden="true"
                                    >
                                      <circle
                                        cx="7"
                                        cy="7"
                                        r="5.5"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeDasharray="20 12"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 14 14"
                                      fill="none"
                                      aria-hidden="true"
                                    >
                                      <path
                                        d="M7 11.5V2.5M7 2.5L3 6.5M7 2.5L11 6.5"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  )}
                                </button>
                              </>
                            )
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* The row carries two independent grants: the branch/workspace
                controls (`p.branch.view`) and the preview (`p.runtime.expose`).
                Either one is reason enough to render it — the toolbar itself
                decides which halves appear. */}
            {isGitRepo && resolveBranchToolbarVisibility(uiCapabilities).showToolbar && (
              <BranchToolbar
                threadId={activeThread.id}
                onEnvModeChange={onEnvModeChange}
                envLocked={envLocked}
                runtimeMode={runtimeMode}
                onRuntimeModeChange={handleRuntimeModeChange}
                onHandoffToWorktree={onHandoffToWorktree}
                onHandoffToLocal={onHandoffToLocal}
                handoffBusy={handoffBusy}
                onComposerFocusRequest={scheduleComposerFocus}
                contextWindow={activeContextWindow}
                cumulativeCostUsd={activeCumulativeCostUsd}
                {...(canCheckoutPullRequestIntoThread
                  ? { onCheckoutPullRequestRequest: openPullRequestDialog }
                  : {})}
              />
            )}
          </div>

          {terminalWorkspaceOpen && agentTerminalReady ? (
            <div
              aria-hidden={!terminalWorkspaceTerminalTabActive}
              className={cn(
                "absolute inset-0 min-h-0 min-w-0 transition-all duration-200 ease-out",
                terminalWorkspaceTerminalTabActive
                  ? "translate-y-0 opacity-100"
                  : "pointer-events-none translate-y-1 opacity-0",
              )}
            >
              <ThreadTerminalDrawerLazy
                key={`${activeThread.id}-workspace`}
                {...terminalDrawerProps}
                presentationMode="workspace"
                isVisible={terminalWorkspaceTerminalTabActive}
                onTogglePresentationMode={
                  terminalState.workspaceLayout === "both" ? collapseTerminalWorkspace : undefined
                }
              />
            </div>
          ) : terminalWorkspaceOpen && !agentTerminalReady ? (
            <div className="absolute inset-0 flex min-h-0 min-w-0 items-center justify-center bg-background/80 text-muted-foreground">
              <div className="flex items-center gap-2 text-sm">
                <Loader2Icon className="size-4 animate-spin" />
                <span>Reanudando agente…</span>
              </div>
            </div>
          ) : null}
        </div>
        {/* end chat column */}

        {/* Plan sidebar */}
        {planSidebarOpen && uiCapabilities.canViewPlan ? (
          <PlanSidebar
            activePlan={activePlan}
            activeProposedPlan={sidebarProposedPlan}
            markdownCwd={threadWorkspaceCwd ?? undefined}
            workspaceRoot={activeProject?.cwd ?? undefined}
            timestampFormat={timestampFormat}
            onClose={() => {
              setPlanSidebarOpen(false);
              // Track that the user explicitly dismissed for this turn so auto-open won't fight them.
              const turnKey = activePlan?.turnId ?? sidebarProposedPlan?.turnId ?? null;
              if (turnKey) {
                planSidebarDismissedForTurnRef.current = turnKey;
              }
            }}
          />
        ) : null}
      </div>
      {/* end horizontal flex container */}

      {(() => {
        if (!terminalState.terminalOpen || !activeProject || terminalWorkspaceOpen) {
          return null;
        }
        if (!agentTerminalReady) {
          return (
            <div
              className="border-t border-border/70 bg-background/80 text-muted-foreground"
              style={{ height: terminalState.terminalHeight }}
            >
              <div className="flex h-full items-center justify-center gap-2 text-sm">
                <Loader2Icon className="size-4 animate-spin" />
                <span>Reanudando agente…</span>
              </div>
            </div>
          );
        }
        return (
          <ThreadTerminalDrawerLazy
            key={activeThread.id}
            {...terminalDrawerProps}
            presentationMode="drawer"
            onTogglePresentationMode={expandTerminalWorkspace}
          />
        );
      })()}

      <ComposerSlashStatusDialog
        open={isSlashStatusDialogOpen}
        onOpenChange={setIsSlashStatusDialogOpen}
        selectedModel={selectedModel}
        fastModeEnabled={fastModeEnabled}
        selectedPromptEffort={selectedPromptEffort}
        interactionMode={interactionMode}
        envMode={envMode}
        envState={envState}
        branch={activeThread?.branch ?? activeRootBranch}
        contextWindow={activeContextWindow}
        cumulativeCostUsd={activeCumulativeCostUsd}
        rateLimitStatus={activeRateLimitStatus}
      />
      <ThreadWorktreeHandoffDialog
        open={worktreeHandoffDialogOpen}
        worktreeName={worktreeHandoffName}
        busy={handoffBusy}
        onWorktreeNameChange={setWorktreeHandoffName}
        onOpenChange={setWorktreeHandoffDialogOpen}
        onConfirm={confirmWorktreeHandoff}
      />

      {expandedImage && expandedImageItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4 py-6 [-webkit-app-region:no-drag]"
          role="dialog"
          aria-modal="true"
          aria-label="Expanded image preview"
        >
          <button
            type="button"
            className="absolute inset-0 z-0 cursor-zoom-out"
            aria-label="Close image preview"
            onClick={closeExpandedImage}
          />
          {expandedImage.images.length > 1 && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="absolute left-2 top-1/2 z-20 -translate-y-1/2 text-white/90 hover:bg-white/10 hover:text-white sm:left-6"
              aria-label="Previous image"
              onClick={() => {
                navigateExpandedImage(-1);
              }}
            >
              <ChevronLeftIcon className="size-5" />
            </Button>
          )}
          <div className="relative isolate z-10 max-h-[92vh] max-w-[92vw]">
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              className="absolute right-2 top-2"
              onClick={closeExpandedImage}
              aria-label="Close image preview"
            >
              <XIcon />
            </Button>
            <img
              src={expandedImageItem.src}
              alt={expandedImageItem.name}
              className="max-h-[86vh] max-w-[92vw] select-none rounded-lg border border-border/70 bg-background object-contain shadow-2xl"
              draggable={false}
            />
            <p className="mt-2 max-w-[92vw] truncate text-center text-xs text-muted-foreground/80">
              {expandedImageItem.name}
              {expandedImage.images.length > 1
                ? ` (${expandedImage.index + 1}/${expandedImage.images.length})`
                : ""}
            </p>
          </div>
          {expandedImage.images.length > 1 && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="absolute right-2 top-1/2 z-20 -translate-y-1/2 text-white/90 hover:bg-white/10 hover:text-white sm:right-6"
              aria-label="Next image"
              onClick={() => {
                navigateExpandedImage(1);
              }}
            >
              <ChevronRightIcon className="size-5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
